// File: frontend/src/components/admin/adminManualContent.ts
//
// Content source for the admin "Kullanım Kılavuzu" (usage manual) tab.
//
// Design notes:
//  - All copy lives here as plain data (TR + EN) instead of i18n keys. The manual
//    is a single long-form document; putting several hundred prose keys into
//    common.json would make TR/EN key parity impossible to audit by hand.
//    Only the tab label itself goes through i18next (with an inline fallback).
//  - Numbers that the admin can change are NEVER hardcoded in the prose.
//    Write {{setting:entry.daily_limit}} instead; AdminManual.tsx replaces the
//    token with the live value read from GET /api/admin/settings.
//    A token whose key is missing renders as "—".
//  - The "settings" block type renders a live table of the given setting keys,
//    including the current value, description and who last changed it.

/** Supported manual languages. Mirrors i18n supportedLngs. */
export type ManualLang = "tr" | "en";

/** Icon identifiers resolved to react-icons components inside AdminManual.tsx. */
export type ManualIconKey =
    | "book"
    | "sitemap"
    | "hashtag"
    | "fileAlt"
    | "userShield"
    | "userClock"
    | "userSlash"
    | "graduation"
    | "thumbsUp"
    | "bell"
    | "idCard"
    | "sliders"
    | "comments"
    | "circle"
    | "flag"
    | "ban"
    | "cogs"
    | "futbol"
    | "database"
    | "random"
    | "thumbtack"
    | "lock"
    | "trash"
    | "search"
    | "image"
    | "history"
    | "tools"
    | "gavel";

/** A single renderable block inside a manual section. */
export type ManualBlock =
    | { type: "p"; text: string }
    | { type: "ul"; items: string[] }
    | { type: "ol"; items: string[] }
    | { type: "note"; tone: "info" | "warn" | "danger" | "ok"; text: string }
    | { type: "table"; headers: string[]; rows: string[][] }
    | { type: "sub"; text: string }
    // Renders a vertical time axis: what happens to an account, and when.
    // "at" is the moment (relative or absolute), "text" is what changes at that moment.
    | {
        type: "timeline";
        caption?: string;
        steps: {
            at: string;
            label: string;
            text: string;
            tone?: "info" | "warn" | "danger" | "ok";
        }[];
    }
    // Renders a live table of the given system-setting keys (value + description).
    | { type: "settings"; keys: string[] };

/** One collapsible section of the manual. */
export interface ManualSection {
    id: string;
    icon: ManualIconKey;
    title: Record<ManualLang, string>;
    /** One-line summary shown under the title and used by the search filter. */
    summary: Record<ManualLang, string>;
    blocks: Record<ManualLang, ManualBlock[]>;
}

/** Top-level grouping shown in the left navigation rail. */
export interface ManualGroup {
    id: string;
    title: Record<ManualLang, string>;
    sections: ManualSection[];
}

// ─────────────────────────────────────────────────────────────────────────────
// GROUP 1 — PLATFORM BASICS
// ─────────────────────────────────────────────────────────────────────────────

const SECTION_OVERVIEW: ManualSection = {
    id: "overview",
    icon: "book",
    title: { tr: "Platforma Genel Bakış", en: "Platform Overview" },
    summary: {
        tr: "Sözlük mantığı, başlık/tanım ilişkisi ve içeriğin nasıl saklandığı.",
        en: "Sözlük model, topic/entry relationship and how content is stored.",
    },
    blocks: {
        tr: [
            { type: "p", text: "Platform sözlük mantığıyla çalışır: <b>başlık</b> (topic) altına <b>tanım</b> (entry) girilir. Başlıkları kural olarak admin açar; kullanıcılar başlık açamaz, sadece Gündem tarafında <b>başlık önerebilir</b>. Tanım girmek ise tüm kullanıcıların işidir." },
            { type: "p", text: "Bir tanım <b>tam olarak bir</b> başlığa bağlıdır — aynı anda hem bir maça hem bir kişiye ait olamaz, başlıksız da olamaz." },
            { type: "sub", text: "İçerik normalizasyonu" },
            {
                type: "ul", items: [
                    "Tüm tanım içerikleri <b>küçük harfe çevrilerek</b> saklanır ve öyle gösterilir (sözlük geleneği).",
                    "Küçültme Türkçeye göre yapılır: <b>İ</b> harfi <b>i</b>, <b>I</b> harfi <b>ı</b> olur — İngilizce kurala göre bozulmaz.",
                    "URL'ler ve referans (bkz) token'larının slug/id kısımları küçültmeden korunur (placeholder tekniği).",
                    "İçine HTML kodu veya zararlı script gömülmüş tanımlar kabul edilmez.",
                    "Aynı maç altında birebir aynı içerik ikinci kez girilemez (duplicate koruması).",
                ]
            },
            { type: "sub", text: "Referans (bkz) sistemi" },
            {
                type: "ul", items: [
                    "<b>Satır içi mod:</b> <code>mac / kisi / kulup / takim / baslik</code> anahtar kelimeleri — etiketin kendisi link olur.",
                    "<b>bkz modu:</b> anahtar kelimenin başına <code>bkz-</code> eklenir (ör. <code>(bkz-mac: etiket||id)</code>) — parantezli <i>(bkz: link)</i> olarak render edilir.",
                    "Her iki modda da <code>||</code> sonrasına hedefin kimliği yazılır ve bu kısım küçültmeden korunur.",
                    "Editör araç çubuğunda ayrıca <b>alıntı</b> (blockquote) ve <b>spoiler</b> butonları vardır; ikisi de iç içe referans girilebilen ortak bir modal kullanır.",
                ]
            },
            { type: "note", tone: "info", text: "Sol panel domain sırası her yerde sabittir: <b>Gündem → Maç → Kulüp → Kişi → Takım</b>." },
        ],
        en: [
            { type: "p", text: "The platform follows the sözlük model: users write <b>entries</b> under <b>topics</b>. As a rule admins create topics; users cannot create them and may only <b>suggest</b> topics on the Gündem side. Writing entries is open to all users." },
            { type: "p", text: "An entry belongs to <b>exactly one</b> topic — it can never belong to a match and a person at once, and never to no topic at all." },
            { type: "sub", text: "Content normalization" },
            {
                type: "ul", items: [
                    "All entry content is stored and rendered <b>lowercase</b> (sözlük convention).",
                    "Lowercasing follows Turkish rules: <b>İ</b> becomes <b>i</b> and <b>I</b> becomes <b>ı</b>, so nothing is mangled by the English rule.",
                    "URLs and the slug/id part of reference tokens are protected from lowercasing.",
                    "Entries with HTML tags or injected scripts are rejected.",
                    "The exact same content cannot be posted twice under the same match (duplicate guard).",
                ]
            },
            { type: "sub", text: "Reference (bkz) system" },
            {
                type: "ul", items: [
                    "<b>Inline mode:</b> <code>mac / kisi / kulup / takim / baslik</code> keywords — the label itself becomes the link.",
                    "<b>bkz mode:</b> prefix the keyword with <code>bkz-</code> (e.g. <code>(bkz-mac: label||id)</code>) — rendered as a literal <i>(bkz: link)</i>.",
                    "Both modes put the target id after <code>||</code>, and that part is protected from lowercasing.",
                    "The editor toolbar also offers <b>alıntı</b> (blockquote) and <b>spoiler</b> buttons, both using a shared modal that supports nested references.",
                ]
            },
            { type: "note", tone: "info", text: "The left-pane domain order is fixed everywhere: <b>Gündem → Match → Club → Person → Team</b>." },
        ],
    },
};

const SECTION_DOMAINS: ManualSection = {
    id: "domains",
    icon: "sitemap",
    title: { tr: "5 Başlık Tipi (Domain)", en: "The 5 Topic Types (Domains)" },
    summary: {
        tr: "Gündem, Maç, Kulüp, Kişi, Takım — kim açar, ne farkı var.",
        en: "Gündem, Match, Club, Person, Team — who creates them and how they differ.",
    },
    blocks: {
        tr: [
            {
                type: "table",
                headers: ["Tip", "Kim açar?", "Kullanıcı önerebilir mi?", "Tanım zorunlu mu?", "URL"],
                rows: [
                    ["Gündem (Özel Başlık)", "Admin / Moderatör", "Evet — “başlık öner”", "<b>Evet</b> — başlık açılışında tanım girilmeli", "/ozel/{slug}"],
                    ["Maç", "Admin / Moderatör", "Hayır", "Hayır", "/mac/{slug}"],
                    ["Kulüp", "Admin / Moderatör", "Hayır", "Hayır", "/kulup/{slug}"],
                    ["Kişi", "Admin / Moderatör", "Hayır", "Hayır", "/kisi/{slug}"],
                    ["Takım", "Admin / Moderatör", "Hayır", "Hayır", "/takim/{kulüpSlug}/{sporSlug}"],
                ],
            },
            { type: "note", tone: "warn", text: "<b>Gündem başlıklarında tanım zorunludur.</b> Diğer dört domainde başlık, içeriği olmadan da var olabilir (maç sayfası açılır, henüz tanım yoktur). Gündem başlığı ise tanımsız bir kabuk olmamalıdır — öneri onaylanırken veya admin başlık açarken ilk tanımın girilmesi beklenir." },
            { type: "sub", text: "Ortak davranışlar" },
            {
                type: "ul", items: [
                    "Beş domainin tamamı <b>kilitlenebilir</b> (yeni tanım girişi kapanır).",
                    "Beş domainin tamamı <b>sabitlenebilir</b> (pin) ve <b>taşıma/birleştirme</b> işlemlerine konu olabilir.",
                    "Beş domainin tamamında “eski tanımları gizle / bugünküleri göster” daraltma davranışı aynı hook ile çalışır; “bugün” sınırını sunucu belirler.",
                    "Slug <b>bir kez</b> üretilir ve güncellemede asla yeniden üretilmez — eski linkler kırılmasın diye.",
                ]
            },
        ],
        en: [
            {
                type: "table",
                headers: ["Type", "Created by", "User-suggestable?", "Entry required?", "URL"],
                rows: [
                    ["Gündem (custom topic)", "Admin / Moderator", "Yes — “suggest a topic”", "<b>Yes</b> — an entry is expected at creation", "/ozel/{slug}"],
                    ["Match", "Admin / Moderator", "No", "No", "/mac/{slug}"],
                    ["Club", "Admin / Moderator", "No", "No", "/kulup/{slug}"],
                    ["Person", "Admin / Moderator", "No", "No", "/kisi/{slug}"],
                    ["Team", "Admin / Moderator", "No", "No", "/takim/{clubSlug}/{sportSlug}"],
                ],
            },
            { type: "note", tone: "warn", text: "<b>Gündem topics require an entry.</b> The other four domains can exist without content (a match page opens with no entries yet). A Gündem topic should never be an empty shell — the first entry is expected when approving a suggestion or creating the topic." },
            { type: "sub", text: "Shared behaviour" },
            {
                type: "ul", items: [
                    "All five domains can be <b>locked</b> (new entries blocked).",
                    "All five can be <b>pinned</b> and can take part in <b>move / merge</b> operations.",
                    "All five share the same collapse behaviour for older entries; the “today” boundary is decided server-side.",
                    "The slug is generated <b>once</b> at creation and never regenerated on update, so old links keep working.",
                ]
            },
        ],
    },
};

const SECTION_GUNDEM: ManualSection = {
    id: "gundem",
    icon: "hashtag",
    title: { tr: "Gündem, Kanallar ve Başlık Önerme", en: "Gündem, Channels and Topic Suggestions" },
    summary: {
        tr: "Öneri akışı, kanal (hashtag) yapısı, nickaltı başlıkları.",
        en: "Suggestion flow, channel (hashtag) structure, nickaltı topics.",
    },
    blocks: {
        tr: [
            { type: "sub", text: "Başlık öneri akışı" },
            {
                type: "ol", items: [
                    "Giriş yapmış kullanıcı “başlık öner” modalını açar, başlığı yazar ve isterse kanal seçer / yeni kanal adı önerir.",
                    "Typeahead aynı başlığın mevcut olup olmadığını canlı kontrol eder; birebir eşleşmede gönderim engellenir.",
                    "Öneri <b>PENDING</b> olarak kaydedilir ve admin panelindeki <i>Gündem</i> sekmesinde listelenir.",
                    "Admin/moderatör onaylar (<b>APPROVED</b>) → gerçek başlık oluşur ve kullanıcıya bildirim gider.",
                    "Reddedilirse (<b>REJECTED</b>) kullanıcıya bildirim gider; admin notu <b>sadece reddedilen</b> önerilerde kullanıcıya gösterilir.",
                ]
            },
            {
                type: "ul", items: [
                    "Aynı başlık için zaten bekleyen bir öneri varsa, ikinci bir öneri kabul edilmez.",
                    "Bekleyen öneri varsa admin ve moderatörlerin üst menüsünde, admin paneli butonunun solunda sayı rozetli bir <b>#</b> butonu çıkar; tıklayınca her zaman <i>Gündem</i> sekmesinin <i>Öneriler</i> listesi “Bekliyor” filtresiyle ve yenilenmiş olarak açılır. Sayaç anlık değildir: sayfa değiştirildiğinde, tarayıcı sekmesine geri dönüldüğünde ve bir öneri onaylanıp reddedildiğinde güncellenir. Bekleyen öneri kalmayınca buton kaybolur.",
                    "Kullanıcı kendi önerilerini profilinde görür: onaylananlar herkese açık sekmede, reddedilenler sadece kendisine görünen sekmede.",
                    "Öneri limitleri ADMIN ve MODERATÖR için uygulanmaz.",
                    "<b>ÇAYLAK</b> rolündeki kullanıcılar başlık öneremez: “başlık öner” butonları onlara hiç gösterilmez, istek yine de gelirse sunucu reddeder.",
                    "Arama sonuç sayfası: aranan kelimeyle <b>birebir aynı</b> Gündem başlığı (veya bekleyen öneri) yoksa sonuçların üstünde “… başlığını öner” kısayolu çıkar; modal kelimeyle dolu açılır. Sadece Gündem başlıkları dikkate alınır, kişi/kulüp/maç eşleşmeleri kısayolu gizlemez.",
                ]
            },
            { type: "settings", keys: ["topic.title.max_length", "topic.suggestion.cooldown_seconds", "topic.suggestion.daily_limit"] },
            { type: "sub", text: "Kanallar (kanal / hashtag)" },
            {
                type: "ul", items: [
                    "Bir gündem başlığı sıralı olarak <b>en fazla 10 kanala</b> bağlanabilir.",
                    "Listede görünen ikonu <b>ilk kanal</b> (index 0) belirler — kanal sırası bu yüzden önemlidir.",
                    "Kanallar master data tarafında <i>Başlık Kategorileri</i> olarak yönetilir; silinmez, pasife alınır.",
                    "Kanal aramada Türkçe karakterler fark etmez: “cografya”, “dunya” ya da “is” yazmak “coğrafya”, “dünya”, “iş dünyası” kanallarını da bulur (ç ğ ı İ ö ş ü â î û). Başlık önerme modalında ve admin Gündem modallarında aynı şekilde çalışır.",
                    "Arama ve # (referans) penceresinde büyük <b>I</b> önce “i” olarak aranır, hiçbir şey bulunamazsa “ı” olarak tekrar denenir: “Irak” yazmak “ırak” başlığını da bulur. Büyük <b>İ</b> her zaman “i”dir (“İngiltere” → “ingiltere”).",
                ]
            },
            { type: "sub", text: "Nickaltı başlıkları" },
            {
                type: "ul", items: [
                    "Her kullanıcıya kayıt anında otomatik olarak bir <b>nickaltı</b> gündem başlığı açılır (normal kayıt, Google girişi ve admin panelinden kullanıcı oluşturma dahil).",
                    "Başlığın adı kullanıcı adıyla aynıdır. Kullanıcı adını değiştirdiğinde başlığın adı da değişir ama <b>adresi (linki) değişmez</b> — eski linkler çalışmaya devam eder.",
                    "Sabit “nickaltı” kanalına bağlıdır.",
                    "Admin başlık adını/slug'ını düzenleyemez, başlık <b>silinemez</b> ve <b>birleştirmeye konu olamaz</b> (iki taraf da olamaz).",
                    "Diğer her şeyde normal gündem başlığı gibi davranır: kilitlenebilir, aktif/pasif yapılabilir, tanım alır.",
                ]
            },
            { type: "note", tone: "danger", text: "Kullanıcı <b>fiziksel olarak (hard delete)</b> silindiğinde nickaltı başlığı pasife alınmaz, <b>silinir</b> — böylece slug serbest kalır." },
            { type: "sub", text: "Başlığı kalıcı silme (hard delete)" },
            {
                type: "ul", items: [
                    "<b>Gündem → Başlıklar</b> sekmesinde satırdaki <b>çöp kutusu</b> butonu başlığı veritabanından fiziksel olarak siler. Sadece <b>ADMIN</b> görür; moderatörde bu buton yoktur.",
                    "Silmeden önce mutlaka bir uyarı penceresi açılır: başlık adı, slug ve <b>silinecek tanım sayısı</b> gösterilir.",
                    "Başlıkla birlikte gidenler: altındaki <b>tüm tanımlar</b> (silinmiş olanlar dahil) ve onların oyları/şikâyetleri, taslaklar, sabitlemeler, kanal bağlantıları, birleştirme geçmişi kayıtları ve “önerin yayınlandı” bildirimleri.",
                    "Başlığı doğuran <b>öneri kaydı silinmez</b>; sadece başlığa giden bağlantısı kopar, böylece öneri geçmişi bozulmaz.",
                    "Silinen tanımlar yazarların <b>tanım sayısı ve karmasından</b> düşülür.",
                    "Nickaltı başlıklarında buton hiç görünmez — onlar hesaba bağlıdır ve bu yoldan silinemez.",
                    "İşlem denetim kaydına <b>HARD_DELETE_TOPIC</b> olarak yazılır (detayda slug ve silinen tanım sayısı durur).",
                ]
            },
            { type: "note", tone: "danger", text: "<b>Kalıcı silme geri alınamaz ve çöp kutusu yoktur.</b> Amacınız başlığı sadece yayından kaldırmaksa <b>pasife alın</b>; mükerrer bir başlıksa <b>Taşı / Birleştir</b> kullanın — her ikisi de tanımları korur. Kalıcı silmeyi yalnızca spam/hatalı açılmış, gerçekten yok olması gereken başlıklar için kullanın." },
        ],
        en: [
            { type: "sub", text: "Suggestion flow" },
            {
                type: "ol", items: [
                    "An authenticated user opens the “suggest a topic” modal, types the title and optionally picks channels or proposes new channel names.",
                    "Typeahead checks live whether the title already exists; an exact match blocks submission.",
                    "The suggestion is stored as <b>PENDING</b> and appears under the admin panel’s <i>Gündem</i> tab.",
                    "An admin/moderator approves it (<b>APPROVED</b>) → the real topic is created and the user is notified.",
                    "On rejection (<b>REJECTED</b>) the user is notified; the admin note is shown to the user <b>only</b> for rejected suggestions.",
                ]
            },
            {
                type: "ul", items: [
                    "If a pending suggestion with the same title already exists, a second one is not accepted.",
                    "While suggestions are pending, admins and moderators see a <b>#</b> button with a count badge in the header, left of the admin panel button; clicking it always opens the <i>Gündem</i> tab’s <i>Suggestions</i> list with the “Pending” filter, freshly loaded. The counter is not real-time: it refreshes on page changes, when returning to the browser tab and after a suggestion is approved or rejected. The button disappears once no suggestion is pending.",
                    "Users see their own suggestions on their profile: approved ones on a public tab, rejected ones on an owner-only tab.",
                    "Suggestion limits are not enforced for ADMIN and MODERATOR.",
                    "<b>ROOKIE</b> users cannot suggest topics: the “suggest a topic” buttons are never shown to them, and the server rejects the request if it arrives anyway.",
                    "Search results page: when no Gündem topic (or pending suggestion) has <b>exactly</b> the searched keyword as its title, a “suggest … as a topic” shortcut appears above the results; the modal opens pre-filled with the keyword. Only Gündem titles count — person/club/match matches never hide the shortcut.",
                ]
            },
            { type: "settings", keys: ["topic.title.max_length", "topic.suggestion.cooldown_seconds", "topic.suggestion.daily_limit"] },
            { type: "sub", text: "Channels (kanal / hashtag)" },
            {
                type: "ul", items: [
                    "A Gündem topic can carry <b>up to 10 ordered channels</b>.",
                    "The icon shown in the list is decided by the <b>first channel</b> (index 0) — channel order matters.",
                    "Channels are managed under master data as <i>Topic Categories</i>; they are deactivated, never deleted.",
                    "Channel search ignores Turkish characters: typing “cografya”, “dunya” or “is” also finds “coğrafya”, “dünya”, “iş dünyası” (ç ğ ı İ ö ş ü â î û). Works the same in the topic suggestion modal and in the admin Gündem modals.",
                    "In search and in the # (reference) popup a capital <b>I</b> is first searched as “i”; if that finds nothing it is retried as “ı”, so typing “Irak” also finds the topic “ırak”. A capital <b>İ</b> is always “i” (“İngiltere” → “ingiltere”).",
                ]
            },
            { type: "sub", text: "Nickaltı topics" },
            {
                type: "ul", items: [
                    "Every user automatically gets one <b>nickaltı</b> Gündem topic at registration (normal sign-up, Google sign-in and admin-panel user creation alike).",
                    "The topic title matches the username. Renaming the user renames the topic, but its <b>URL never changes</b>, so old links keep working.",
                    "It is bound to the fixed “nickaltı” channel.",
                    "Admins cannot edit its title/slug, the topic <b>cannot be deleted</b> and <b>cannot take part in a merge</b> (on either side).",
                    "Otherwise it behaves like any Gündem topic: lockable, activatable, accepts entries.",
                ]
            },
            { type: "note", tone: "danger", text: "When a user is <b>hard-deleted</b>, the nickaltı topic is <b>deleted</b> rather than deactivated, so the slug is freed." },
            { type: "sub", text: "Hard-deleting a topic" },
            {
                type: "ul", items: [
                    "The <b>trash</b> button on a row under <b>Gündem → Topics</b> physically removes the topic from the database. <b>ADMIN only</b> — moderators do not see the button.",
                    "A warning dialog always comes first, showing the title, the slug and the <b>number of entries that will be deleted</b>.",
                    "Removed with the topic: <b>all of its entries</b> (soft-deleted ones included) together with their votes and reports, drafts, pins, channel links, merge history rows and the “your suggestion was published” notifications.",
                    "The originating <b>suggestion row is kept</b> — only its link to the topic is cleared, so the review history stays intact.",
                    "The deleted entries are subtracted from their authors’ <b>entry count and karma</b>.",
                    "The button never appears on nickaltı topics — those belong to a user account and cannot be removed this way.",
                    "The operation is audited as <b>HARD_DELETE_TOPIC</b> (the detail carries the slug and the number of deleted entries).",
                ]
            },
            { type: "note", tone: "danger", text: "<b>A hard delete is irreversible and there is no trash bin.</b> If you only want the topic off the site, <b>deactivate</b> it; if it is a duplicate, use <b>Move / Merge</b> — both keep the entries. Reserve hard deletion for spam or mistakenly created topics that really must disappear." },
        ],
    },
};

const SECTION_ENTRY_RULES: ManualSection = {
    id: "entry-rules",
    icon: "fileAlt",
    title: { tr: "Tanım Kuralları ve Limitler", en: "Entry Rules and Limits" },
    summary: {
        tr: "Uzunluk, günlük kota, spam bekleme süresi, düzenleme penceresi, taslak, gündemde art arda tanım yasağı.",
        en: "Length, daily quota, spam cooldown, edit window, drafts, no consecutive entries in Gündem.",
    },
    blocks: {
        tr: [
            { type: "p", text: "Aşağıdaki değerler <i>Ayarlar</i> sekmesinden değiştirilir ve <b>yeniden başlatmaya gerek olmadan</b> anında geçerli olur. Bu tablo canlı değerleri gösterir:" },
            {
                type: "settings",
                keys: [
                    "entry.min_length",
                    "entry.max_length",
                    "entry.daily_limit",
                    "entry.edit_window_minutes",
                    "entry.draft_limit",
                    "entry.preview_line_limit",
                    "moderation.spam_cooldown_seconds",
                ],
            },
            { type: "sub", text: "Kurallar nasıl işler" },
            {
                type: "ul", items: [
                    "<b>Günlük kota:</b> gece yarısında sıfırlanır. Kullanıcı bugün {{setting:entry.daily_limit}} tanıma ulaştıysa yeni tanım giremez ve “günlük limite ulaştınız” uyarısı alır. Değer <b>0</b> ise limit yoktur.",
                    "<b>Spam bekleme:</b> iki tanım arasında en az {{setting:moderation.spam_cooldown_seconds}} saniye olmalıdır. Limit aşılırsa aynı süre kadar cooldown uygulanır. Değer <b>0</b> ise spam koruması kapalıdır.",
                    "<b>Uzunluk:</b> trim edilmiş içerik {{setting:entry.min_length}}–{{setting:entry.max_length}} karakter aralığında olmalıdır. Aynı kontrol düzenlemede de çalışır.",
                    "<b>Düzenleme penceresi:</b> yazar tanımını yayınladıktan sonra {{setting:entry.edit_window_minutes}} dakika içinde düzenleyebilir. Değer <b>0</b> ise süresiz düzenlenebilir.",
                    "<b>Önizleme satırı:</b> tanım {{setting:entry.preview_line_limit}} satırdan uzunsa “devamını göster” linki çıkar. Değer <b>0</b> ise kısaltma yapılmaz.",
                    "<b>Taslak:</b> kullanıcı en fazla {{setting:entry.draft_limit}} taslak tutabilir. Taslaklar özeldir, moderasyona konu değildir, oy almaz ve <b>her zaman kalıcı silinir</b> (çöp kutusuna düşmez).",
                ]
            },
            { type: "note", tone: "info", text: "<b>ADMIN ve MODERATÖR</b> için günlük kota, spam bekleme süresi ve düzenleme penceresi <b>uygulanmaz</b>. Kilitli başlıklara da tanım girebilirler." },
            { type: "sub", text: "Gündem: art arda tanım yasağı" },
            {
                type: "ul", items: [
                    "<b>Yalnızca gündem başlıklarında</b> bir yazar art arda iki tanım giremez: başlıktaki son (silinmemiş) tanım kendisine aitse yeni tanımı reddedilir ve formda “Gündem başlıklarına art arda iki tanım girilemez…” uyarısını görür.",
                    "Araya başka bir yazarın tanımı girdiğinde tekrar yazabilir. Kendi son tanımını silerse de yeniden yazabilir.",
                    "Taslaktan yayınlama da aynı kurala tabidir. Maç, kişi, kulüp ve takım başlıkları <b>etkilenmez</b>.",
                    "Gündem sayfasında tanım formunun altında kullanıcının bu başlığa daha önce girdiği tanımlar <b>#id (tarih)</b> linkleri olarak listelenir; en yeni {{const:own_topic_entries_max}} tanım gösterilir. Daha fazlası varsa listenin sonunda “ve N tanım daha” satırı çıkar; eski tanımlar silinmez, başlıkta ve profilde yerinde durur.",
                ]
            },
            { type: "note", tone: "warn", text: "Bu kural bir kota değil, içerik kuralıdır: <b>ADMIN ve MODERATÖR için de geçerlidir</b>." },
            { type: "sub", text: "Silme ve geri alma" },
            {
                type: "ul", items: [
                    "Kullanıcı kendi tanımını sildiğinde tanım <b>soft delete</b> olur ve kendi <i>çöp kutusuna</i> düşer; oradan geri alabilir veya kalıcı silebilir.",
                    "Tanımı bir moderatör veya admin sildiyse kullanıcı onu <b>kendi başına geri getiremez</b> ve çöp kutusundan silemez — moderasyon kararı kullanıcı tarafından geri alınamaz.",
                    "Silinen tanımlar moderasyon geçmişi için veritabanında kalır; birleştirme sırasında da hedefe taşınır.",
                ]
            },
        ],
        en: [
            { type: "p", text: "The values below are changed from the <i>Settings</i> tab and take effect immediately <b>without a restart</b>. This table shows the live values:" },
            {
                type: "settings",
                keys: [
                    "entry.min_length",
                    "entry.max_length",
                    "entry.daily_limit",
                    "entry.edit_window_minutes",
                    "entry.draft_limit",
                    "entry.preview_line_limit",
                    "moderation.spam_cooldown_seconds",
                ],
            },
            { type: "sub", text: "How the rules behave" },
            {
                type: "ul", items: [
                    "<b>Daily quota:</b> resets at midnight. Once the user reaches {{setting:entry.daily_limit}} entries today they cannot post and are told they have hit the daily limit. <b>0</b> means unlimited.",
                    "<b>Spam cooldown:</b> at least {{setting:moderation.spam_cooldown_seconds}} seconds must pass between two entries; exceeding it triggers a cooldown of the same length. <b>0</b> disables spam protection.",
                    "<b>Length:</b> trimmed content must be between {{setting:entry.min_length}} and {{setting:entry.max_length}} characters. The same check runs on edit.",
                    "<b>Edit window:</b> the author can edit for {{setting:entry.edit_window_minutes}} minutes after posting. <b>0</b> means always editable.",
                    "<b>Preview lines:</b> a “show more” link appears past {{setting:entry.preview_line_limit}} lines. <b>0</b> disables truncation.",
                    "<b>Drafts:</b> a user may keep up to {{setting:entry.draft_limit}} drafts. Drafts are private, never moderated, never voted on and are <b>always hard-deleted</b> (they never reach the trash).",
                ]
            },
            { type: "note", tone: "info", text: "For <b>ADMIN and MODERATOR</b> the daily quota, spam cooldown and edit window are <b>not enforced</b>. They can also post into locked topics." },
            { type: "sub", text: "Gündem: no consecutive entries" },
            {
                type: "ul", items: [
                    "<b>In Gündem topics only</b>, an author cannot post two entries in a row: if the topic’s latest (non-deleted) entry is their own, the new entry is rejected and the form shows “You cannot post two consecutive entries…”.",
                    "They can post again once another author has written in between, or after deleting their own latest entry.",
                    "Publishing a draft follows the same rule. Match, person, club and team topics are <b>not affected</b>.",
                    "On a Gündem page, the user’s earlier entries in that topic are listed under the entry form as <b>#id (date)</b> links; the newest {{const:own_topic_entries_max}} are shown. If there are more, an “and N more” line closes the list; older entries are not deleted and stay in the topic and on the profile.",
                ]
            },
            { type: "note", tone: "warn", text: "This is a content rule, not a quota: it <b>also applies to ADMIN and MODERATOR</b>." },
            { type: "sub", text: "Deleting and restoring" },
            {
                type: "ul", items: [
                    "When a user deletes their own entry it is <b>soft-deleted</b> into their personal <i>trash</i>, from which they can restore or permanently delete it.",
                    "If a moderator or admin deleted the entry, the user <b>cannot restore it themselves</b> and cannot purge it from their trash — a moderation decision is not user-reversible.",
                    "Deleted entries stay in the database for moderation history and are carried over during a merge.",
                ]
            },
        ],
    },
};

// ─────────────────────────────────────────────────────────────────────────────
// GROUP 2 — USERS, ROLES AND LIFECYCLE
// ─────────────────────────────────────────────────────────────────────────────

const SECTION_ROLES: ManualSection = {
    id: "roles",
    icon: "userShield",
    title: { tr: "Roller ve Yetki Matrisi", en: "Roles and Permission Matrix" },
    summary: {
        tr: "ÇAYLAK / YAZAR / MODERATÖR / ADMIN — hangi rol neyi yapabilir.",
        en: "ROOKIE / AUTHOR / MODERATOR / ADMIN — who can do what.",
    },
    blocks: {
        tr: [
            { type: "p", text: "Rol <b>kalıcıdır</b>; dondurma, askıya alma, silme gibi durum işlemleri rolü değiştirmez. Yetkiler ve tanımların görünümü role göre belirlenir. Bu bölüm rollerin ne yapabildiğini anlatır; okuyanın hangi rolde olduğunu varsaymaz." },
            {
                type: "table",
                headers: ["Rol", "Açıklama"],
                rows: [
                    ["<b>ÇAYLAK</b> (ROOKIE)", "Yeni kullanıcı. Tanımları soluk/gri gösterilir. <b>Oy veremez.</b> <b>Başlık öneremez.</b> Yeni kayıtların varsayılan rolüdür (Ayarlar’dan değiştirilebilir)."],
                    ["<b>YAZAR</b> (AUTHOR)", "Yerleşik yazar. Tanımları normal gösterilir, oy verebilir."],
                    ["<b>MODERATÖR</b>", "İçerik moderasyonunun tamamını yürütür: tanım düzenleme/silme, başlık kilitleme, sabitleme, taşıma ve <b>birleştirme</b>. Kullanıcı tarafında yalnızca çaylak ve yazarlara dokunabilir."],
                    ["<b>ADMIN</b>", "Tüm içerik ve kullanıcılar üzerinde tam yetki."],
                ],
            },
            { type: "sub", text: "Panel sekmelerine erişim" },
            {
                type: "table",
                headers: ["Sekme", "MODERATÖR", "ADMIN"],
                rows: [
                    ["Panel, Tanımlar, Kişiler, Maçlar, Kulüpler, Takımlar, Stadyumlar", "✔", "✔"],
                    ["Gündem (öneri onay/ret, başlık aç/düzenle)", "✔", "✔"],
                    ["Çaylaklar / Yazarlar sıralaması", "✔", "✔"],
                    ["Şikayetler", "✔", "✔"],
                    ["IP Yasakları", "✔", "✔"],
                    ["Sabitlenenler", "✔", "✔"],
                    ["Kullanıcılar", "✔", "✔"],
                    ["Taşı / Birleştir", "taşıma + <b>birleştirme</b>", "taşıma + <b>birleştirme</b>"],
                    ["Denetim Kayıtları", "✘", "✔"],
                    ["Master Data", "✘", "✔"],
                    ["Ayarlar", "✘", "✔"],
                    ["İletişim", "✘", "✔"],
                    ["Kullanım Kılavuzu (bu sayfa)", "✔", "✔"],
                ],
            },
            { type: "sub", text: "Kullanıcı üzerinde yapılabilecek işlemler" },
            { type: "p", text: "“Korumalı hedef” = rolü <b>ADMIN</b> veya <b>MODERATÖR</b> olan bir hesap. Moderatör rolü korumalı hedeflere aşağıdaki işlemlerin hiçbirini uygulayamaz; kullanıcı listesinde o satırın <b>rol, şifre ve askı butonları gri ve tıklanamaz</b> gelir, seçim kutusu da pasiftir. Hesabı görüntülemek için göz butonu kullanılır." },
            { type: "p", text: "<b>Tek istisna: onaylı rozeti.</b> Rozet yetki vermez, hesabın durumunu değiştirmez; profilde görünen bir işarettir. Bu yüzden her rol <b>kendi seviyesine</b> uygulayabilir — admin bir admini, moderatör bir moderatörü (ikisi de kendisi dâhil) onaylayabilir. Bu nedenle bir moderatörün, başka bir moderatör satırında <b>düzenle</b> butonu açık kalır: modalde yalnızca rozet kutusu aktiftir, rol / durum / şifre alanları gridir. Bir <b>ADMIN</b> satırında ise moderatör için düzenle butonu da kapalıdır." },
            { type: "p", text: "Aynı görünürlük kuralı <b>ADMIN için de</b> işler: bir adminin kendi satırında rol, durum, askıya alma, yasaklama ve silme butonları gri gelir; herhangi bir <b>ADMIN satırında</b> ise durum, askıya alma, yasaklama ve silme butonları gri gelir. Butonun üzerine gelince neden kapalı olduğu yazar. Yani reddedilen bir işlem artık tıklandıktan sonra hata mesajıyla değil, tıklanmadan önce görünür hâle gelir." },
            { type: "p", text: "Son iki sütun <b>hedefin rolünü</b> anlatır: işlem o role sahip bir hesaba uygulanabiliyor mu? Moderatör bir hedefe hiçbir moderatör dokunamayacağı için oradaki “Evet” daima <b>yalnızca ADMIN eliyle</b> demektir." },
            {
                type: "table",
                headers: ["İşlem", "MODERATÖR", "ADMIN", "Bir ADMIN’e uygulanabilir mi?", "Bir MODERATÖR’e uygulanabilir mi?"],
                rows: [
                    ["Kullanıcı oluşturma", "✘", "✔", "—", "—"],
                    ["Rol değiştirme", "✔ — sadece çaylak/yazar hedefe, sadece çaylak/yazar rolüne", "✔ — <b>kendi hesabı hariç</b>", "<b>Evet</b> — bir ADMIN başka bir ADMIN’in rolünü değiştirebilir", "<b>Evet</b> — sadece ADMIN eliyle"],
                    ["Askıya alma", "✔ — korumalı hedefe ✘", "✔", "<b>Hayır</b> — engellenir", "<b>Evet</b> — sadece ADMIN eliyle"],
                    ["Askıyı kaldırma", "✔ — korumalı hedefe ✘", "✔", "<b>Evet</b> — kurtarma yolu açık tutulur", "<b>Evet</b> — sadece ADMIN eliyle"],
                    ["Şifre değiştirme", "✔ — korumalı hedefe ✘", "✔", "<b>Evet</b> (kayda uyarı düşer)", "<b>Evet</b> — sadece ADMIN eliyle"],
                    ["Kullanıcı adı değiştirme", "✘", "✔", "Evet", "Evet — sadece ADMIN eliyle"],
                    ["E-posta değiştirme", "✘", "✔", "Evet", "Evet — sadece ADMIN eliyle"],
                    ["Onaylı rozeti verme/alma", "✔ — <b>moderatöre de uygulanabilir</b>, ADMIN hedefe ✘", "✔", "Evet — sadece ADMIN eliyle", "Evet — moderatör de yapabilir"],
                    ["Biyografi düzenleme / silme / bloklama", "✔ — hedef rolü fark etmez", "✔", "Evet", "Evet — moderatör de yapabilir"],
                    ["<b>Durum değiştirme</b> (doğrudan durum seçimi)", "✘", "✔", "<b>Hayır</b> — engellenir", "Evet — sadece ADMIN eliyle"],
                    ["<b>Yasaklama / yasağı kaldırma</b>", "✘", "✔", "<b>Hayır</b> — engellenir", "Evet — sadece ADMIN eliyle"],
                    ["<b>Silme</b> (soft veya hard)", "✘", "✔", "<b>Hayır</b> — engellenir", "Evet — sadece ADMIN eliyle"],
                    ["Toplu askı", "✔ — korumalı hedefler seçilemez", "✔", "—", "Evet — sadece ADMIN eliyle"],
                    ["Toplu yasak / toplu rol değişimi / toplu silme", "✘", "✔", "—", "Evet — sadece ADMIN eliyle"],
                ],
            },
            { type: "sub", text: "İçerik üzerinde yapılabilecek işlemler" },
            {
                type: "table",
                headers: ["İşlem", "MODERATÖR", "ADMIN"],
                rows: [
                    ["Tanım düzenleme / silme / geri alma", "✔", "✔"],
                    ["Başlık kilitleme, sabitleme (pin)", "✔", "✔"],
                    ["Gündem başlığı açma/düzenleme, öneri onay/ret", "✔", "✔"],
                    ["Maç / kulüp / takım / kişi / stadyum yönetimi", "✔ — kulüp pasife alma hariç", "✔"],
                    ["Tanım taşıma", "✔", "✔"],
                    ["<b>Başlık birleştirme</b>", "✔ — <b>geri alınamaz</b>", "✔"],
                    ["IP yasağı", "✔", "✔"],
                    ["Sistem ayarları, yasal metinler, master data, denetim kayıtları", "✘", "✔"],
                ],
            },
            { type: "note", tone: "warn", text: "<b>Hiçbir rol kendi hesabına işlem yapamaz</b> — kendi rolünü veya durumunu değiştiremez, kendini askıya alamaz, yasaklayamaz, silemez. Kendi rolünü değiştirme denemesi ayrı bir mesajla reddedilir: bunu <b>başka bir yönetici</b> yapmalıdır. Kural, son adminin kendini panelden kilitlemesini önlemek için vardır." },
            { type: "note", tone: "warn", text: "Bir <b>ADMIN hesabına</b> dört işlem herkese kapalıdır: <b>durum değiştirme, askıya alma, yasaklama ve silme</b>. Açık kalanlar: rol değiştirme, şifre değiştirme, kullanıcı adı / e-posta değiştirme ve onaylı rozeti. Yani bir ADMIN önce yazar seviyesine indirilir, sonra istenen işlem yapılır. <b>Askıyı ve yasağı kaldırma</b> bu kuralın dışındadır — hâlihazırda askıda/yasaklı bir admin, rolü düşürülmeden geri alınabilsin diye." },
            { type: "note", tone: "info", text: "<b>Rol değişimi anında geçerlidir.</b> Hedef kullanıcı o sırada açık bir sekmede oturuyorsa yeni rolü tarayıcısına anında iletilir; oturumu kapanmaz, sadece rozeti, yetkileri ve panel erişimi yerinde güncellenir. Yeniden giriş yapması gerekmez. Aynı şey toplu rol değişimi için de geçerlidir." },
            { type: "note", tone: "info", text: "Moderatör kısıtları hedefin <b>rolüne</b> göre işler — durumuna göre değil. Askıdaki bir moderatör bile diğer moderatörlerden korunmaya devam eder; askısı da bir moderatör tarafından kaldırılamaz." },
            { type: "note", tone: "danger", text: "<b>Başlık birleştirme moderatörlere de açıktır ve geri alınamaz.</b> Kaynak başlık yayından kalkar, tüm tanımları hedefe geçer. İşlem MERGE_TOPIC olarak denetim kaydına yazılır; birleştirme geçmişi Taşı / Birleştir sekmesinin altında listelenir." },
        ],
        en: [
            { type: "p", text: "The role is <b>permanent</b>; lifecycle operations (freeze, suspend, delete) never change it. Permissions and entry styling follow the role. This section describes what each role can do; it makes no assumption about the role of whoever is reading it." },
            {
                type: "table",
                headers: ["Role", "Description"],
                rows: [
                    ["<b>ROOKIE</b>", "New user. Entries rendered dimmed/grey. <b>Cannot vote.</b> <b>Cannot suggest topics.</b> Default role for new sign-ups (configurable in Settings)."],
                    ["<b>AUTHOR</b>", "Established writer. Entries rendered normally, can vote."],
                    ["<b>MODERATOR</b>", "Runs content moderation end to end: editing/deleting entries, locking topics, pinning, moving and <b>merging</b>. On the user side, only rookies and authors are in reach."],
                    ["<b>ADMIN</b>", "Full control over all content and users."],
                ],
            },
            { type: "sub", text: "Panel tab access" },
            {
                type: "table",
                headers: ["Tab", "MODERATOR", "ADMIN"],
                rows: [
                    ["Dashboard, Entries, People, Matches, Clubs, Teams, Stadiums", "✔", "✔"],
                    ["Gündem (approve/reject suggestions, create/edit topics)", "✔", "✔"],
                    ["Rookies / Authors ranking", "✔", "✔"],
                    ["Reports", "✔", "✔"],
                    ["Banned IPs", "✔", "✔"],
                    ["Pins", "✔", "✔"],
                    ["Users", "✔", "✔"],
                    ["Move / Merge", "move + <b>merge</b>", "move + <b>merge</b>"],
                    ["Audit logs", "✘", "✔"],
                    ["Master data", "✘", "✔"],
                    ["Settings", "✘", "✔"],
                    ["Contact", "✘", "✔"],
                    ["Usage manual (this page)", "✔", "✔"],
                ],
            },
            { type: "sub", text: "Actions on a user" },
            { type: "p", text: "A “protected target” is an account whose role is <b>ADMIN</b> or <b>MODERATOR</b>. The moderator role can apply none of the actions below to one; on that row the <b>role, password and suspend buttons are greyed out and unclickable</b> and the selection checkbox is disabled. The view button is how such an account is inspected." },
            { type: "p", text: "<b>One exception: the verified badge.</b> It grants no permission and changes no lifecycle state — it is a profile marker. So every role may set it at <b>its own level</b>: an admin can verify another admin, a moderator another moderator, either of them including themselves. That is why a moderator keeps the <b>edit</b> button on another moderator’s row: inside the modal only the badge checkbox is live, while role, status and password stay greyed out. On an <b>ADMIN</b> row the edit button is closed to a moderator as well." },
            { type: "p", text: "The same visibility rule applies <b>to admins as well</b>: on an admin’s own row the role, status, suspend, ban and delete buttons come up greyed out, and on <b>any ADMIN row</b> the status, suspend, ban and delete buttons do. Hovering a disabled button names the rule that closed it. A refusal is therefore visible before the click instead of arriving as an error toast after it." },
            { type: "p", text: "The last two columns are about the <b>target’s role</b>: can the action be applied to an account holding that role? Since no moderator may act on a moderator, a “Yes” in the last column always means <b>by an ADMIN only</b>." },
            {
                type: "table",
                headers: ["Action", "MODERATOR", "ADMIN", "Can it target an ADMIN?", "Can it target a MODERATOR?"],
                rows: [
                    ["Create user", "✘", "✔", "—", "—"],
                    ["Change role", "✔ — rookie/author targets only, rookie/author roles only", "✔ — <b>never on one’s own account</b>", "<b>Yes</b> — an ADMIN can change another ADMIN’s role", "<b>Yes</b> — by an ADMIN only"],
                    ["Suspend", "✔ — ✘ on a protected target", "✔", "<b>No</b> — blocked", "<b>Yes</b> — by an ADMIN only"],
                    ["Unsuspend", "✔ — ✘ on a protected target", "✔", "<b>Yes</b> — the recovery path stays open", "<b>Yes</b> — by an ADMIN only"],
                    ["Change password", "✔ — ✘ on a protected target", "✔", "<b>Yes</b> (logged with a warning)", "<b>Yes</b> — by an ADMIN only"],
                    ["Change username", "✘", "✔", "Yes", "Yes — by an ADMIN only"],
                    ["Change email", "✘", "✔", "Yes", "Yes — by an ADMIN only"],
                    ["Grant/revoke verified badge", "✔ — <b>a moderator target is allowed</b>, ✘ on an ADMIN", "✔", "Yes — by an ADMIN only", "Yes — a moderator can do this too"],
                    ["Edit / delete / block bio", "✔ — any target role", "✔", "Yes", "Yes — a moderator can do this too"],
                    ["<b>Change status</b> (direct status pick)", "✘", "✔", "<b>No</b> — blocked", "Yes — by an ADMIN only"],
                    ["<b>Ban / unban</b>", "✘", "✔", "<b>No</b> — blocked", "Yes — by an ADMIN only"],
                    ["<b>Delete</b> (soft or hard)", "✘", "✔", "<b>No</b> — blocked", "Yes — by an ADMIN only"],
                    ["Bulk suspend", "✔ — protected targets cannot be selected", "✔", "—", "Yes — by an ADMIN only"],
                    ["Bulk ban / bulk role change / bulk delete", "✘", "✔", "—", "Yes — by an ADMIN only"],
                ],
            },
            { type: "sub", text: "Actions on content" },
            {
                type: "table",
                headers: ["Action", "MODERATOR", "ADMIN"],
                rows: [
                    ["Edit / delete / restore an entry", "✔", "✔"],
                    ["Lock a topic, pin a topic or entry", "✔", "✔"],
                    ["Create/edit Gündem topics, approve/reject suggestions", "✔", "✔"],
                    ["Match / club / team / person / stadium management", "✔ — except deactivating a club", "✔"],
                    ["Move entries", "✔", "✔"],
                    ["<b>Merge topics</b>", "✔ — <b>irreversible</b>", "✔"],
                    ["IP bans", "✔", "✔"],
                    ["Settings, legal documents, master data, audit logs", "✘", "✔"],
                ],
            },
            { type: "note", tone: "warn", text: "<b>No role can act on its own account</b> — no self role change, status change, suspension, ban or deletion. A self role change is refused with its own message: <b>another admin</b> has to perform it. The rule exists so the last admin cannot lock themselves out of the panel." },
            { type: "note", tone: "warn", text: "Against an <b>ADMIN account</b> four actions are closed to everyone: <b>change status, suspend, ban and delete</b>. Still open: role change, password change, username / e-mail change and the verified badge. So an ADMIN is demoted to author first and then handled. <b>Unsuspend and unban</b> are exempt — an already suspended or banned admin can be restored without a demotion round-trip." },
            { type: "note", tone: "info", text: "<b>A role change takes effect immediately.</b> If the target is signed in at that moment, the new role is pushed to their browser at once; their session is not ended, only the badge, the permissions and the panel access update in place. No re-login is needed. The same holds for bulk role changes." },
            { type: "note", tone: "info", text: "Moderator restrictions follow the target’s <b>role</b>, not their status — even a suspended moderator stays protected from other moderators, and their suspension cannot be lifted by a moderator either." },
            { type: "note", tone: "danger", text: "<b>Topic merging is open to moderators too, and it cannot be undone.</b> The source topic goes offline and all of its entries move to the target. The operation is audited as MERGE_TOPIC and the merge history is listed under the Move / Merge tab." },
        ],
    },
};

const SECTION_STATUSES: ManualSection = {
    id: "statuses",
    icon: "userClock",
    title: { tr: "Kullanıcı Durumları: Tam Tablo", en: "User Statuses: The Full Picture" },
    summary: {
        tr: "Her durumda kullanıcı ne yapabilir, ne görür, profilinde ne yazar.",
        en: "In each status: what the user can do, what they see, what shows on their profile.",
    },
    blocks: {
        tr: [
            { type: "p", text: "Bir hesabın <b>rolü</b> (çaylak/yazar/moderatör/admin) ile <b>durumu</b> ayrı şeylerdir. Rol yetkiyi, durum ise hesabın o anda kullanılabilir olup olmadığını belirler. Aşağıdaki üç tablo her durumda ne olduğunu anlatır." },

            { type: "sub", text: "1) Kullanıcı ne yapabilir?" },
            { type: "p", text: "Kritik nokta şu: <b>giriş yapılamayan her durumda kullanıcı hiçbir şey yapamaz.</b> Tanım giremez, oy veremez, mesaj atamaz, <b>kendi tanımlarını silemez</b>, ayarlarını değiştiremez — çünkü siteye hiç giremez." },
            {
                type: "table",
                headers: ["Durum", "Giriş yapabilir mi?", "Tanım girebilir mi?", "Oy / mesaj", "Kendi tanımını silebilir mi?", "Ayarlarını değiştirebilir mi?"],
                rows: [
                    ["<b>Aktif</b>", "✔ Evet", "✔", "✔", "✔", "✔"],
                    ["<b>Askıda</b>", "✘ Hayır", "✘", "✘", "✘", "✘"],
                    ["<b>Yasaklı</b>", "✘ Hayır", "✘", "✘", "✘", "✘"],
                    ["<b>Dondurulmuş</b> (kendi isteğiyle)", "İlk 24 saat ✘, sonra ✔ (girince hesap açılır)", "Girene kadar ✘", "Girene kadar ✘", "Girene kadar ✘", "Girene kadar ✘"],
                    ["<b>Silme talebi verilmiş</b>", "İlk 24 saat ✘, sonra ✔ (girince talep iptal olur)", "Girene kadar ✘", "Girene kadar ✘", "Girene kadar ✘", "Girene kadar ✘"],
                    ["<b>Admin sildi</b>", "✘ Hayır", "✘", "✘", "✘", "✘"],
                    ["<b>Pasif</b>", "✘ Hayır", "✘", "✘", "✘", "✘"],
                ],
            },
            { type: "note", tone: "warn", text: "Bu yüzden bir kullanıcıyı askıya aldığınızda <b>içeriği kendisi temizleyemez</b>. Tanımların gitmesini istiyorsanız bunu siz yapmalısınız (Tanımlar sekmesinden tek tek, ya da Kullanıcılar sekmesindeki silme akışında “tanımları da sil” seçeneğiyle)." },

            { type: "sub", text: "2) Diğerleri ne görür?" },
            {
                type: "table",
                headers: ["Durum", "Tanımları sitede görünür mü?", "Profili açılır mı?", "Profilde görünen bant / rozet", "Online listesinde"],
                rows: [
                    ["<b>Aktif</b>", "✔ Görünür", "✔", "Yok", "Bağlıysa görünür"],
                    ["<b>Askıda</b>", "✔ <b>Görünür</b> — askı içeriği silmez", "✔", "<b>Askıya alındı</b> bandı + bitiş tarihi (süresiz askıda tarihsiz)", "Görünmez (giremez)"],
                    ["<b>Yasaklı</b>", "✔ <b>Görünür</b> — yasak içeriği silmez", "✔", "<b>Kalıcı olarak engellendi</b> bandı + engellenme tarihi", "Görünmez"],
                    ["<b>Dondurulmuş</b>", "✔ Görünür", "✔", "<b>Dondurulmuş hesap</b> bandı", "Görünmez"],
                    ["<b>Silme talebi verilmiş</b>", "✔ Görünür (süre dolana kadar)", "✔", "<b>Silme bekliyor</b> bandı + kalan süre; sahibine iptal butonu", "Görünmez"],
                    ["<b>Admin sildi</b>", "Adminin seçimine bağlı — bkz. Kullanıcı Silme bölümü", "✔", "<b>Admin tarafından silindi</b> bandı", "Görünmez"],
                    ["<b>Pasif</b>", "✔ Görünür", "✔", "Yok", "Görünmez"],
                ],
            },
            { type: "note", tone: "info", text: "Askı ve yasak <b>hiçbir tanımı silmez</b>. İçerik sitede kalmaya devam eder; sadece hesap kullanılamaz hale gelir. Kullanıcının tanımlarının da gitmesi gerekiyorsa ayrıca silinmelidir." },
            { type: "note", tone: "info", text: "Askı bandı <b>işlemi kimin yaptığını yazmaz</b> — sadece hesabın yönetim tarafından hangi tarihe kadar askıya alındığını söyler. Askıyı uygulayan moderatör/admin yalnızca <b>Denetim Kayıtları</b>'nda görünür." },
            { type: "note", tone: "info", text: "Yasak bandı da aynı kuralı izler: <b>kimin yasakladığını ve gerekçeyi yazmaz</b>, sadece hesabın yönetim tarafından hangi tarihte kalıcı olarak engellendiğini söyler. <b>Gerekçe yalnızca yasaklanan kullanıcıya</b>, kendi giriş denemesinde gösterilir — profili açan üçüncü kişiler görmez." },

            { type: "sub", text: "3) Kullanıcı giriş yapmaya çalışırsa ne görür, nasıl çıkar?" },
            {
                type: "table",
                headers: ["Durum", "Giriş ekranında gördüğü", "Bu durumdan nasıl çıkılır?", "Ne kadar sürer?"],
                rows: [
                    ["<b>Askıda</b>", "“Hesabın askıya alınmış, <b>şu tarihe kadar</b> giriş yapamazsın” — bitiş tarihi ve saati mesajın içinde yazar. Süresiz askıda tarihsiz varyant çıkar.", "Süre dolunca kendiliğinden, ya da moderatör/admin askıyı kaldırınca", "Askıya alırken girilen gün sayısı kadar"],
                    ["<b>Yasaklı</b>", "“Hesabın kalıcı olarak engellendi” — yasaklarken <b>gerekçe girildiyse</b> mesajın sonunda o gerekçe de yazar. Mesajın altında <b>iletişim sayfasına giden bir bağlantı</b> çıkar.", "Sadece bir ADMIN yasağı kaldırırsa", "Süresizdir"],
                    ["<b>Dondurulmuş</b>", "İlk 24 saat: “hesap donduruldu, şu tarihten sonra girebilirsiniz”. Sonrasında normal giriş yapar.", "Kullanıcı giriş yapar → hesap otomatik açılır. Ayarlar sayfasından da kendisi çözebilir.", "24 saat kilit, sonra anında"],
                    ["<b>Silme talebi verilmiş</b>", "İlk 24 saat: “iptal için şu tarihten sonra girebilirsiniz”. Sonrasında normal giriş yapar.", "Giriş yapmak talebi iptal eder; profilindeki banttan da iptal edebilir.", "{{const:deletion_grace_days}} gün içinde giriş yapmazsa hesap kalıcı silinir"],
                    ["<b>Admin sildi</b>", "“Hesabınız silindi”", "Sadece bir ADMIN durumu tekrar Aktif yaparsa", "Admin karar verene kadar"],
                    ["<b>Pasif</b>", "E-posta doğrulaması bekliyorsa “e-postanızı doğrulayın”, değilse “hesabınız pasif”", "E-postadaki doğrulama linkine tıklayarak <b>veya</b> şifre sıfırlayarak — ikisi de hesabı otomatik aktifleştirir", "Anında"],
                ],
            },

            { type: "sub", text: "Kullanıcı kendi hesabına ne yapabilir?" },
            {
                type: "table",
                headers: ["İşlem", "Nereden", "Sonrası", "Tekrar / iptal"],
                rows: [
                    ["<b>Hesabı dondurma</b>", "Ayarlar → Hesap", "Hesap dondurulur, profilde bant çıkar, çıkış yapılır", "İlk <b>{{const:freeze_cooldown_hours}} saat</b> ayarlardan iptal edilemez; ama giriş yapmak her zaman dondurmayı çözer"],
                    ["<b>Hesabı silme talebi</b>", "Ayarlar → Hesap", "<b>{{const:deletion_grace_days}} günlük</b> geri sayım başlar, profilde bant + kalan süre görünür", "İlk <b>{{const:deletion_cancel_cooldown_hours}} saat</b> giriş kapalı; sonrasında giriş yapmak talebi iptal eder"],
                    ["<b>Silme talebinde tanımları da silme</b>", "Silme talebi modalindeki seçenek", "Hesapla birlikte tanımları da gider", "Talep iptal edilirse silme hiç gerçekleşmez"],
                    ["<b>Kalıcı silme tercihi</b>", "Ayarlar → Tanım Ayarları", "Açıksa kullanıcının sildiği tanımlar çöp kutusuna düşmez, doğrudan gider", "İstediği zaman kapatabilir"],
                ],
            },
            { type: "note", tone: "danger", text: "Silme talebi verilen hesap, bekleme süresi içinde giriş yapılmazsa çalışma programı <b>{{setting:scheduler.account_deletion.cron.human}}</b> olan iş tarafından <b>kalıcı olarak</b> silinir. Kullanıcı adı ve e-posta serbest kalır, geri dönüşü yoktur." },

            { type: "sub", text: "Kimler kurallardan muaftır?" },
            { type: "p", text: "Aşağıdaki kısıtlar <b>moderatör ve adminlere uygulanmaz</b>:" },
            {
                type: "table",
                headers: ["Kural", "Çaylak", "Yazar", "Moderatör", "Admin"],
                rows: [
                    ["Günlük tanım kotası", "Uygulanır", "Uygulanır", "<b>Muaf</b>", "<b>Muaf</b>"],
                    ["Tanımlar arası bekleme süresi (spam koruması)", "Uygulanır", "Uygulanır", "<b>Muaf</b>", "<b>Muaf</b>"],
                    ["Düzenleme penceresi (süre dolunca düzenleyememe)", "Uygulanır", "Uygulanır", "<b>Muaf</b>", "<b>Muaf</b>"],
                    ["Kilitli başlığa tanım girememe", "Uygulanır", "Uygulanır", "<b>Muaf</b>", "<b>Muaf</b>"],
                    ["Başlık önerebilme", "<b>Öneremez</b>", "Önerebilir", "Önerebilir", "Önerebilir"],
                    ["Başlık önerme bekleme süresi ve günlük limiti", "—", "Uygulanır", "<b>Muaf</b>", "<b>Muaf</b>"],
                    ["Oy verebilme", "<b>Veremez</b>", "Verebilir", "Verebilir", "Verebilir"],
                    ["Kendi tanımına oy verme", "Yasak", "Yasak", "Yasak", "Yasak"],
                    ["Tanım uzunluk sınırları", "Uygulanır", "Uygulanır", "Uygulanır", "Uygulanır"],
                ],
            },
        ],
        en: [
            { type: "p", text: "An account’s <b>role</b> (rookie/author/moderator/admin) and its <b>status</b> are different things. The role defines permissions; the status defines whether the account is usable right now. The three tables below cover what happens in each status." },

            { type: "sub", text: "1) What can the user do?" },
            { type: "p", text: "The key point: <b>in any status where login is blocked, the user can do nothing at all.</b> No entries, no votes, no messages, <b>no deleting their own entries</b>, no settings changes — because they cannot get in." },
            {
                type: "table",
                headers: ["Status", "Can log in?", "Can post entries?", "Vote / message", "Can delete own entries?", "Can change settings?"],
                rows: [
                    ["<b>Active</b>", "✔ Yes", "✔", "✔", "✔", "✔"],
                    ["<b>Suspended</b>", "✘ No", "✘", "✘", "✘", "✘"],
                    ["<b>Banned</b>", "✘ No", "✘", "✘", "✘", "✘"],
                    ["<b>Frozen</b> (self)", "First 24h ✘, then ✔ (login reopens it)", "✘ until login", "✘ until login", "✘ until login", "✘ until login"],
                    ["<b>Deletion requested</b>", "First 24h ✘, then ✔ (login cancels it)", "✘ until login", "✘ until login", "✘ until login", "✘ until login"],
                    ["<b>Deleted by admin</b>", "✘ No", "✘", "✘", "✘", "✘"],
                    ["<b>Inactive</b>", "✘ No", "✘", "✘", "✘", "✘"],
                ],
            },
            { type: "note", tone: "warn", text: "This is why a suspended user <b>cannot clean up their own content</b>. If you want their entries gone, you have to do it — one by one from the Entries tab, or via the “also delete entries” option in the user deletion flow." },

            { type: "sub", text: "2) What does everyone else see?" },
            {
                type: "table",
                headers: ["Status", "Entries visible on the site?", "Profile opens?", "Banner / badge on profile", "In the online list"],
                rows: [
                    ["<b>Active</b>", "✔ Visible", "✔", "None", "Visible when connected"],
                    ["<b>Suspended</b>", "✔ <b>Visible</b> — suspension deletes nothing", "✔", "<b>Suspended</b> banner + end date (undated variant for an open-ended suspension)", "Not shown (cannot log in)"],
                    ["<b>Banned</b>", "✔ <b>Visible</b> — a ban deletes nothing", "✔", "<b>Permanently banned</b> banner + ban date", "Not shown"],
                    ["<b>Frozen</b>", "✔ Visible", "✔", "<b>Frozen account</b> banner", "Not shown"],
                    ["<b>Deletion requested</b>", "✔ Visible until the deadline", "✔", "<b>Deletion pending</b> banner + remaining time; cancel button for the owner", "Not shown"],
                    ["<b>Deleted by admin</b>", "Depends on the admin’s choice — see the User Deletion section", "✔", "<b>Deleted by admin</b> banner", "Not shown"],
                    ["<b>Inactive</b>", "✔ Visible", "✔", "None", "Not shown"],
                ],
            },
            { type: "note", tone: "info", text: "Suspension and bans <b>delete nothing</b>. The content stays on the site; only the account becomes unusable. If the entries must go too, delete them separately." },
            { type: "note", tone: "info", text: "The suspension banner <b>never names who acted</b> — it only states that the account is suspended by moderation and until when. The moderator or admin behind it appears in <b>Audit Logs</b> only." },
            { type: "note", tone: "info", text: "The ban banner follows the same rule: it <b>names neither the admin nor the reason</b>, only that the account was permanently banned by moderation and on what date. The <b>reason is shown to the banned user alone</b>, on their own login attempt — visitors opening the profile never see it." },

            { type: "sub", text: "3) What does the user see when trying to log in, and how do they get out?" },
            {
                type: "table",
                headers: ["Status", "Login screen message", "How it ends", "How long"],
                rows: [
                    ["<b>Suspended</b>", "“Your account is suspended, you cannot log in <b>until this date</b>” — the end date and time are part of the message. An open-ended suspension gets the undated variant.", "Expires by itself, or a moderator/admin lifts it", "As many days as entered when suspending"],
                    ["<b>Banned</b>", "“Your account has been permanently banned” — when a <b>reason was entered</b> at ban time, that reason is appended to the message. A <b>link to the contact page</b> appears underneath.", "Only if an ADMIN unbans", "Indefinite"],
                    ["<b>Frozen</b>", "First 24h: “frozen, you can log in after this date”. After that, a normal login.", "The user logs in → the account reopens automatically. They can also unfreeze from Settings.", "24-hour lockout, then instant"],
                    ["<b>Deletion requested</b>", "First 24h: “you can log in to cancel after this date”. After that, a normal login.", "Logging in cancels the request; they can also cancel from the profile banner.", "If they do not log in within {{const:deletion_grace_days}} days, the account is permanently deleted"],
                    ["<b>Deleted by admin</b>", "“Your account has been deleted”", "Only if an ADMIN sets the status back to Active", "Until the admin decides"],
                    ["<b>Inactive</b>", "“Verify your email” if verification is pending, otherwise “your account is inactive”", "Click the verification link <b>or</b> reset the password — either one auto-activates the account", "Instant"],
                ],
            },

            { type: "sub", text: "What can users do to their own account?" },
            {
                type: "table",
                headers: ["Action", "Where", "What happens", "Repeat / cancel"],
                rows: [
                    ["<b>Freeze the account</b>", "Settings → Account", "Account freezes, a banner appears on the profile, the user is logged out", "Cannot be cancelled from Settings for <b>{{const:freeze_cooldown_hours}} hours</b>; logging in always unfreezes"],
                    ["<b>Request deletion</b>", "Settings → Account", "A <b>{{const:deletion_grace_days}}-day</b> countdown starts; the profile shows a banner with the remaining time", "Login is blocked for the first <b>{{const:deletion_cancel_cooldown_hours}} hours</b>; after that, logging in cancels it"],
                    ["<b>Delete entries along with the account</b>", "Option inside the deletion modal", "Entries go with the account", "If the request is cancelled, nothing is deleted"],
                    ["<b>Permanent-delete preference</b>", "Settings → Entry settings", "When on, entries the user deletes skip the trash and are gone immediately", "Can be turned off any time"],
                ],
            },
            { type: "note", tone: "danger", text: "If nobody logs in during the grace period, the account is <b>permanently</b> deleted by the job whose schedule is <b>{{setting:scheduler.account_deletion.cron.human}}</b>. The username and email are freed and there is no way back." },

            { type: "sub", text: "Who is exempt from the rules?" },
            { type: "p", text: "The limits below <b>do not apply to moderators and admins</b>:" },
            {
                type: "table",
                headers: ["Rule", "Rookie", "Author", "Moderator", "Admin"],
                rows: [
                    ["Daily entry quota", "Applies", "Applies", "<b>Exempt</b>", "<b>Exempt</b>"],
                    ["Cooldown between entries (spam protection)", "Applies", "Applies", "<b>Exempt</b>", "<b>Exempt</b>"],
                    ["Edit window (cannot edit after it expires)", "Applies", "Applies", "<b>Exempt</b>", "<b>Exempt</b>"],
                    ["Blocked from posting in locked topics", "Applies", "Applies", "<b>Exempt</b>", "<b>Exempt</b>"],
                    ["Can suggest topics", "<b>No</b>", "Yes", "Yes", "Yes"],
                    ["Topic suggestion cooldown and daily limit", "—", "Applies", "<b>Exempt</b>", "<b>Exempt</b>"],
                    ["Can vote", "<b>No</b>", "Yes", "Yes", "Yes"],
                    ["Voting on own entry", "Forbidden", "Forbidden", "Forbidden", "Forbidden"],
                    ["Entry length limits", "Applies", "Applies", "Applies", "Applies"],
                ],
            },
        ],
    },
};

const SECTION_DELETION: ManualSection = {
    id: "deletion",
    icon: "userSlash",
    title: { tr: "Kullanıcı Silme: Ne Oluyor?", en: "Deleting a User: What Happens?" },
    summary: {
        tr: "Soft delete, hard delete, tanımların akıbeti ve geri getirme.",
        en: "Soft delete, hard delete, what happens to entries and restoration.",
    },
    blocks: {
        tr: [
            { type: "note", tone: "danger", text: "Kullanıcı silme <b>yalnızca ADMIN</b> yetkisindedir. Hiçbir hesap kendini silemez; bir ADMIN hesabı da doğrudan silinemez. Gerekiyorsa önce o hesabın rolü yazar seviyesine indirilir, sonra silinir." },
            { type: "sub", text: "1) Soft delete (varsayılan)" },
            {
                type: "ul", items: [
                    "Durum <b>DELETED_BY_ADMIN</b> olur; giriş kapanır, kullanıcı normal listelerden düşer.",
                    "Kullanıcı satırı ve ona bağlı tüm kayıtlar veritabanında <b>durur</b>: kullanıcı adı ve e-posta <b>hâlâ dolu sayılır</b>, tekrar kullanılamaz.",
                    "Profilde <b>DELETED_BY_ADMIN</b> bandı görünür.",
                    "İki seçenek vardır: <b>tanımları da sil</b> (soft ya da hard) veya tanımları olduğu gibi bırak.",
                    "Tanımlar soft silinirse özel bir sebep etiketiyle işaretlenir; bu sayede kullanıcı ileride yeniden aktifleştirilirse <b>tam olarak o tanımlar</b> geri gelir.",
                ]
            },
            { type: "note", tone: "ok", text: "<b>Geri getirme:</b> Bir kullanıcının durumu DELETED_BY_ADMIN’den ACTIVE’e çevrildiğinde, hesap silme işlemiyle birlikte silinmiş tanımlar otomatik geri yüklenir. Ancak <b>bağımsız olarak moderasyon nedeniyle silinmiş tanımlar geri gelmez</b> — onların silme sebebi farklıdır ve kasıtlı olarak silinmiş kalır." },
            { type: "sub", text: "2) Hard delete (fiziksel silme)" },
            { type: "p", text: "Kullanıcı satırının kendisi ve ona referans veren her şey <b>fiziksel olarak</b> silinir. <b>Geri dönüşü yoktur.</b> Kullanıcı adı ve e-posta serbest kalır." },
            { type: "p", text: "Silme işlemi, kullanıcıya bağlı her kaydı belirli bir sırayla tek tek temizler. Sırayla silinenler:" },
            {
                type: "ol", items: [
                    "Sabitlenmiş tanım referansları (pinned_entry_id)",
                    "Nickaltı başlığının içeriği ve o tanımlara verilen oylar",
                    "Tanım şikayetleri",
                    "Mesajlar → konuşmalar",
                    "Bildirimler",
                    "Takipler → engellemeler",
                    "Taslaklar → tercihler → istatistikler → favoriler",
                    "Profil → başlık önerileri",
                    "Başka kullanıcıların tanımlarındaki moderasyon referansları (silen/düzenleyen alanları)",
                    "Maç denetim kolonları",
                    "Son olarak kullanıcı satırı",
                ]
            },
            { type: "note", tone: "warn", text: "Toplu silme işlemlerinde <b>her kullanıcı için ayrı bir denetim kaydı</b> yazılır; silme notu da denetim kaydına eklenir." },
            { type: "sub", text: "Silinen kullanıcının içeriği ne olur?" },
            {
                type: "table",
                headers: ["Senaryo", "Kullanıcı adı serbest kalır mı?", "Tanımlar", "Geri alınabilir mi?"],
                rows: [
                    ["Soft delete, tanımlara dokunma", "Hayır", "Sitede kalır", "Evet"],
                    ["Soft delete + tanımları soft sil", "Hayır", "Gizlenir, DB’de durur", "Evet — aktifleştirmede geri gelir"],
                    ["Soft delete + tanımları hard sil", "Hayır", "Fiziksel silinir (önce oylar)", "Hayır"],
                    ["Hard delete (kullanıcı satırı)", "Evet", "Fiziksel silinir", "Hayır"],
                    ["Kendi silme talebi + <b>tanımlarımı da sil</b>", "Hayır — kullanıcı adı ve e-posta dolu kalır", "Fiziksel silinir", "Bekleme süresi içinde giriş yaparak"],
                    ["Kendi silme talebi + <b>tanımlarım kalsın</b>", "Hayır — kullanıcı adı ve e-posta dolu kalır", "Sitede aynı kullanıcı adıyla kalır", "Bekleme süresi içinde giriş yaparak"],
                ],
            },
        ],
        en: [
            { type: "note", tone: "danger", text: "Deleting users is <b>ADMIN only</b>. You cannot delete yourself, and you cannot delete an ADMIN account directly. If you must, demote that account to author first, then delete it." },
            { type: "sub", text: "1) Soft delete (default)" },
            {
                type: "ul", items: [
                    "The status becomes <b>DELETED_BY_ADMIN</b>; login is blocked and the user drops out of regular views.",
                    "The user row and everything referencing it <b>stays</b> in the database: the username and email remain <b>taken</b>.",
                    "A <b>DELETED_BY_ADMIN</b> banner is shown on the profile.",
                    "Two options: <b>also delete the entries</b> (soft or hard), or leave them untouched.",
                    "Soft-deleted entries are tagged with a dedicated reason, so if the user is reactivated later, <b>exactly those entries</b> come back.",
                ]
            },
            { type: "note", tone: "ok", text: "<b>Restoration:</b> when a user goes from DELETED_BY_ADMIN back to ACTIVE, the entries removed by that account deletion are restored automatically. However, entries deleted separately for moderation reasons <b>do not</b> come back — their deletion reason differs and they stay deleted on purpose." },
            { type: "sub", text: "2) Hard delete" },
            { type: "p", text: "The user row itself and everything referencing it is <b>physically removed</b>. <b>Irreversible.</b> The username and email are freed." },
            { type: "p", text: "The deletion clears every record attached to the user, one group at a time, in this order:" },
            {
                type: "ol", items: [
                    "Pinned entry references (pinned_entry_id)",
                    "Nickaltı topic contents and votes on those entries",
                    "Entry reports",
                    "Messages → conversations",
                    "Notifications",
                    "Follows → blocks",
                    "Drafts → preferences → stats → favorites",
                    "Profile → topic suggestions",
                    "Moderation references on other users’ entries (deleted-by / edited-by columns)",
                    "Match audit columns",
                    "Finally the user row",
                ]
            },
            { type: "note", tone: "warn", text: "Bulk deletions write <b>one audit log entry per user</b>, and the deletion note is stored in the audit log." },
            { type: "sub", text: "What happens to their content?" },
            {
                type: "table",
                headers: ["Scenario", "Username freed?", "Entries", "Reversible?"],
                rows: [
                    ["Soft delete, keep entries", "No", "Stay visible", "Yes"],
                    ["Soft delete + soft-delete entries", "No", "Hidden, kept in DB", "Yes — restored on reactivation"],
                    ["Soft delete + hard-delete entries", "No", "Physically removed (votes first)", "No"],
                    ["Hard delete (user row)", "Yes", "Physically removed", "No"],
                    ["Self-deletion + <b>delete my entries</b>", "No — username and e-mail stay taken", "Physically removed", "By logging in during the grace period"],
                    ["Self-deletion + <b>keep my entries</b>", "No — username and e-mail stay taken", "Stay visible under the same username", "By logging in during the grace period"],
                ],
            },
        ],
    },
};

// ─────────────────────────────────────────────────────────────────────────────
// ACCOUNT LIFECYCLE TIMELINE
// One place that answers "what happens at which hour" for every account state.
// The individual rules also live in the status / deletion sections; this section
// exists because the ORDER and the CLOCK are what people actually get wrong.
// ─────────────────────────────────────────────────────────────────────────────

const SECTION_LIFECYCLE_TIMELINE: ManualSection = {
    id: "lifecycle-timeline",
    icon: "userClock",
    title: { tr: "Hesap Zaman Çizelgesi: Hangi Saatte Ne Olur?", en: "Account Timeline: What Happens When" },
    summary: {
        tr: "Silme, dondurma, askı ve yasak durumlarının saat saat akışı.",
        en: "Hour-by-hour flow of deletion, freeze, suspension and ban.",
    },
    blocks: {
        tr: [
            { type: "note", tone: "info", text: "Tüm saatler <b>sunucu saatiyle (UTC)</b> hesaplanır. Kullanıcıya gösterilen tarihler tarayıcının yerel saatine çevrilir; bu yüzden bir kullanıcının gördüğü saat ile buradaki saat arasında saat farkı olabilir." },

            { type: "sub", text: "1) Kullanıcı kendi hesabını siliyor — <b>tanımlarım kalsın</b>" },
            {
                type: "timeline",
                caption: "En sık kullanılan senaryo. Tanımlar sitede kalır, hesabın kendisi anonimleşir.",
                steps: [
                    { at: "T+0", label: "Talep oluşturuldu", tone: "warn", text: "Durum <b>DELETED_BY_USER</b> olur, oturum kapanır. Tanımlara dokunulmaz. <code>scheduled_deletion_at = T + {{const:deletion_grace_days}} gün</code> yazılır." },
                    { at: "T+0 → T+{{const:deletion_cancel_cooldown_hours}} saat", label: "Kilit süresi", tone: "danger", text: "Giriş <b>tamamen kapalıdır</b>. Kullanıcı bu sürede talebini iptal <b>edemez</b>. Giriş ekranında hem kilidin açılacağı saat hem de silinme tarihi gösterilir." },
                    { at: "T+{{const:deletion_cancel_cooldown_hours}} saat", label: "Kilit açılır", tone: "info", text: "Kullanıcı tekrar giriş yapabilir. Giriş yapar yapmaz silme uyarısı ekranı çıkar: silinme tarihi + tek tıkla iptal butonu." },
                    { at: "T+{{const:deletion_cancel_cooldown_hours}} saat → T+{{const:deletion_grace_days}} gün", label: "İptal penceresi", tone: "ok", text: "Bu aralıkta giriş yapıp <b>Profil</b> ya da <b>Ayarlar</b> sayfasından talep iptal edilebilir. İptal edilirse durum ACTIVE'e döner, hiçbir veri kaybolmaz." },
                    { at: "T+{{const:deletion_grace_days}} gün", label: "Süre doldu", tone: "danger", text: "Giriş <b>anında</b> kapanır — zamanlayıcı henüz çalışmamış olsa bile. Bu andan sonra hesap geri alınamaz." },
                    { at: "Silme işinin sıradaki çalışması", label: "Hesap kapatılır", tone: "danger", text: "Silme işi ({{setting:scheduler.account_deletion.cron.human}}) hesabı kalıcı olarak kapatır. <b>Silinen:</b> profil bilgileri (bio, avatar, kapak — dosyalar diskten de kaldırılır), mesajlar, konuşmalar, bildirimler, takipler, engellemeler, taslaklar, verilen oylar, tercihler, favori kulüpler. <b>Kalan:</b> kullanıcı adı, e-posta, tanımlar. Kimlik alanlarına dokunulmaz; giriş <code>closed_at</code> damgasıyla engellenir." },
                ],
            },
            { type: "note", tone: "warn", text: "Kapatılan hesabın <b>kullanıcı adı ve e-postası serbest kalmaz</b>: satır yerinde durduğu için ikisi de dolu sayılır, aynı ad veya adresle yeni kayıt açılamaz. Giriş bir daha açılmaz; kullanıcı silme talebini geri alamaz." },

            { type: "note", tone: "info", text: "Kapatma tamamlandıktan sonra durum yine <b>DELETED_BY_USER</b> olarak kalır — olan budur. Yönetici panelinde rozet <b>“hesabını kapattı”</b>ya döner (bekleyen talepte <b>“hesabını siliyor”</b> yazar), profilde de kapatılma bandı görünür." },

            { type: "sub", text: "2) Kullanıcı kendi hesabını siliyor — <b>tanımlarımı da sil</b>" },
            { type: "note", tone: "info", text: "Bu senaryo birincisiyle <b>aynı</b> biter: hesap kapatılır, satır yerinde kalır, panelde “hesabını kapattı” yazar. Tek fark tanımların da silinmesidir. Kullanıcı satırını tamamen yok eden tek işlem, yöneticinin <b>hard delete</b> seçeneğidir." },
            {
                type: "timeline",
                caption: "İlk üç adım yukarıdakiyle birebir aynıdır; yalnızca son adım farklıdır.",
                steps: [
                    { at: "T+0", label: "Talep oluşturuldu", tone: "warn", text: "Durum <b>DELETED_BY_USER</b>. Kullanıcı ayrıca <b>tanımlarım hemen gizlensin</b> derse tanımlar aynı anda soft-delete edilir ve siteden kaybolur." },
                    { at: "T+{{const:deletion_cancel_cooldown_hours}} saat → T+{{const:deletion_grace_days}} gün", label: "İptal penceresi", tone: "ok", text: "İptal edilirse gizlenen tanımlar da <b>otomatik geri gelir</b>." },
                    { at: "Silme işinin sıradaki çalışması", label: "Hesap kapatılır", tone: "danger", text: "Önce tanımlar (ve onlara verilen oylar, şikayetler, sabitlemeler) fiziksel olarak silinir, ardından hesap birinci senaryodaki gibi kapatılır. Hesap satırı yerinde kalır, panelde <b>“hesabını kapattı”</b> olarak görünür; <b>kullanıcı adı ve e-posta serbest kalmaz</b>. Geri dönüşü yoktur." },
                ],
            },

            { type: "sub", text: "3) Hesap dondurma (kullanıcının kendisi)" },
            {
                type: "timeline",
                steps: [
                    { at: "T+0", label: "Donduruldu", tone: "warn", text: "Durum <b>FROZEN_BY_USER</b>. Profil dondurulmuş görünür; tanımlar sitede kalır." },
                    { at: "T+0 → T+{{const:freeze_cooldown_hours}} saat", label: "Kilit süresi", tone: "danger", text: "Giriş kapalıdır. Giriş ekranı kilidin açılacağı saati gösterir." },
                    { at: "T+{{const:freeze_cooldown_hours}} saat sonrası", label: "İlk girişte çözülür", tone: "ok", text: "Kullanıcı giriş yapar yapmaz dondurma <b>otomatik kalkar</b> ve durum ACTIVE olur. Ayarlar sayfasından elle de çözülebilir. Süre sınırı yoktur — hesap istendiği kadar dondurulmuş kalabilir." },
                ],
            },
            { type: "note", tone: "info", text: "Silme talebi varken dondurma yapılamaz; sistem <code>FREEZE_BLOCKED_BY_DELETION</code> ile reddeder." },

            { type: "sub", text: "4) Askıya alma (yönetici)" },
            {
                type: "timeline",
                steps: [
                    { at: "T+0", label: "Askıya alındı", tone: "warn", text: "Durum <b>SUSPENDED</b>, <code>suspension_end_date</code> yazılır. Giriş kapanır, kullanıcıya bildirim gider. Tanımlar sitede kalır." },
                    { at: "T+0 (aynı anda)", label: "Oturum kapatılır", tone: "warn", text: "Kullanıcı o sırada <b>sitede açıksa oturumu anında sonlandırılır</b> — sayfayı yenilemesi veya bir şeye tıklaması beklenmez. Sekmesi arka planda ya da bağlantısı kopuksa attığı ilk istekte düşer. Aynı şey yasaklama ve yönetici silmesi için de, toplu işlemler için de geçerlidir." },
                    { at: "Bitiş tarihine kadar", label: "Askı sürüyor", tone: "danger", text: "Her giriş denemesi reddedilir. Yönetici dilediği an elle kaldırabilir." },
                    { at: "Bitiş tarihinden sonra ilk giriş", label: "Anında çözülür", tone: "ok", text: "Süresi dolan askı, kullanıcı giriş yaptığı anda kalkar — zamanlayıcı beklenmez." },
                    { at: "Askı işinin sıradaki çalışması", label: "Zamanlayıcı taraması", tone: "info", text: "Giriş yapmayan kullanıcıların da durumu düzelsin diye süresi dolmuş askılar askı işi ({{setting:scheduler.suspension_expiry.cron.human}}) tarafından ACTIVE'e çevrilir. Profil rozeti ve yönetici listesi bu aralık kadar geriden gelir." },
                ],
            },
            { type: "note", tone: "warn", text: "<b>Bitiş tarihi girilmemiş (süresiz) askı hiçbir zaman kendiliğinden kalkmaz.</b> Ne giriş anında ne de zamanlayıcıda toplanır; mutlaka elle kaldırılması gerekir." },

            { type: "sub", text: "5) Yasaklama ve yönetici silmesi" },
            {
                type: "timeline",
                steps: [
                    { at: "T+0 — BANNED", label: "Yasaklandı", tone: "danger", text: "Süresizdir, kendiliğinden <b>hiçbir zaman</b> kalkmaz. Yalnızca yönetici kaldırabilir. Kullanıcı adı ve e-posta dolu kalır. Yasaklarken girilen <b>gerekçe kullanıcının satırında saklanır</b> ve kullanıcı giriş denemesinde bu gerekçeyi görür; yasak kaldırıldığında gerekçe de silinir." },
                    { at: "T+0 — DELETED_BY_ADMIN", label: "Yönetici sildi (soft)", tone: "danger", text: "Giriş kapanır, profilde silinme bandı görünür. Yönetici hesabı ACTIVE'e çevirirse, silme sırasında gizlenen tanımlar geri gelir." },
                    { at: "T+0 — Hard delete", label: "Fiziksel silme", tone: "danger", text: "Zamanlayıcı yok, bekleme süresi yok: işlem anında ve geri dönüşsüz uygulanır." },
                ],
            },
        ],
        en: [
            { type: "note", tone: "info", text: "All deadlines are computed in <b>server time (UTC)</b>. Dates shown to a user are converted to their browser's local time, so what they see may differ from the hours listed here." },

            { type: "sub", text: "1) User deletes their own account — <b>keep my entries</b>" },
            {
                type: "timeline",
                caption: "The common case. The entries stay on the site; the account itself is closed.",
                steps: [
                    { at: "T+0", label: "Request filed", tone: "warn", text: "Status becomes <b>DELETED_BY_USER</b> and the session ends. Entries are untouched. <code>scheduled_deletion_at = T + {{const:deletion_grace_days}} days</code>." },
                    { at: "T+0 → T+{{const:deletion_cancel_cooldown_hours}}h", label: "Lock window", tone: "danger", text: "Login is <b>closed entirely</b> and the request <b>cannot</b> be cancelled. The login screen shows both when the lock lifts and when the account will be closed." },
                    { at: "T+{{const:deletion_cancel_cooldown_hours}}h", label: "Lock lifts", tone: "info", text: "The user can log in again, and is met immediately by a warning screen: the deletion date plus a one-click cancel button." },
                    { at: "T+{{const:deletion_cancel_cooldown_hours}}h → T+{{const:deletion_grace_days}}d", label: "Cancellation window", tone: "ok", text: "The request can be cancelled from the <b>Profile</b> or <b>Settings</b> page. On cancellation the status returns to ACTIVE and nothing is lost." },
                    { at: "T+{{const:deletion_grace_days}}d", label: "Grace period over", tone: "danger", text: "Login closes <b>immediately</b>, even if the scheduler has not run yet. From here the account cannot be recovered." },
                    { at: "Next run of the deletion job", label: "Account closed", tone: "danger", text: "The deletion job ({{setting:scheduler.account_deletion.cron.human}}) closes the account for good. <b>Removed:</b> profile details (bio, avatar, banner — files deleted from disk too), messages, conversations, notifications, follows, blocks, drafts, votes cast, preferences, favourite clubs. <b>Kept:</b> username, e-mail, entries. Identity fields are left alone; login is refused by the <code>closed_at</code> stamp." },
                ],
            },
            { type: "note", tone: "warn", text: "A closed account <b>frees neither its username nor its e-mail</b>: the row stays, so both remain taken and cannot be registered again. Login never reopens and the user cannot undo the request." },

            { type: "note", tone: "info", text: "Once the closure is done the status is still <b>DELETED_BY_USER</b> — that is what happened. In the admin panel the badge switches to <b>“closed their account”</b> (a pending request reads <b>“deleting their account”</b>), and the profile shows the closure banner." },

            { type: "sub", text: "2) User deletes their own account — <b>delete my entries too</b>" },
            { type: "note", tone: "info", text: "This ends <b>the same way</b> as the first scenario: the account is closed, the row stays, the panel reads “closed their account”. The only difference is that the entries go too. The one operation that physically removes a user row is the admin's <b>hard delete</b>." },
            {
                type: "timeline",
                caption: "The first steps are identical to the case above; only the ending differs.",
                steps: [
                    { at: "T+0", label: "Request filed", tone: "warn", text: "Status <b>DELETED_BY_USER</b>. If the user also chose <b>hide my entries now</b>, the entries are soft-deleted at the same moment and disappear from the site." },
                    { at: "T+{{const:deletion_cancel_cooldown_hours}}h → T+{{const:deletion_grace_days}}d", label: "Cancellation window", tone: "ok", text: "On cancellation the hidden entries are <b>restored automatically</b>." },
                    { at: "Next run of the deletion job", label: "Account closed", tone: "danger", text: "The entries (and the votes, reports and pins on them) are physically removed first, then the account is closed exactly as in the first scenario. The row stays and shows as <b>“closed their account”</b> in the panel; <b>the username and e-mail are not freed</b>. Irreversible." },
                ],
            },

            { type: "sub", text: "3) Account freeze (by the user)" },
            {
                type: "timeline",
                steps: [
                    { at: "T+0", label: "Frozen", tone: "warn", text: "Status <b>FROZEN_BY_USER</b>. The profile shows as frozen; entries stay on the site." },
                    { at: "T+0 → T+{{const:freeze_cooldown_hours}}h", label: "Lock window", tone: "danger", text: "Login is closed. The login screen shows when the lock lifts." },
                    { at: "After T+{{const:freeze_cooldown_hours}}h", label: "Lifted on first login", tone: "ok", text: "The freeze is <b>lifted automatically</b> the moment the user logs in, and the status returns to ACTIVE. It can also be lifted manually from Settings. There is no time limit — an account may stay frozen indefinitely." },
                ],
            },
            { type: "note", tone: "info", text: "Freezing is refused while a deletion request is pending — the system returns <code>FREEZE_BLOCKED_BY_DELETION</code>." },

            { type: "sub", text: "4) Suspension (by a moderator or admin)" },
            {
                type: "timeline",
                steps: [
                    { at: "T+0", label: "Suspended", tone: "warn", text: "Status <b>SUSPENDED</b> and <code>suspension_end_date</code> is written. Login closes and the user is notified. Entries stay on the site." },
                    { at: "T+0 (at the same moment)", label: "Session ended", tone: "warn", text: "If the user is on the site at that moment, <b>their session is ended immediately</b> — no refresh or click required. A backgrounded tab or an offline client drops out on its next request instead. The same applies to bans, admin deletions and bulk operations." },
                    { at: "Until the end date", label: "Suspension runs", tone: "danger", text: "Every login attempt is rejected. An admin can lift it manually at any point." },
                    { at: "First login after the end date", label: "Lifted instantly", tone: "ok", text: "An expired suspension is lifted the moment the user logs in — no waiting for the scheduler." },
                    { at: "Next run of the suspension job", label: "Scheduler sweep", tone: "info", text: "Expired suspensions are flipped back to ACTIVE by the suspension job ({{setting:scheduler.suspension_expiry.cron.human}}) so users who do not log in are corrected too. Profile badges and the admin table lag by that interval." },
                ],
            },
            { type: "note", tone: "warn", text: "<b>An open-ended suspension (no end date) never lifts by itself.</b> It is picked up neither at login nor by the scheduler and must be lifted manually." },

            { type: "sub", text: "5) Bans and admin deletion" },
            {
                type: "timeline",
                steps: [
                    { at: "T+0 — BANNED", label: "Banned", tone: "danger", text: "Open-ended and <b>never</b> lifted automatically. Only an admin can undo it. Username and e-mail stay taken." },
                    { at: "T+0 — DELETED_BY_ADMIN", label: "Soft-deleted by admin", tone: "danger", text: "Login closes and a deletion banner appears on the profile. Setting the account back to ACTIVE restores the entries hidden by that deletion." },
                    { at: "T+0 — Hard delete", label: "Physical removal", tone: "danger", text: "No scheduler, no grace period: applied immediately and irreversibly." },
                ],
            },
        ],
    },
};

const SECTION_PROMOTION: ManualSection = {
    id: "promotion",
    icon: "graduation",
    title: { tr: "Çaylaktan Yazarlığa: Sıralama Nasıl Hesaplanır?", en: "Rookie to Author: How the Ranking Works" },
    summary: {
        tr: "Sıralama sadece bir öneri listesidir — kararı admin ve moderatörler verir.",
        en: "The ranking is only a shortlist — admins and moderators make the call.",
    },
    blocks: {
        tr: [
            { type: "note", tone: "warn", text: "<b>Bu sıralama sadece bir göstergedir.</b> Hiç kimse otomatik olarak yazar olmaz, hiçbir puan eşiği kendiliğinden terfi ettirmez. Kimin yazar yapılacağına <b>admin ve moderatörler karar verir</b>. Sıralama sadece “kime önce bakayım” sorusunun cevabıdır; listenin başındaki kişiyi reddedip alttaki birini terfi ettirmek tamamen normaldir." },
            { type: "p", text: "Terfi işlemi elle yapılır: <i>Kullanıcılar</i> sekmesinden (ya da sıralama panosundan) kişinin rolü <b>Çaylak → Yazar</b> olarak değiştirilir. Rol değişince kullanıcıya otomatik bildirim gider ve tanımları artık soluk gösterilmez, oy verebilir hale gelir." },
            { type: "sub", text: "Sıralama listesi nasıl oluşur?" },
            {
                type: "ul", items: [
                    "<b>Çaylaklar</b> sekmesi terfi bekleyen çaylakları puana göre sıralar.",
                    "Bir çaylağın sıralamaya girebilmesi için <b>en az 10 tanım</b> yazmış olması gerekir. Eşiğin altındakiler listenin sonunda “—” (sıralama dışı) olarak görünür; profillerinde sıra rozeti çıkmaz ve istatistiklerdeki çaylak listelerinde yer almazlar. Tanım silinip sayı 10’un altına düşerse kullanıcı sıralamadan otomatik çıkar.",
                    "Yeni üyeye kayıt sonrası bu kuralı kibarca anlatan tek seferlik bir karşılama bildirimi gönderilir.",
                    "<b>Yazarlar</b> sekmesi aynı puanlamayı yazar, moderatör ve adminlere uygular — mevcut yazarları karşılaştırmak için.",
                    "Yazar sıralaması <b>sadece admin panelinde</b> görünür, hiçbir profilde gösterilmez. Çaylak ise kendi sırasını profilinde görür.",
                    "Yasaklı ve silinmiş hesaplar listeye hiç alınmaz.",
                    "“Yenile” butonu her seferinde sıfırdan hesaplar.",
                ]
            },
            { type: "sub", text: "Puanı artıran şeyler" },
            {
                type: "table",
                headers: ["Bileşen", "Nasıl hesaplanır", "En fazla"],
                rows: [
                    ["<b>Yazı kalitesi</b><br/>(ortalama tanım uzunluğu)", "Kişinin tüm tanımlarının ortalama karakter sayısı <b>300</b> karaktere ne kadar yakınsa o kadar puan. 300 ve üstü tam puan alır.", "30 puan"],
                    ["<b>Siteye düzenli uğrama</b>", "Üye olduğu günlerin kaçında giriş yaptığı. Her gün giren tam puan alır.", "20 puan"],
                    ["<b>Favori alma</b>", "Aldığı her favori için 3 puan.", "Sınırsız"],
                    ["<b>Artı oy alma</b>", "Aldığı her artı için 1,5 puan.", "Sınırsız"],
                    ["<b>Tanım sayısı</b>", "Her tanım için 0,5 puan — ama 100 tanımdan sonrası puan getirmez.", "50 puan"],
                ],
            },
            { type: "note", tone: "info", text: "<b>Favori neden artının iki katı?</b> Çünkü birinin tanımı favorilemesi, sadece artı vermesinden daha güçlü bir “bu gerçekten iyi” sinyalidir. <b>Tanım sayısı neden 100’de kesiliyor?</b> Kalitesiz ama çok sayıda tanım girerek listeyi domine etmeyi engellemek için." },
            { type: "sub", text: "“Rampa” nedir? (siteye uğrama puanının kademeli açılması)" },
            { type: "p", text: "Siteye uğrama puanı “giriş yapılan gün sayısı ÷ üye olduğu gün sayısı” oranına bakar. Bu oran <b>yeni üyelerde yanıltıcıdır</b>: dün üye olmuş ve bugün girmiş biri 1/1 = %100 çıkar ve puanın tamamını alır. Oysa bu kişi hakkında hiçbir şey bilmiyoruz." },
            { type: "p", text: "Rampa, bu puanın <b>ilk 30 gün boyunca kademeli olarak açılmasıdır</b>. Üyelik 30 güne ulaşana kadar puan orantılı biçimde kısılır, 30 günden sonra tam etkisine ulaşır." },
            {
                type: "table",
                headers: ["Örnek kişi", "Giriş oranı", "Rampa etkisi", "Aldığı puan"],
                rows: [
                    ["2 günlük üye, 2 gün de girmiş", "%100", "2/30 ≈ küçük", "≈ 1,3 / 20"],
                    ["15 günlük üye, 15 gün de girmiş", "%100", "15/30 = yarısı", "10 / 20"],
                    ["60 günlük üye, 30 gün girmiş", "%50", "tam (30 günü geçmiş)", "10 / 20"],
                    ["60 günlük üye, her gün girmiş", "%100", "tam", "20 / 20"],
                ],
            },
            { type: "sub", text: "Puanı düşüren şeyler" },
            {
                type: "table",
                headers: ["Ceza", "Ne kadar düşürür"],
                rows: [
                    ["Aldığı her <b>eksi oy</b>", "−2 puan"],
                    ["Hakkındaki her <b>şikayet</b> (reddedilmemiş olanlar)", "−5 puan"],
                    ["Admin/moderatörün <b>düzenlemek zorunda kaldığı</b> her tanımı", "−4 puan"],
                    ["Admin/moderatörün <b>sildiği</b> her tanımı", "−8 puan"],
                ],
            },
            { type: "note", tone: "info", text: "Cezaların tavanı yoktur — puan eksiye düşebilir. Sıralama eşitlikte <b>daha eski üyeyi</b> öne alır." },
            { type: "sub", text: "Peki “en az 10 tanım, 30 gün” ne demek?" },
            { type: "p", text: "Bunlar puana dahil <b>değildir</b> ve sistem bunları kimseye dayatmaz. Kodda tanımlı <b>tavsiye niteliğinde alt sınırlardır</b>: “bir çaylağı yazar yapmadan önce en az {{const:rookie_min_entries}} tanım yazmış ve en az {{const:rookie_min_days}} gündür üye olmuş olmasına bakın” anlamına gelir." },
            {
                type: "ul", items: [
                    "Bu eşiklerin altındaki biri de terfi <b>edilebilir</b> — sistem engellemez.",
                    "Bu eşikleri geçen biri otomatik terfi <b>etmez</b> — kimse otomatik terfi etmez.",
                    "Kısacası: puan sıralamayı, bu iki sayı ise “çok mu erken?” sorusunu cevaplar. Karar sizindir.",
                ]
            },
        ],
        en: [
            { type: "note", tone: "warn", text: "<b>This ranking is only an indicator.</b> Nobody is promoted automatically and no score threshold promotes anyone. <b>Admins and moderators decide</b> who becomes an author. The ranking only answers “who should I look at first”; rejecting the person at the top and promoting someone further down is perfectly normal." },
            { type: "p", text: "Promotion is manual: change the person’s role from <b>Rookie → Author</b> in the <i>Users</i> tab (or from the ranking board). The user is notified automatically, their entries stop being dimmed and they can vote." },
            { type: "sub", text: "How the list is built" },
            {
                type: "ul", items: [
                    "The <b>Rookies</b> tab ranks rookies awaiting promotion by score.",
                    "A rookie must have written <b>at least 10 entries</b> to enter the ranking. Those below the threshold appear at the end of the list as “—” (unranked); no rank badge is shown on their profile and they are excluded from the rookie lists in statistics. If entries are deleted and the count drops below 10, the user automatically leaves the ranking again.",
                    "New members receive a one-time welcome notification right after registration that politely explains this rule.",
                    "The <b>Authors</b> tab applies the same scoring to authors, moderators and admins — useful for comparing existing writers.",
                    "Author ranks are <b>admin-panel only</b> and never shown on profiles. A rookie does see their own rank on their profile.",
                    "Banned and deleted accounts never appear on the board.",
                    "The “Refresh” button recomputes everything from scratch.",
                ]
            },
            { type: "sub", text: "What raises the score" },
            {
                type: "table",
                headers: ["Component", "How it is calculated", "Maximum"],
                rows: [
                    ["<b>Writing quality</b><br/>(average entry length)", "The closer the average character count across all their entries is to <b>300</b>, the more points. 300 or above earns full marks.", "30 pts"],
                    ["<b>Showing up regularly</b>", "On how many of their membership days they logged in. Logging in every day earns full marks.", "20 pts"],
                    ["<b>Favorites received</b>", "3 points per favorite.", "Unbounded"],
                    ["<b>Upvotes received</b>", "1.5 points per upvote.", "Unbounded"],
                    ["<b>Entry count</b>", "0.5 points per entry — but nothing beyond 100 entries.", "50 pts"],
                ],
            },
            { type: "note", tone: "info", text: "<b>Why are favorites worth double an upvote?</b> Because favoriting an entry is a stronger “this is genuinely good” signal than a plain upvote. <b>Why cap entry count at 100?</b> To stop someone dominating the list by posting large numbers of low-effort entries." },
            { type: "sub", text: "What is the “ramp”? (the attendance score phasing in)" },
            { type: "p", text: "The attendance score looks at “login days ÷ membership days”. That ratio is <b>misleading for new members</b>: someone who joined yesterday and logged in today scores 1/1 = 100% and takes the full points — even though we know nothing about them yet." },
            { type: "p", text: "The ramp makes this score <b>phase in gradually over the first 30 days</b>. Until membership reaches 30 days the score is scaled down proportionally; after 30 days it reaches full effect." },
            {
                type: "table",
                headers: ["Example member", "Login ratio", "Ramp effect", "Points earned"],
                rows: [
                    ["2 days old, logged in both days", "100%", "2/30 ≈ small", "≈ 1.3 / 20"],
                    ["15 days old, logged in every day", "100%", "15/30 = half", "10 / 20"],
                    ["60 days old, logged in 30 days", "50%", "full (past 30 days)", "10 / 20"],
                    ["60 days old, logged in every day", "100%", "full", "20 / 20"],
                ],
            },
            { type: "sub", text: "What lowers the score" },
            {
                type: "table",
                headers: ["Penalty", "Cost"],
                rows: [
                    ["Each <b>downvote</b> received", "−2 pts"],
                    ["Each <b>report</b> against them (not dismissed)", "−5 pts"],
                    ["Each entry an admin/moderator <b>had to edit</b>", "−4 pts"],
                    ["Each entry an admin/moderator <b>deleted</b>", "−8 pts"],
                ],
            },
            { type: "note", tone: "info", text: "Penalties are uncapped — the score can go negative. Ties are broken in favour of the <b>older member</b>." },
            { type: "sub", text: "So what does “at least 10 entries, 30 days” mean?" },
            { type: "p", text: "Those are <b>not</b> part of the score and the system enforces them on nobody. They are <b>advisory floors defined in code</b>, meaning: “before promoting a rookie, check that they have written at least {{const:rookie_min_entries}} entries and have been a member for at least {{const:rookie_min_days}} days.”" },
            {
                type: "ul", items: [
                    "Someone below these floors <b>can</b> still be promoted — nothing blocks it.",
                    "Someone above them is <b>not</b> promoted automatically — nobody is.",
                    "In short: the score answers the ordering, these two numbers answer “is it too early?”. The decision is yours.",
                ]
            },
        ],
    },
};

const SECTION_VOTES: ManualSection = {
    id: "votes",
    icon: "thumbsUp",
    title: { tr: "Oylama ve Favoriler", en: "Voting and Favorites" },
    summary: {
        tr: "Artı / eksi / favori kuralları ve oy hız limiti.",
        en: "Up / down / fav rules and vote rate limiting.",
    },
    blocks: {
        tr: [
            {
                type: "ul", items: [
                    "Üç oy tipi vardır: <b>UP</b> (artı), <b>DOWN</b> (eksi) ve <b>FAV</b> (favori).",
                    "Artı ve eksi birbirini dışlar — biri seçiliyken diğerine basmak oyu değiştirir; aynısına tekrar basmak oyu kaldırır.",
                    "Favori bağımsızdır: bir tanım hem artılanıp hem favorilenebilir.",
                    "<b>ROOKIE kullanıcılar oy veremez.</b>",
                    "Kimse <b>kendi tanımına</b> oy veremez.",
                    "Oy hız limiti: {{const:vote_window}} saniyede en fazla {{const:vote_max}} oy; aşılırsa {{const:vote_cooldown}} saniye bekleme uygulanır.",
                    "Artı oy ve favori, tanım sahibine bildirim üretir (kullanıcı tercihlerinden kapatılabilir). <b>Eksi oy bildirim üretmez.</b>",
                    "Bir tanıma kimlerin artı/eksi/favori verdiği listelenebilir.",
                ]
            },
        ],
        en: [
            {
                type: "ul", items: [
                    "Three vote types exist: <b>UP</b>, <b>DOWN</b> and <b>FAV</b>.",
                    "Up and down are mutually exclusive — pressing the other switches the vote; pressing the same one again removes it.",
                    "Fav is independent: an entry can be both upvoted and favorited.",
                    "<b>ROOKIE users cannot vote.</b>",
                    "Nobody can vote on <b>their own entry</b>.",
                    "Vote rate limit: at most {{const:vote_max}} votes per {{const:vote_window}} seconds; exceeding it triggers a {{const:vote_cooldown}}-second cooldown.",
                    "Upvotes and favorites notify the entry author (can be disabled in user preferences). <b>Downvotes never notify.</b>",
                    "The list of users who up/down/fav’d an entry can be viewed.",
                ]
            },
        ],
    },
};

// ─────────────────────────────────────────────────────────────────────────────
// GROUP 3 — USER EXPERIENCE
// ─────────────────────────────────────────────────────────────────────────────

const SECTION_NOTIFICATIONS: ManualSection = {
    id: "notifications",
    icon: "bell",
    title: { tr: "Bildirimler: Ne Zaman Gelir?", en: "Notifications: When Are They Sent?" },
    summary: {
        tr: "9 bildirim tipi, tetikleyicileri ve kullanıcının kapatabildikleri.",
        en: "The 9 notification types, their triggers and what users can mute.",
    },
    blocks: {
        tr: [
            {
                type: "table",
                headers: ["Bildirim", "Ne zaman gelir?", "Kullanıcı kapatabilir mi?"],
                rows: [
                    ["<b>Tanıma artı verildi</b>", "Biri kullanıcının tanımına artı verdiğinde", "Evet"],
                    ["<b>Tanım favorilendi</b>", "Biri kullanıcının tanımını favorilediğinde", "Evet"],
                    ["<b>Tanım silindi</b>", "Bir admin/moderatör kullanıcının tanımını sildiğinde", "Evet"],
                    ["<b>Hesap durumu değişti</b>", "Askıya alma, yasaklama, durum değiştirme gibi işlemlerde", "Evet"],
                    ["<b>Rol değişti</b>", "Rolü değiştiğinde (ör. çaylaktan yazara terfi)", "Hesap durumu bildirimleriyle birlikte"],
                    ["<b>Yeni takipçi</b>", "Biri kullanıcıyı takip etmeye başladığında", "Evet"],
                    ["<b>Başlık önerisi onaylandı</b>", "Önerdiği başlık açıldığında", "—"],
                    ["<b>Başlık önerisi reddedildi</b>", "Önerisi reddedildiğinde — admin notu da bildirimde görünür", "—"],
                    ["<b>Tanım taşındı</b>", "Tanımı başka bir başlığa taşındığında", "—"],
                ],
            },
            { type: "note", tone: "info", text: "<b>Eksi oy hiçbir zaman bildirim üretmez</b> — kimse eksi yediğini anlık olarak görmez. Toplu taşıma yaptığınızda, 20 tanımı taşınsa bile kullanıcıya <b>tek bir</b> bildirim gider." },
            { type: "sub", text: "Bilinmesi gerekenler" },
            {
                type: "ul", items: [
                    "Bildirimler <b>anlık</b> düşer; kullanıcı sayfayı yenilemek zorunda değildir.",
                    "Bildirim gönderimi başarısız olursa <b>asıl işlem yine de tamamlanır</b>. Yani bildirim gitmediği için askı, silme veya rol değişikliği geri alınmaz.",
                    "Zil ikonundaki sayı okunmamış bildirim sayısıdır; okunanlar zamanla temizlenir.",
                    "Kullanıcı hangi bildirimleri alacağını <b>Ayarlar → Bildirimler</b> altından seçer.",
                    "Ayrıca varsayılan olarak <b>kapalı</b> gelen iki bildirim daha vardır: takip ettiği kişi yeni tanım girdiğinde ve takip ettiği maça yeni tanım girildiğinde. Bir de <b>e-posta bildirimi</b> anahtarı vardır (o da varsayılan kapalı).",
                ]
            },
        ],
        en: [
            {
                type: "table",
                headers: ["Notification", "When it arrives", "Can the user mute it?"],
                rows: [
                    ["<b>Entry upvoted</b>", "Someone upvotes the user’s entry", "Yes"],
                    ["<b>Entry favorited</b>", "Someone favorites the user’s entry", "Yes"],
                    ["<b>Entry deleted</b>", "An admin/moderator deletes the user’s entry", "Yes"],
                    ["<b>Account status changed</b>", "Suspension, ban, status change and similar actions", "Yes"],
                    ["<b>Role changed</b>", "Their role changes (e.g. promoted from rookie to author)", "Together with status notifications"],
                    ["<b>New follower</b>", "Someone starts following them", "Yes"],
                    ["<b>Topic suggestion approved</b>", "Their suggested topic goes live", "—"],
                    ["<b>Topic suggestion rejected</b>", "Their suggestion is rejected — the admin note is included", "—"],
                    ["<b>Entry moved</b>", "Their entry is moved to another topic", "—"],
                ],
            },
            { type: "note", tone: "info", text: "<b>Downvotes never notify anyone</b> — nobody gets a live ping about being downvoted. On a bulk move, a user gets <b>one</b> notification even if 20 of their entries were moved." },
            { type: "sub", text: "Worth knowing" },
            {
                type: "ul", items: [
                    "Notifications arrive <b>live</b>; the user does not need to refresh.",
                    "If sending a notification fails, <b>the underlying action still completes</b>. A suspension, deletion or role change is never rolled back because a notification did not go out.",
                    "The number on the bell icon is the unread count; read ones are cleaned up over time.",
                    "Users choose which notifications they receive under <b>Settings → Notifications</b>.",
                    "There are two more that are <b>off by default</b>: a new entry from someone they follow, and a new entry on a match they follow. There is also an <b>email notification</b> switch (also off by default).",
                ]
            },
        ],
    },
};

const SECTION_PROFILE: ManualSection = {
    id: "profile",
    icon: "idCard",
    title: { tr: "Profil Sayfasında Neler Var?", en: "What’s on the Profile Page?" },
    summary: {
        tr: "Genel/Albüm/Bana Özel sekmeleri, albüm görsel işlemleri, profil alanları, biyografi moderasyonu.",
        en: "Public/album/private tabs, album image actions, profile fields, bio moderation.",
    },
    blocks: {
        tr: [
            { type: "sub", text: "Sekme yapısı" },
            {
                type: "table",
                headers: ["Ana sekme", "Alt sekmeler", "Kim görür?"],
                rows: [
                    ["<b>Genel</b>", "Son tanımlar · En çok beğenilenler · En çok eksi alanlar · En çok favorilenenler · Onaylanan başlıklar", "Herkes"],
                    ["<b>Albüm</b>", "Kullanıcının tanımlarına eklediği tüm görseller", "Herkes"],
                    ["<b>Bana Özel</b>", "Favorilerim · Artıladıklarım · Eksilediklerim · Takip ettiklerim · Engellediklerim · Reddedilen başlık önerilerim", "Yalnızca profil sahibi"],
                ],
            },
            { type: "sub", text: "Albüm" },
            {
                type: "ul", items: [
                    "Albüm, kullanıcının silinmemiş tanımlarındaki görselleri önizleme olarak listeler; her görselin altında hangi başlığa girildiği yazar.",
                    "Önizlemeye tıklayınca görsel büyük görünümde açılır. Altında üç buton vardır: <b>entry'ye git</b> (tanımın kalıcı linki), <b>görseli indir</b> ve <b>yayından kaldır</b>.",
                    "<b>Yayından kaldır</b> yalnızca albüm sahibine görünür. Onaydan sonra görsel o tanımdan çıkarılır, tanım “düzenlendi” olarak işaretlenir. Düzenleme penceresi, günlük düzenleme limiti ve minimum uzunluk bu işlemde <b>uygulanmaz</b>.",
                    "Tanımda görselden başka içerik yoksa görsel kaldırılamaz; kullanıcının tanımı silmesi gerekir.",
                    "Dosya sunucudan silinmez, çünkü aynı görsel başka tanımlarda, taslaklarda veya çöp kutusunda kullanılıyor olabilir.",
                ]
            },
            { type: "sub", text: "Profil alanları" },
            {
                type: "ul", items: [
                    "Biyografi, doğum tarihi, cinsiyet, şehir, ülke (varsayılan TR)",
                    "Web sitesi, X (twitter) ve Instagram kullanıcı adları",
                    "Avatar ve kapak görseli",
                    "Takipçi / takip edilen sayıları ve modalleri",
                    "Kullanıcı istatistikleri (tanım sayısı, alınan oylar, giriş serisi vb.)",
                    "Çaylaklar için <b>Çaylak Onay Sırası</b> — sadece kendi profilinde görünür",
                ]
            },
            { type: "settings", keys: ["user.bio.max_length", "user.username.max_length"] },
            { type: "sub", text: "Biyografi moderasyonu" },
            {
                type: "ul", items: [
                    "Admin biyografiyi <b>düzenleyebilir</b>, <b>silebilir</b>, <b>bloklayabilir</b> ve <b>bloğu kaldırabilir</b>.",
                    "Blok işleminde blok zamanı, bloklayan admin ve sebep kaydedilir.",
                    "Dört işlem de denetim kaydına yazılır: EDIT_BIO, DELETE_BIO, BLOCK_BIO, UNBLOCK_BIO.",
                ]
            },
            { type: "note", tone: "warn", text: "Kullanıcı adı deseni: yalnızca küçük harf (a-z ve çğıöşü), rakam, alt çizgi ve kelimeler arasında <b>tek</b> boşluk. Baştan/sondan boşluk ve arka arkaya boşluk kabul edilmez. Minimum {{const:username_min}} karakter, maksimum {{setting:user.username.max_length}} karakter (mutlak tavan {{const:username_max}} — anlık görüntü kolonları VARCHAR(50) olduğu için aşılamaz)." },
        ],
        en: [
            { type: "sub", text: "Tab structure" },
            {
                type: "table",
                headers: ["Main tab", "Sub-tabs", "Visible to"],
                rows: [
                    ["<b>Public</b>", "Recent · Most liked · Most disliked · Most favorited · Approved topics", "Everyone"],
                    ["<b>Album</b>", "Every image the user attached to their entries", "Everyone"],
                    ["<b>Private</b>", "Favorites · Liked · Disliked · Following · Blocked · Rejected topic suggestions", "Profile owner only"],
                ],
            },
            { type: "sub", text: "Album" },
            {
                type: "ul", items: [
                    "The album lists the images of the user’s non-deleted entries as thumbnails, each labelled with the topic it was posted under.",
                    "Clicking a thumbnail opens the large view with three buttons below it: <b>go to entry</b> (the entry permalink), <b>download image</b> and <b>unpublish</b>.",
                    "<b>Unpublish</b> is visible to the album owner only. After confirmation the image is removed from that entry and the entry is flagged as edited. The edit window, daily edit limit and minimum length are <b>not enforced</b> for this action.",
                    "If the entry contains nothing but the image, it cannot be removed; the user has to delete the entry instead.",
                    "The file itself is not deleted from the server, because the same image may be used in other entries, drafts or the trash.",
                ]
            },
            { type: "sub", text: "Profile fields" },
            {
                type: "ul", items: [
                    "Bio, birthday, gender, city, country (defaults to TR)",
                    "Website, X (Twitter) and Instagram handles",
                    "Avatar and cover image",
                    "Follower / following counts and modals",
                    "User stats (entry count, votes received, login streak, etc.)",
                    "For rookies, the <b>rookie approval rank</b> — visible only on their own profile",
                ]
            },
            { type: "settings", keys: ["user.bio.max_length", "user.username.max_length"] },
            { type: "sub", text: "Bio moderation" },
            {
                type: "ul", items: [
                    "Admins can <b>edit</b>, <b>delete</b>, <b>block</b> and <b>unblock</b> a bio.",
                    "Blocking records the timestamp, the blocking admin and the reason.",
                    "All four write audit entries: EDIT_BIO, DELETE_BIO, BLOCK_BIO, UNBLOCK_BIO.",
                ]
            },
            { type: "note", tone: "warn", text: "Username pattern: lowercase letters only (a-z plus çğıöşü), digits, underscores and <b>single</b> spaces between words. No leading/trailing or consecutive spaces. Minimum {{const:username_min}} characters, maximum {{setting:user.username.max_length}} (hard ceiling {{const:username_max}} — snapshot columns are VARCHAR(50))." },
        ],
    },
};

const SECTION_PREFERENCES: ManualSection = {
    id: "preferences",
    icon: "sliders",
    title: { tr: "Kullanıcı Tercihleri", en: "User Preferences" },
    summary: {
        tr: "Tema, sayfa boyutu, çaylak filtresi, mesaj gizliliği, bildirim anahtarları.",
        en: "Theme, page size, rookie filter, message privacy, notification switches.",
    },
    blocks: {
        tr: [
            {
                type: "table",
                headers: ["Tercih", "Varsayılan", "Not"],
                rows: [
                    ["Tema", "PAPER", "10 tema: light, dark, paper, tango, flamingo, comolokko, lokum, mercan, gökyüzü, taraftar (dinamik — favori kulüp renkleri)"],
                    ["Sayfa başına tanım", "{{const:entries_per_page}}", "İzin verilen aralık {{const:min_entries_per_page}}–{{const:max_entries_per_page}}"],
                    ["Sayfa başına maç", "{{const:matches_per_page}}", "İzin verilen aralık {{const:min_matches_per_page}}–{{const:max_matches_per_page}}"],
                    ["Tanım numaralarını göster", "Açık", "—"],
                    ["Çaylak tanımlarını göster", "Açık", "Kapalıysa çaylak tanımları listelerden <b>ve rozet sayaçlarından</b> düşer"],
                    ["Çaylaklardan mesaj kabul et", "Açık", "—"],
                    ["Mesaj gizliliği", "EVERYONE", "NOBODY · FOLLOWERS · AUTHORS · EVERYONE"],
                    ["Artı oy bildirimi", "Açık", "—"],
                    ["Favori bildirimi", "Açık", "—"],
                    ["Tanım silindi bildirimi", "Açık", "—"],
                    ["Hesap durumu bildirimi", "Açık", "—"],
                    ["Yeni takipçi bildirimi", "Açık", "—"],
                    ["Takip edilen kullanıcının tanımı", "Kapalı", "—"],
                    ["Takip edilen maçın tanımı", "Kapalı", "—"],
                    ["E-posta bildirimleri", "Kapalı", "—"],
                    ["Çevrimiçi durumu", "ONLINE", "Aşağıdaki Online bölümüne bakın"],
                ],
            },
            { type: "note", tone: "info", text: "Çaylak filtresi rozet sayaçlarını da etkiler: sayaç her zaman sayfada gerçekten görünen tanım sayısıyla uyumludur." },
        ],
        en: [
            {
                type: "table",
                headers: ["Preference", "Default", "Note"],
                rows: [
                    ["Theme", "PAPER", "10 themes: light, dark, paper, tango, flamingo, comolokko, lokum, mercan, gökyüzü, fan (dynamic — favourite club colours)"],
                    ["Entries per page", "{{const:entries_per_page}}", "Allowed range {{const:min_entries_per_page}}–{{const:max_entries_per_page}}"],
                    ["Matches per page", "{{const:matches_per_page}}", "Allowed range {{const:min_matches_per_page}}–{{const:max_matches_per_page}}"],
                    ["Show entry ids", "On", "—"],
                    ["Show rookie entries", "On", "When off, rookie entries drop out of lists <b>and badge counts</b>"],
                    ["Accept messages from rookies", "On", "—"],
                    ["Message privacy", "EVERYONE", "NOBODY · FOLLOWERS · AUTHORS · EVERYONE"],
                    ["Upvote notification", "On", "—"],
                    ["Favorite notification", "On", "—"],
                    ["Entry deleted notification", "On", "—"],
                    ["Account status notification", "On", "—"],
                    ["New follower notification", "On", "—"],
                    ["Followed user’s new entry", "Off", "—"],
                    ["Followed match’s new entry", "Off", "—"],
                    ["Email notifications", "Off", "—"],
                    ["Presence status", "ONLINE", "See the Online section below"],
                ],
            },
            { type: "note", tone: "info", text: "The rookie filter also affects badge counts, so a badge always matches what the page actually renders." },
        ],
    },
};

const SECTION_SOCIAL: ManualSection = {
    id: "social",
    icon: "comments",
    title: { tr: "Mesajlaşma, Takip ve Engelleme", en: "Messaging, Following and Blocking" },
    summary: {
        tr: "Mesaj limitleri, gizlilik seçenekleri, engelleme etkisi.",
        en: "Message limits, privacy options, effects of blocking.",
    },
    blocks: {
        tr: [
            { type: "sub", text: "Mesajlaşma" },
            { type: "settings", keys: ["messaging.max_length", "messaging.inbox_limit", "messaging.rate_limit_max_messages", "messaging.rate_limit_window_seconds"] },
            {
                type: "ul", items: [
                    "Mesaj hız limiti: {{setting:messaging.rate_limit_window_seconds}} saniyede en fazla {{setting:messaging.rate_limit_max_messages}} mesaj. Bu iki değer <b>Ayarlar → Mesajlaşma</b> altından değiştirilebilir; mesaj sayısı 0 yapılırsa hız limiti tamamen kapanır.",
                    "Konuşma görünümünde sayfa başına {{const:messages_per_page}} mesaj, konuşma listesinde {{const:conversations_per_page}} konuşma yüklenir.",
                    "Konuşma listesindeki önizleme {{const:msg_preview}} karakterde kısaltılır.",
                    "Mesaj yazma alanında, Türkçe karakter klavyesinin yanında bir <b>emoji butonu</b> vardır. Pencerede Unicode'un standart grupları (yüzler, insanlar, hayvanlar, yiyecek, seyahat, etkinlikler, nesneler, semboller, bayraklar) sekmeler hâlinde listelenir; seçilen emoji imlecin bulunduğu yere eklenir. Pencere açık kaldığı için arka arkaya birden fazla emoji eklenebilir; pencerenin dışına tıklayınca kapanır. Windows 10 bunları gösteremediği için Emoji 12.1'den yeni emojiler ve ülke bayrakları listede yoktur, ama cihazın kendi emoji klavyesinden yazılabilir.",
                    "Yazarken <b>kısayollar</b> otomatik olarak emojiye dönüşür: <code>:)</code> 🙂, <code>:d</code> / <code>:D</code> 😄, <code>xd</code> 😆, <code>;)</code> 😉, <code>:p</code> 😛, <code>:(</code> 🙁, <code>:'(</code> 😢, <code>:o</code> 😮, <code>:*</code> 😘, <code>&lt;3</code> ❤️ ve diğerleri. Dönüşüm, kısayoldan sonra boşluk/yeni satır yazılınca veya mesaj gönderilince olur; kelimenin tamamı kısayollardan (ve varsa emojilerden) oluşmalıdır — <code>:D:D:D</code> üç emojiye dönüşür, ama bağlantılar ve biçimlendirme etkilenmez. Yapıştırılan metin dönüştürülmez. Emoji penceresinde kısayolu olan bir emojinin üzerine gelince kısayolları pencerenin altında görünür.",
                    "Emojiler karakter sayacında ve <b>messaging.max_length</b> kontrolünde genellikle <b>2 karakter</b> (bazıları, örneğin ❤️, daha fazla) sayılır; sayaç ile sunucu aynı şekilde saydığı için gönderimde sürpriz olmaz.",
                    "Gizlilik seçeneği <b>NOBODY / FOLLOWERS / AUTHORS / EVERYONE</b> olarak ayarlanır; ayrıca “çaylaklardan mesaj kabul et” anahtarı vardır.",
                    "<b>FOLLOWERS</b> seçeneğinde yön önemlidir: mesaj atabilecek olanlar <b>alıcının takip ettiği</b> kişilerdir, alıcıyı takip edenler değil. Ayarlar ekranındaki adı da bunu söyler: “Sadece Takip Ettiklerim”. Alıcı göndereni takip etmiyorsa mesaj ikonu çıkmaz ve gönderim reddedilir.",
                    "Bir kullanıcının profilinde, takip ve engelle butonlarının arasında bir <b>mesaj ikonu</b> vardır; tıklanınca mesajlar sayfasında o kişiyle konuşma açılır.",
                    "Bu ikon yalnızca mesaj gerçekten gönderilebiliyorsa çıkar: <b>iki tarafın da hesabı ACTIVE olmalı</b> (askıda, yasaklı, dondurulmuş, pasif veya silinmiş hesapta çıkmaz), <b>iki yönden de engel olmamalı</b> ve alıcının gizlilik tercihi göndereni kabul etmeli. Kapalı kutuya boşuna tıklatan bir buton gösterilmez.",
                    "<b>MODERATOR ve ADMIN</b> gizlilik tercihini aşar; alıcı <b>NOBODY</b> seçmiş olsa bile ikon onlara görünür ve mesaj gider. <b>Engelleme</b> bunun istisnasıdır — engel varsa ikon yönetici dâhil kimseye çıkmaz.",
                    "Hız limiti ve dolu gelen kutusu ikonu <b>gizlemez</b>: bunlar geçici durumlardır, gönderim sırasında hata olarak bildirilir.",
                    "Mesajlar da şikayet edilebilir; şikayetler admin panelindeki <i>Şikayetler</i> sekmesinde ayrı bir listede toplanır.",
                ]
            },
            { type: "sub", text: "Takip" },
            {
                type: "ul", items: [
                    "Takip ilişkisi tekildir; aynı kişi iki kez takip edilemez.",
                    "Takip edildiğinde karşı tarafa <b>NEW_FOLLOWER</b> bildirimi gider.",
                    "Kullanıcı takip ettiklerini profilindeki özel sekmede görür ve oradan takibi bırakabilir.",
                    "Akış (feed) sayfası takip edilen kullanıcıların tanımlarını gösterir.",
                ]
            },
            { type: "sub", text: "Engelleme" },
            {
                type: "ul", items: [
                    "Engellenen kullanıcının tanımları, engelleyen kişinin gördüğü <b>tüm listelerden</b> çıkarılır.",
                    "Engelleme tek yönlü ve kişiseldir — genel görünürlüğü etkilemez.",
                    "Kullanıcı engellediklerini profilindeki özel sekmede görür ve engeli kaldırabilir.",
                ]
            },
        ],
        en: [
            { type: "sub", text: "Messaging" },
            { type: "settings", keys: ["messaging.max_length", "messaging.inbox_limit", "messaging.rate_limit_max_messages", "messaging.rate_limit_window_seconds"] },
            {
                type: "ul", items: [
                    "Message rate limit: at most {{setting:messaging.rate_limit_max_messages}} messages per {{setting:messaging.rate_limit_window_seconds}} seconds. Both values are editable under <b>Settings → Messaging</b>; setting the message count to 0 turns the rate limit off entirely.",
                    "The conversation view loads {{const:messages_per_page}} messages per page; the conversation list loads {{const:conversations_per_page}}.",
                    "The list preview is truncated at {{const:msg_preview}} characters.",
                    "The message composer has an <b>emoji button</b> next to the Turkish character keyboard. The popup lists Unicode's standard groups (smileys, people, animals, food, travel, activities, objects, symbols, flags) as tabs; the picked emoji is inserted at the caret. The popup stays open so several can be added in a row, and closes on a click outside it. Emoji newer than Emoji 12.1 and country flags are left out because Windows 10 cannot display them, but they can still be typed from the device's own emoji keyboard.",
                    "<b>Shortcuts</b> turn into emoji while typing: <code>:)</code> 🙂, <code>:d</code> / <code>:D</code> 😄, <code>xd</code> 😆, <code>;)</code> 😉, <code>:p</code> 😛, <code>:(</code> 🙁, <code>:'(</code> 😢, <code>:o</code> 😮, <code>:*</code> 😘, <code>&lt;3</code> ❤️ and more. The conversion happens when a space/new line is typed after the shortcut or when the message is sent; the whole word must consist of shortcuts (and any emoji already there) — <code>:D:D:D</code> becomes three emoji, while links and formatting are untouched. Pasted text is not converted. Hovering an emoji that has shortcuts in the popup shows them at the bottom of the popup.",
                    "An emoji usually counts as <b>2 characters</b> (some, e.g. ❤️, count more) in the character counter and against <b>messaging.max_length</b>; the counter and the server count the same way, so there is no surprise on send.",
                    "Privacy can be set to <b>NOBODY / FOLLOWERS / AUTHORS / EVERYONE</b>, plus an “accept messages from rookies” switch.",
                    "With <b>FOLLOWERS</b> the direction matters: the senders admitted are the people <b>the recipient follows</b>, not the recipient's own followers — which is what the settings label (“People I Follow”) promises. If the recipient does not follow the sender, the message icon is hidden and the send is refused.",
                    "A profile page carries a <b>message icon</b> between the follow and block buttons; clicking it opens the conversation with that user on the messages page.",
                    "The icon appears only when a message would actually go through: <b>both accounts must be ACTIVE</b> (never shown on a suspended, banned, frozen, inactive or deleted account), <b>no block in either direction</b>, and the recipient's privacy preference must admit the sender. No button is offered for a closed inbox.",
                    "<b>MODERATOR and ADMIN</b> bypass the privacy preference: the icon is shown to them even when the recipient chose <b>NOBODY</b>, and the message is delivered. <b>Blocking</b> is the exception — with a block in place the icon is hidden from staff too.",
                    "Rate limit and a full inbox do <b>not</b> hide the icon: those are transient and are reported as errors when sending.",
                    "Messages can also be reported; message reports get their own list in the admin <i>Reports</i> tab.",
                ]
            },
            { type: "sub", text: "Following" },
            {
                type: "ul", items: [
                    "The follow relation is unique; you cannot follow the same person twice.",
                    "Following sends a <b>NEW_FOLLOWER</b> notification.",
                    "Users see who they follow on a private profile tab and can unfollow from there.",
                    "The feed page shows entries from followed users.",
                ]
            },
            { type: "sub", text: "Blocking" },
            {
                type: "ul", items: [
                    "A blocked user’s entries are removed from <b>every list</b> the blocker sees.",
                    "Blocking is one-way and personal — it does not affect global visibility.",
                    "Users see their blocked list on a private profile tab and can unblock from there.",
                ]
            },
        ],
    },
};

const SECTION_ONLINE: ManualSection = {
    id: "online",
    icon: "circle",
    title: { tr: "Online Sayfası ve Durum Rozetleri", en: "Online Page and Presence Badges" },
    summary: {
        tr: "Kimler online sayılır, 7 durum, görünmez modun admin görünürlüğü.",
        en: "Who counts as online, the 7 statuses, invisible mode for admins.",
    },
    blocks: {
        tr: [
            {
                type: "ul", items: [
                    "Online listesi <b>WebSocket bağlantısı</b> üzerinden çalışır: kullanıcı STOMP ile bağlandığında listeye girer, bağlantı koptuğunda çıkar. “Son görülme”ye göre tahmin yapılmaz.",
                    "Liste her zaman güce göre sıralanır: <b>yönetici → moderatör → yazar → çaylak</b>, aynı rol içinde alfabetik. Rol filtresi kaldırıldı; her satırın başında tek harfli rol rozeti var (<b>A</b> yönetici, <b>M</b> moderatör, <b>Y</b> yazar, <b>Ç</b> çaylak), üzerine gelince rolün tam adı görünür.",
                    "Satır düzeni: rol rozeti, profil avatarı, kullanıcı adı, en sağda durum rengi. Durumun adı renkli noktanın üzerine gelince görünür. Mesaj butonu satırın üzerine gelince çıkar (dokunmatik ekranlarda her zaman görünür).",
                    "Kendi durumunuz üstteki <b>Durumunuz</b> seçicisinden değiştirilir; açılan listede her durumun adı yazar.",
                    "Listeden doğrudan profile gidilebilir ve mesaj başlatılabilir.",
                    "Kullanıcı durumunu değiştirdiğinde rozet, o sırada siteyi açık tutan herkeste <b>anında</b> güncellenir.",
                    "Kullanıcının seçtiği durum tercihlerde saklanır, oturumlar arasında korunur.",
                ]
            },
            {
                type: "table",
                headers: ["Durum", "Gösterim", "Görünürlük"],
                rows: [
                    ["<b>ONLINE</b>", "Yeşil nokta", "Herkes"],
                    ["<b>BUSY</b>", "Sarı nokta", "Herkes"],
                    ["<b>DND</b> (rahatsız etmeyin)", "Kırmızı nokta", "Herkes"],
                    ["<b>AWAY</b>", "Mor nokta", "Herkes"],
                    ["<b>WATCHING</b> (maç izliyor)", "Mavi nokta", "Herkes"],
                    ["<b>BATHROOM</b>", "Gri nokta", "Herkes"],
                    ["<b>INVISIBLE</b>", "Kesikli çerçeveli nokta", "Normal kullanıcılardan <b>gizli</b>; MOD/ADMIN’e özel işaretle görünür"],
                ],
            },
            { type: "note", tone: "info", text: "Panodaki “çevrimiçi kullanıcı” KPI’sı da bu WebSocket sayacından beslenir." },
            {
                type: "ul", items: [
                    "Biri online olduğunda veya çıktığında sunucu bir sinyal yayınlar; online modalı ve panodaki “Şu an Online” kartı <b>anında</b> yenilenir. Kart, modal ile aynı listeyi saydığı için iki sayı her zaman aynıdır.",
                    "Kopmuş bağlantılar (uyku modu, ağ değişimi) heartbeat ile en geç yaklaşık 1 dakikada tespit edilir: sunucu oturumu düşürür, tarayıcı kendiliğinden yeniden bağlanır.",
                    "Modal altındaki <b>Bugün · 7 gün · 30 gün · Bu yıl</b> sayıları giriş tarihine ek olarak son aktiviteye (her WebSocket bağlantısında güncellenir) bakar; şu an online olan herkes her zaman sayılır. Oturumu açık kalıp yeniden giriş yapmayan kullanıcılar da artık görünür. Beşinci kutu (<b>Toplam</b>) aktif üye sayısıdır. Büyük sayılar kısaltılır (<b>1,2k</b>, <b>3,4M</b>); tam değer kutunun üzerine gelince görünür.",
                ]
            },
        ],
        en: [
            {
                type: "ul", items: [
                    "The online list is driven by the <b>WebSocket connection</b>: a user appears when their STOMP session connects and disappears on disconnect. Nothing is inferred from “last seen”.",
                    "The list is always ordered by rank: <b>admin → moderator → author → rookie</b>, alphabetical within a role. The role filter was removed; each row starts with a one-letter role badge (TR: <b>A</b> admin, <b>M</b> moderator, <b>Y</b> author, <b>Ç</b> rookie), and hovering it shows the full role name.",
                    "Row layout: role badge, profile avatar, username, and the status color on the far right. Hover the colored dot to see the status name. The message button appears on row hover (always visible on touch screens).",
                    "You change your own status with the <b>Your status</b> selector at the top; the list names every status.",
                    "You can open a profile or start a message straight from the list.",
                    "When a user changes their status, the badge updates <b>instantly</b> for everyone who has the site open.",
                    "The chosen status is stored in preferences and survives across sessions.",
                ]
            },
            {
                type: "table",
                headers: ["Status", "Indicator", "Visibility"],
                rows: [
                    ["<b>ONLINE</b>", "Green dot", "Everyone"],
                    ["<b>BUSY</b>", "Yellow dot", "Everyone"],
                    ["<b>DND</b>", "Red dot", "Everyone"],
                    ["<b>AWAY</b>", "Purple dot", "Everyone"],
                    ["<b>WATCHING</b>", "Blue dot", "Everyone"],
                    ["<b>BATHROOM</b>", "Grey dot", "Everyone"],
                    ["<b>INVISIBLE</b>", "Dashed-ring dot", "<b>Hidden</b> from regular users; shown to MOD/ADMIN with a special marker"],
                ],
            },
            { type: "note", tone: "info", text: "The dashboard’s “online users” KPI is fed by the same WebSocket counter." },
            {
                type: "ul", items: [
                    "When someone comes online or goes offline, the server broadcasts a signal; the online modal and the dashboard’s “Online now” card refresh <b>instantly</b>. The card counts the same list as the modal, so both numbers always match.",
                    "Dead connections (sleep mode, network switch) are detected by heart-beats within about 1 minute: the server drops the session and the browser reconnects on its own.",
                    "The <b>Today · 7 days · 30 days · This year</b> numbers under the modal look at last activity (updated on every WebSocket connection) in addition to the sign-in date, and everyone online right now is always counted. Users who stay signed in without logging in again now show up too. The fifth box (<b>Total</b>) is the active member count. Large values are shortened (<b>1.2k</b>, <b>3.4M</b>); hover a box to see the exact value.",
                ]
            },
        ],
    },
};

// ─────────────────────────────────────────────────────────────────────────────
// GROUP 4 — MODERATION AND THE ADMIN PANEL
// ─────────────────────────────────────────────────────────────────────────────

const SECTION_PANEL_TOUR: ManualSection = {
    id: "panel-tour",
    icon: "tools",
    title: { tr: "Admin Panelinde Ne Var, Ne Yok?", en: "Admin Panel: Tab by Tab" },
    summary: {
        tr: "Her sekmenin ne işe yaradığı ve kimin görebildiği.",
        en: "What each tab does and who can see it.",
    },
    blocks: {
        tr: [
            {
                type: "table",
                headers: ["Sekme", "Ne yapar?", "Erişim"],
                rows: [
                    ["<b>Panel</b> (Dashboard)", "KPI kartları (toplam kullanıcı/tanım/maç, çevrimiçi sayısı, bekleyen şikayet), dönemsel grafikler, tema dağılımı ve son hareketler. Bekleyen şikayet ve öneri kartlarına tıklayınca ilgili sekmeye atlar.", "MOD + ADMIN"],
                    ["<b>Kullanıcılar</b>", "Arama/filtre/sıralama ile kullanıcı listesi; askıya alma, yasaklama, rol ve durum değiştirme, şifre/e-posta/kullanıcı adı değiştirme, onaylı rozeti, kullanıcı oluşturma, tekli ve toplu silme, biyografi moderasyonu. <i>Hangi işlemi kimin yapabildiği için “Roller ve Yetki Matrisi” bölümüne bakın.</i>", "MOD (kısıtlı) + ADMIN"],
                    ["<b>Yazarlar</b>", "AUTHOR + MODERATOR + ADMIN kullanıcıların puan sıralaması. Sadece panelde görünür, profillerde gösterilmez.", "MOD + ADMIN"],
                    ["<b>Çaylaklar</b>", "Terfi bekleyen ROOKIE kullanıcıların puan sıralaması ve terfi değerlendirmesi.", "MOD + ADMIN"],
                    ["<b>Tanımlar</b>", "Tüm tanımlar; ayrıca <b>silinmiş</b> ve <b>düzenlenmiş</b> tanım listeleri. Tanım oluşturma, düzenleme, silme ve geri alma.", "MOD + ADMIN"],
                    ["<b>Gündem</b>", "Başlık önerilerini onaylama/reddetme, gündem başlığı açma ve düzenleme, kanal atama ve sıralama, aktif/pasif ve kilit yönetimi. <b>Başlığı kalıcı silme sadece ADMIN.</b>", "MOD + ADMIN"],
                    ["<b>Taşı / Birleştir</b>", "Tanımları başka başlığa taşıma ve başlık birleştirme — ikisi de MOD + ADMIN.", "MOD + ADMIN"],
                    ["<b>Sabitlenenler</b>", "Site geneli başlık ve tanım sabitleme.", "MOD + ADMIN"],
                    ["<b>Maçlar</b>", "Maç oluşturma/düzenleme, skor ve durum güncelleme, hakem atama, kilitleme.", "MOD + ADMIN"],
                    ["<b>Kulüpler</b>", "Kulüp oluşturma/düzenleme; silme (pasife alma) sadece ADMIN.", "MOD + ADMIN"],
                    ["<b>Kişiler</b>", "Futbolcu, teknik direktör, hakem vb. kişi kayıtları.", "MOD + ADMIN"],
                    ["<b>Takımlar</b>", "Kulüplerin spor/cinsiyet bazlı takım şubeleri.", "MOD + ADMIN"],
                    ["<b>Stadyumlar</b>", "Stadyum kayıtları (kapasite, zemin, konum).", "MOD + ADMIN"],
                    ["<b>IP Yasakları</b>", "IP bazlı erişim engelleme ve kaldırma.", "MOD + ADMIN"],
                    ["<b>Şikayetler</b>", "Tanım ve mesaj şikayetleri; reddetme (dismiss) veya işlem alma (action).", "MOD + ADMIN"],
                    ["<b>Denetim Kayıtları</b>", "Tüm admin/moderatör işlemlerinin kaydı.", "<b>Sadece ADMIN</b>"],
                    ["<b>Master Data</b>", "Ülke, lig, hafta/tur, kişi rolü, başlık kategorisi (kanal) referans verileri.", "<b>Sadece ADMIN</b>"],
                    ["<b>Ayarlar</b>", "Tüm çalışma zamanı sistem ayarları.", "<b>Sadece ADMIN</b>"],
                    ["<b>İletişim</b>", "Herkese açık iletişim sayfasının e-posta, telefon, adres, sosyal medya ve politika linkleri.", "<b>Sadece ADMIN</b>"],
                ],
            },
            { type: "note", tone: "info", text: "Panel URL’i sadece ADMIN ve MODERATOR rollerine açıktır; diğer roller ana sayfaya yönlendirilir. Yetkiye bağlı sekmeler moderatöre <b>hiç gösterilmez</b>, ayrıca backend tarafında da ayrıca doğrulanır." },
        ],
        en: [
            {
                type: "table",
                headers: ["Tab", "What it does", "Access"],
                rows: [
                    ["<b>Dashboard</b>", "KPI cards (total users/entries/matches, online count, pending reports), period charts, theme distribution and recent activity. Pending-report and suggestion cards jump to the relevant tab.", "MOD + ADMIN"],
                    ["<b>Users</b>", "Searchable/filterable/sortable user list; suspend, ban, change role and status, change password/email/username, verified badge, create users, single and bulk delete, bio moderation. <i>See “Roles and Permission Matrix” for who may do what.</i>", "MOD (limited) + ADMIN"],
                    ["<b>Authors</b>", "Score-ranked board of AUTHOR + MODERATOR + ADMIN users. Admin-panel only, never shown on profiles.", "MOD + ADMIN"],
                    ["<b>Rookies</b>", "Score-ranked board of ROOKIE users awaiting promotion.", "MOD + ADMIN"],
                    ["<b>Entries</b>", "All entries, plus dedicated <b>deleted</b> and <b>edited</b> lists. Create, edit, delete and restore entries.", "MOD + ADMIN"],
                    ["<b>Gündem</b>", "Approve/reject topic suggestions, create and edit custom topics, assign and order channels, manage active/locked state. <b>Hard-deleting a topic is ADMIN only.</b>", "MOD + ADMIN"],
                    ["<b>Move / Merge</b>", "Move entries to another topic and merge topics — both MOD + ADMIN.", "MOD + ADMIN"],
                    ["<b>Pins</b>", "Sitewide topic and entry pinning.", "MOD + ADMIN"],
                    ["<b>Matches</b>", "Create/edit matches, update scores and status, assign referees, lock.", "MOD + ADMIN"],
                    ["<b>Clubs</b>", "Create/edit clubs; deletion (deactivation) is ADMIN only.", "MOD + ADMIN"],
                    ["<b>People</b>", "Players, coaches, referees and other person records.", "MOD + ADMIN"],
                    ["<b>Teams</b>", "Sport/gender-specific team branches of clubs.", "MOD + ADMIN"],
                    ["<b>Stadiums</b>", "Stadium records (capacity, surface, location).", "MOD + ADMIN"],
                    ["<b>Banned IPs</b>", "IP-level access bans and removals.", "MOD + ADMIN"],
                    ["<b>Reports</b>", "Entry and message reports; dismiss or action them.", "MOD + ADMIN"],
                    ["<b>Audit logs</b>", "Record of every admin/moderator action.", "<b>ADMIN only</b>"],
                    ["<b>Master data</b>", "Country, league, round, person role and topic category (channel) reference data.", "<b>ADMIN only</b>"],
                    ["<b>Settings</b>", "All runtime system settings.", "<b>ADMIN only</b>"],
                    ["<b>Contact</b>", "Emails, phone, address, social links and policy URLs for the public contact page.", "<b>ADMIN only</b>"],
                ],
            },
            { type: "note", tone: "info", text: "The panel URL is open to ADMIN and MODERATOR only; other roles are redirected home. Restricted tabs are <b>never rendered</b> for moderators and are additionally enforced on the backend." },
        ],
    },
};

const SECTION_TOGGLES: ManualSection = {
    id: "toggles",
    icon: "sliders",
    title: { tr: "Neyi Nereden Açıp Kapatıyorum?", en: "Where Do I Turn Things On and Off?" },
    summary: {
        tr: "“Şunu değiştirmek istiyorum” → hangi sekme, hangi alan.",
        en: "“I want to change this” → which tab, which field.",
    },
    blocks: {
        tr: [
            { type: "p", text: "Bu bölüm bir arama tablosudur: soldaki sütunda yapmak istediğinizi bulun, sağda nereye gideceğiniz yazar." },

            { type: "sub", text: "Site geneli" },
            {
                type: "table",
                headers: ["Ne yapmak istiyorum", "Nereden", "Not"],
                rows: [
                    ["Siteyi bakıma almak", "<b>Ayarlar → Genel</b> → Bakım modu", "Açıkken ziyaretçiler bakım sayfası görür, açık oturumlar kapatılır; kilitli kalırsanız <code>/login</code> adresinden admin girişi yapın"],
                    ["Tüm kullanıcılara duyuru göstermek", "<b>Ayarlar → Genel</b> → Duyuru açık + Duyuru metni", "İkisini birlikte doldurun; metin boşsa bant anlamsız görünür"],
                    ["Site adını / sloganı değiştirmek", "<b>Ayarlar → Genel</b>", "Başlıkta ve logonun altında görünür"],
                    ["İletişim sayfasındaki e-posta, telefon, adres, sosyal medya", "<b>İletişim</b> sekmesi", "Boş bıraktığınız alan sayfada hiç görünmez"],
                    ["Gizlilik / kullanım şartları linkleri", "<b>İletişim</b> sekmesi", "—"],
                ],
            },

            { type: "sub", text: "Üyelik ve kullanıcılar" },
            {
                type: "table",
                headers: ["Ne yapmak istiyorum", "Nereden", "Not"],
                rows: [
                    ["Yeni kayıtları tamamen kapatmak", "<b>Ayarlar → Kayıt</b> → Kayıt açık", "Mevcut kullanıcılar etkilenmez"],
                    ["Yeni üyelerin çaylak mı yazar mı başlayacağı", "<b>Ayarlar → Kayıt</b> → Varsayılan rol", "Çaylak seçilirse yeni üyeler oy veremez"],
                    ["E-posta doğrulamasını zorunlu kılmak", "<b>Ayarlar → Kayıt</b> → E-posta doğrulama", "Açıkken hesap doğrulanana kadar pasif kalır"],
                    ["Kullanıcı adı / biyografi uzunluk sınırı", "<b>Ayarlar → Kayıt</b>", "Kullanıcı adı üst sınırı 50’yi geçemez"],
                    ["Birini askıya almak / askıyı kaldırmak", "<b>Kullanıcılar</b> → satırdaki askı butonu", "Gün sayısı ve sebep girilir"],
                    ["Birini yasaklamak / yasağı kaldırmak", "<b>Kullanıcılar</b> → yasakla butonu", "Sadece admin"],
                    ["Birinin rolünü değiştirmek (terfi/tenzil)", "<b>Kullanıcılar</b> → rol değiştir", "Çaylak → Yazar terfisi buradan yapılır"],
                    ["Kullanıcı silmek", "<b>Kullanıcılar</b> → sil", "Sadece admin; soft/hard seçenekleri modalde"],
                    ["Şifre / e-posta / kullanıcı adı değiştirmek", "<b>Kullanıcılar</b> → ilgili buton", "—"],
                    ["Onaylı rozeti vermek", "<b>Kullanıcılar</b> → onaylı işaretle", "—"],
                    ["Uygunsuz biyografiyi silmek / bloklamak", "<b>Kullanıcılar</b> → kullanıcı detayı", "Bloklarken sebep girilir"],
                    ["Yeni kullanıcı oluşturmak", "<b>Kullanıcılar</b> → kullanıcı ekle", "Sadece admin"],
                    ["Terfi adaylarına bakmak", "<b>Çaylaklar</b> sekmesi", "Sadece öneri listesidir"],
                ],
            },

            { type: "sub", text: "Tanım ve başlık kuralları" },
            {
                type: "table",
                headers: ["Ne yapmak istiyorum", "Nereden", "Not"],
                rows: [
                    ["Günlük tanım kotasını değiştirmek", "<b>Ayarlar → Tanım Kuralları</b> → Günlük limit", "0 girerseniz limit kalkar"],
                    ["Tanım uzunluk sınırlarını değiştirmek", "<b>Ayarlar → Tanım Kuralları</b> → Min / Maks uzunluk", "—"],
                    ["Yazarın kaç dakika düzenleyebileceğini ayarlamak", "<b>Ayarlar → Tanım Kuralları</b> → Düzenleme penceresi", "0 girerseniz süresiz düzenlenir"],
                    ["Arka arkaya tanım girmeyi yavaşlatmak", "<b>Ayarlar → Tanım Kuralları</b> → Spam bekleme süresi", "0 girerseniz spam koruması kapanır"],
                    ["Taslak sayısını sınırlamak", "<b>Ayarlar → Tanım Kuralları</b> → Taslak limiti", "—"],
                    ["“Devamını göster” eşiğini değiştirmek", "<b>Ayarlar → Tanım Kuralları</b> → Önizleme satır limiti", "0 girerseniz hiç kısaltılmaz"],
                    ["Başlık adı uzunluk sınırı", "<b>Ayarlar → Tanım Kuralları</b> → Başlık uzunluğu", "50–80 arası bir değer alır"],
                    ["Başlık önerme sıklığını sınırlamak", "<b>Ayarlar → Tanım Kuralları</b> → Öneri bekleme süresi / günlük limit", "0 girerseniz sınır kalkar"],
                ],
            },

            { type: "sub", text: "İçerik ve başlık yönetimi" },
            {
                type: "table",
                headers: ["Ne yapmak istiyorum", "Nereden", "Not"],
                rows: [
                    ["Bir başlığa yeni tanım girilmesini durdurmak", "İlgili sekme (<b>Maçlar / Kulüpler / Takımlar / Kişiler / Gündem</b>) → satırdaki kilit", "Sebep yazabilirsiniz; admin ve modlar kilidi aşar"],
                    ["Bir gündem başlığını yayından kaldırmak", "<b>Gündem</b> → aktif/pasif anahtarı", "Kilitten farklıdır: pasif başlık yok gibi davranır"],
                    ["Bir gündem başlığını kalıcı silmek", "<b>Gündem → Başlıklar</b> → satırdaki çöp kutusu", "Sadece ADMIN, <b>geri alınamaz</b> — tanımları da siler"],
                    ["Başlık önerisini onaylamak / reddetmek", "<b>Gündem</b> → öneri listesi", "Reddederken yazdığınız not kullanıcıya gider"],
                    ["Yeni gündem başlığı açmak", "<b>Gündem</b> → başlık ekle", "Kanal seçmeyi unutmayın — ilk kanal ikonu belirler"],
                    ["Bir başlığı listenin en üstüne sabitlemek", "<b>Sabitlenenler</b> → başlık sabitle", "—"],
                    ["Bir tanımı başlık sayfasının en üstüne sabitlemek", "<b>Sabitlenenler</b> → tanım sabitle", "Başlık başına tek tanım"],
                    ["Tanım silmek / geri almak / düzenlemek", "<b>Tanımlar</b> sekmesi", "Silinmiş ve düzenlenmiş tanımlar için ayrı listeler var"],
                    ["Yanlış başlığa girilmiş tanımı taşımak", "<b>Taşı / Birleştir</b> → taşı", "Farklı tipe de taşınabilir"],
                    ["Mükerrer iki başlığı birleştirmek", "<b>Taşı / Birleştir</b> → birleştir", "MOD + ADMIN, <b>geri alınamaz</b>"],
                    ["Şikayeti karara bağlamak", "<b>Şikayetler</b> → reddet / işlem al", "Tanım ve mesaj şikayetleri ayrı listelerde"],
                ],
            },

            { type: "sub", text: "Spor verisi" },
            {
                type: "table",
                headers: ["Ne yapmak istiyorum", "Nereden", "Not"],
                rows: [
                    ["Yeni kulüp eklemek", "<b>Kulüpler</b> → kulüp ekle", "<b>Ardından mutlaka takım da ekleyin</b>"],
                    ["Yeni takım eklemek", "<b>Takımlar</b> → takım ekle", "Kulüp + spor + cinsiyet + (opsiyonel) takım kodu"],
                    ["Yeni maç eklemek / skor girmek", "<b>Maçlar</b> sekmesi", "Takımlar üzerinden seçilir, kulüpler üzerinden değil. Skor, durum, kilit ve hakem değişiklikleri giriş yapmış kullanıcıların ekranına yenilemeden anında yansır; ziyaretçiler Yenile ile görür"],
                    ["Hakem atamak", "<b>Maçlar</b> → maç detayı", "—"],
                    ["Futbolcu / teknik direktör / hakem eklemek", "<b>Kişiler</b> sekmesi", "Rolü master data’dan gelir"],
                    ["Stadyum eklemek", "<b>Stadyumlar</b> sekmesi", "—"],
                    ["Yeni lig / ülke / hafta / kişi rolü eklemek", "<b>Master Data</b>", "Sadece admin; silme yoktur, pasife alınır"],
                    ["Yeni kanal (gündem kategorisi) eklemek", "<b>Master Data → Başlık Kategorileri</b>", "Sadece admin"],
                ],
            },

            { type: "sub", text: "Güvenlik ve denetim" },
            {
                type: "table",
                headers: ["Ne yapmak istiyorum", "Nereden", "Not"],
                rows: [
                    ["Bir IP’yi engellemek", "<b>IP Yasakları</b> → IP ekle", "0 gün girerseniz kalıcı olur"],
                    ["Varsayılan IP yasak süresini değiştirmek", "<b>Ayarlar → Moderasyon</b>", "—"],
                    ["Aynı tanımın tekrar şikayet edilme aralığı", "<b>Ayarlar → Moderasyon</b> → Şikayet bekleme günü", "—"],
                    ["Kaç şikayette inceleme bayrağı çıkacağı", "<b>Ayarlar → Moderasyon</b> → İnceleme eşiği", "—"],
                    ["Kimin ne yaptığını görmek", "<b>Denetim Kayıtları</b>", "Sadece admin; tüm admin/mod işlemleri burada"],
                ],
            },

            { type: "sub", text: "Mesajlaşma, arama, medya" },
            {
                type: "table",
                headers: ["Ne yapmak istiyorum", "Nereden", "Not"],
                rows: [
                    ["Mesaj uzunluk sınırı / kutu limiti", "<b>Ayarlar → Mesajlaşma</b>", "—"],
                    ["Aramanın kaç karakterden sonra çalışacağı", "<b>Ayarlar → Arama</b> → Minimum karakter", "—"],
                    ["Arama önerilerinin gecikmesi", "<b>Ayarlar → Arama</b> → Gecikme (ms)", "—"],
                    ["Açılır aramada kategori başına sonuç sayısı", "<b>Ayarlar → Arama</b> → Sonuç limiti", "—"],
                    ["Avatar / kapak / tanım görseli boyut sınırı", "<b>Ayarlar → Medya</b>", "Değerler MB cinsindendir"],
                ],
            },

            { type: "note", tone: "info", text: "<b>Ayarlar</b> sekmesinde her grubun kendi <b>Kaydet</b> butonu vardır — bir gruptaki değişiklikleri kaydetmeden başka gruba geçerseniz o değişiklikler kaybolur. Kaydedilen değer <b>anında</b> geçerli olur, yeniden başlatma gerekmez ve bu kılavuzdaki sayılar da onunla birlikte güncellenir." },
        ],
        en: [
            { type: "p", text: "This section is a lookup table: find what you want to do on the left, and the right column tells you where to go." },

            { type: "sub", text: "Site-wide" },
            {
                type: "table",
                headers: ["I want to…", "Where", "Note"],
                rows: [
                    ["Put the site into maintenance", "<b>Settings → General</b> → Maintenance mode", "Visitors see a maintenance page and signed-in sessions end; if locked out, sign in as admin at <code>/login</code>"],
                    ["Show an announcement to everyone", "<b>Settings → General</b> → Announcement enabled + text", "Fill both; an empty text makes the banner meaningless"],
                    ["Change the site name / slogan", "<b>Settings → General</b>", "Shown in the title and under the logo"],
                    ["Contact page emails, phone, address, socials", "<b>Contact</b> tab", "Fields you leave empty are hidden on the page"],
                    ["Privacy / terms links", "<b>Contact</b> tab", "—"],
                ],
            },

            { type: "sub", text: "Membership and users" },
            {
                type: "table",
                headers: ["I want to…", "Where", "Note"],
                rows: [
                    ["Close new registrations entirely", "<b>Settings → Registration</b> → Registration enabled", "Existing users are unaffected"],
                    ["Decide whether new members start as rookie or author", "<b>Settings → Registration</b> → Default role", "Rookies cannot vote"],
                    ["Require email verification", "<b>Settings → Registration</b> → Email verification", "Accounts stay inactive until verified"],
                    ["Username / bio length limits", "<b>Settings → Registration</b>", "The username cap can never exceed 50"],
                    ["Suspend / unsuspend someone", "<b>Users</b> → suspend button on the row", "Enter days and a reason"],
                    ["Ban / unban someone", "<b>Users</b> → ban button", "Admin only"],
                    ["Change someone’s role (promote/demote)", "<b>Users</b> → change role", "Rookie → Author promotion happens here"],
                    ["Delete a user", "<b>Users</b> → delete", "Admin only; soft/hard options are in the modal"],
                    ["Change password / email / username", "<b>Users</b> → the relevant button", "—"],
                    ["Grant the verified badge", "<b>Users</b> → mark verified", "—"],
                    ["Remove or block an inappropriate bio", "<b>Users</b> → user detail", "A reason is required when blocking"],
                    ["Create a new user", "<b>Users</b> → add user", "Admin only"],
                    ["Review promotion candidates", "<b>Rookies</b> tab", "It is only a shortlist"],
                ],
            },

            { type: "sub", text: "Entry and topic rules" },
            {
                type: "table",
                headers: ["I want to…", "Where", "Note"],
                rows: [
                    ["Change the daily entry quota", "<b>Settings → Entry rules</b> → Daily limit", "0 removes the limit"],
                    ["Change entry length limits", "<b>Settings → Entry rules</b> → Min / max length", "—"],
                    ["Set how long authors can edit", "<b>Settings → Entry rules</b> → Edit window", "0 means always editable"],
                    ["Slow down back-to-back posting", "<b>Settings → Entry rules</b> → Spam cooldown", "0 disables spam protection"],
                    ["Limit the number of drafts", "<b>Settings → Entry rules</b> → Draft limit", "—"],
                    ["Change the “show more” threshold", "<b>Settings → Entry rules</b> → Preview line limit", "0 disables truncation"],
                    ["Topic title length limit", "<b>Settings → Entry rules</b> → Topic title length", "Accepts a value between 50 and 80"],
                    ["Limit how often users suggest topics", "<b>Settings → Entry rules</b> → Suggestion cooldown / daily limit", "0 removes the limit"],
                ],
            },

            { type: "sub", text: "Content and topic management" },
            {
                type: "table",
                headers: ["I want to…", "Where", "Note"],
                rows: [
                    ["Stop new entries on a topic", "The relevant tab (<b>Matches / Clubs / Teams / People / Gündem</b>) → lock on the row", "You can add a reason; admins and mods bypass the lock"],
                    ["Take a Gündem topic off the site", "<b>Gündem</b> → active/inactive toggle", "Different from a lock: an inactive topic behaves as if it does not exist"],
                    ["Permanently delete a Gündem topic", "<b>Gündem → Topics</b> → trash button on the row", "ADMIN only, <b>irreversible</b> — deletes its entries too"],
                    ["Approve / reject a topic suggestion", "<b>Gündem</b> → suggestion list", "The note you write on rejection is sent to the user"],
                    ["Create a new Gündem topic", "<b>Gündem</b> → add topic", "Do not forget the channels — the first one sets the icon"],
                    ["Pin a topic to the top of its list", "<b>Pins</b> → pin topic", "—"],
                    ["Pin an entry to the top of a topic", "<b>Pins</b> → pin entry", "One entry per topic"],
                    ["Delete / restore / edit entries", "<b>Entries</b> tab", "Separate lists exist for deleted and edited entries"],
                    ["Move an entry posted under the wrong topic", "<b>Move / Merge</b> → move", "Cross-type moves are allowed"],
                    ["Merge two duplicate topics", "<b>Move / Merge</b> → merge", "MOD + ADMIN, <b>irreversible</b>"],
                    ["Resolve a report", "<b>Reports</b> → dismiss / action", "Entry and message reports are separate lists"],
                ],
            },

            { type: "sub", text: "Sports data" },
            {
                type: "table",
                headers: ["I want to…", "Where", "Note"],
                rows: [
                    ["Add a new club", "<b>Clubs</b> → add club", "<b>Then always add a team too</b>"],
                    ["Add a new team", "<b>Teams</b> → add team", "Club + sport + gender + (optional) team code"],
                    ["Add a match / enter a score", "<b>Matches</b> tab", "Selected via teams, not clubs. Score, status, lock and referee changes appear instantly on signed-in users’ screens without a refresh; visitors see them after Refresh"],
                    ["Assign referees", "<b>Matches</b> → match detail", "—"],
                    ["Add a player / coach / referee", "<b>People</b> tab", "The role comes from master data"],
                    ["Add a stadium", "<b>Stadiums</b> tab", "—"],
                    ["Add a league / country / round / person role", "<b>Master data</b>", "Admin only; no deletion, only deactivation"],
                    ["Add a new channel (Gündem category)", "<b>Master data → Topic categories</b>", "Admin only"],
                ],
            },

            { type: "sub", text: "Security and audit" },
            {
                type: "table",
                headers: ["I want to…", "Where", "Note"],
                rows: [
                    ["Block an IP", "<b>Banned IPs</b> → add IP", "0 days makes it permanent"],
                    ["Change the default IP ban duration", "<b>Settings → Moderation</b>", "—"],
                    ["How long before the same entry can be re-reported", "<b>Settings → Moderation</b> → Report cooldown days", "—"],
                    ["How many reports raise a review flag", "<b>Settings → Moderation</b> → Review threshold", "—"],
                    ["See who did what", "<b>Audit logs</b>", "Admin only; every admin/mod action lands here"],
                ],
            },

            { type: "sub", text: "Messaging, search, media" },
            {
                type: "table",
                headers: ["I want to…", "Where", "Note"],
                rows: [
                    ["Message length / inbox limit", "<b>Settings → Messaging</b>", "—"],
                    ["How many characters before search fires", "<b>Settings → Search</b> → Minimum characters", "—"],
                    ["Search suggestion delay", "<b>Settings → Search</b> → Delay (ms)", "—"],
                    ["Results per category in the dropdown", "<b>Settings → Search</b> → Result limit", "—"],
                    ["Avatar / banner / entry image size limits", "<b>Settings → Media</b>", "Values are in MB"],
                ],
            },

            { type: "note", tone: "info", text: "In the <b>Settings</b> tab each group has its own <b>Save</b> button — switching groups without saving loses your changes. A saved value takes effect <b>immediately</b>, needs no restart, and the numbers in this manual update along with it." },
        ],
    },
};

const SECTION_REPORTS: ManualSection = {
    id: "reports",
    icon: "flag",
    title: { tr: "Şikayetler ve Moderasyon", en: "Reports and Moderation" },
    summary: {
        tr: "Şikayet sebepleri, durumlar, tekrar şikayet bekleme süresi.",
        en: "Report reasons, statuses and the re-report cooldown.",
    },
    blocks: {
        tr: [
            { type: "sub", text: "Şikayet sebepleri" },
            {
                type: "ul", items: [
                    "<b>SPAM</b> — Spam / Reklam",
                    "<b>PROFANITY</b> — Küfür / Hakaret",
                    "<b>HATE_SPEECH</b> — Nefret Söylemi / Ayrımcılık",
                    "<b>HARASSMENT</b> — Tehdit / Taciz",
                    "<b>MISINFORMATION</b> — Yanlış / Yanıltıcı Bilgi",
                    "<b>PERSONAL_INFO</b> — Kişisel Bilgi İfşası",
                    "<b>INAPPROPRIATE</b> — Müstehcen İçerik",
                    "<b>AI_GENERATED</b> — Yapay Zeka İçeriği <i>(sadece tanım şikayetlerinde)</i>",
                    "<b>SPELLING_ERROR</b> — Yazım / Noktalama Hatası <i>(sadece tanım)</i>",
                    "<b>NOT_A_DEFINITION</b> — Forumsal / Tanımsız / Cevap <i>(sadece tanım)</i>",
                    "<b>OTHER</b> — Diğer <i>(serbest metin zorunlu)</i>",
                ]
            },
            { type: "note", tone: "info", text: "Mesaj şikayetleri aynı sebep listesini paylaşır ama son dört sebep mesaj şikayet modalinde <b>gösterilmez</b> — onlar tanıma özgüdür." },
            { type: "sub", text: "Şikayet yaşam döngüsü" },
            {
                type: "table",
                headers: ["Durum", "Anlamı"],
                rows: [
                    ["<b>PENDING</b>", "İncelenmeyi bekliyor — panelde bekleyen sayacına dahildir"],
                    ["<b>DISMISSED</b>", "İncelendi, işlem yapılmadı"],
                    ["<b>ACTIONED</b>", "İncelendi, tanıma veya yazara işlem uygulandı"],
                ],
            },
            { type: "settings", keys: ["moderation.report_cooldown_days", "moderation.auto_ban_report_threshold"] },
            {
                type: "ul", items: [
                    "Bir kullanıcı aynı tanımı ancak {{setting:moderation.report_cooldown_days}} gün sonra yeniden şikayet edebilir.",
                    "Bir tanım {{setting:moderation.auto_ban_report_threshold}} şikayete ulaştığında inceleme bayrağı tetiklenir.",
                    "Serbest metin sebebi ve admin çözüm notu {{const:report_text_max}} karakterle sınırlıdır.",
                    "Reddedilmemiş her şikayet, yazarın çaylak/yazar puanından <b>−5.0</b> düşürür.",
                ]
            },
            { type: "sub", text: "Modlog" },
            { type: "p", text: "Kullanıcı, kendi tanımlarına uygulanan moderasyon işlemlerini <b>modlog</b> sayfasında görebilir. Bu sayfa şeffaflık içindir; silme ve düzenleme işlemleri burada listelenir." },
        ],
        en: [
            { type: "sub", text: "Report reasons" },
            {
                type: "ul", items: [
                    "<b>SPAM</b> — spam / advertising",
                    "<b>PROFANITY</b> — profanity / insults",
                    "<b>HATE_SPEECH</b> — hate speech / discrimination",
                    "<b>HARASSMENT</b> — threats / harassment",
                    "<b>MISINFORMATION</b> — false or misleading information",
                    "<b>PERSONAL_INFO</b> — doxxing",
                    "<b>INAPPROPRIATE</b> — explicit content",
                    "<b>AI_GENERATED</b> — AI-generated content <i>(entry reports only)</i>",
                    "<b>SPELLING_ERROR</b> — spelling/punctuation <i>(entry only)</i>",
                    "<b>NOT_A_DEFINITION</b> — forum-style / not a definition <i>(entry only)</i>",
                    "<b>OTHER</b> — other <i>(free text required)</i>",
                ]
            },
            { type: "note", tone: "info", text: "Message reports share the same reason enum, but the last four are <b>not offered</b> in the message report modal — they are entry-specific." },
            { type: "sub", text: "Report lifecycle" },
            {
                type: "table",
                headers: ["Status", "Meaning"],
                rows: [
                    ["<b>PENDING</b>", "Awaiting review — counted in the dashboard’s pending badge"],
                    ["<b>DISMISSED</b>", "Reviewed, no action taken"],
                    ["<b>ACTIONED</b>", "Reviewed, action taken on the entry or author"],
                ],
            },
            { type: "settings", keys: ["moderation.report_cooldown_days", "moderation.auto_ban_report_threshold"] },
            {
                type: "ul", items: [
                    "A user can re-report the same entry only after {{setting:moderation.report_cooldown_days}} days.",
                    "Once an entry reaches {{setting:moderation.auto_ban_report_threshold}} reports, a review flag is raised.",
                    "The free-text reason and the admin resolution note are capped at {{const:report_text_max}} characters.",
                    "Every non-dismissed report costs the author <b>−5.0</b> on the rookie/author rank score.",
                ]
            },
            { type: "sub", text: "Modlog" },
            { type: "p", text: "Users can see the moderation actions applied to their own entries on the <b>modlog</b> page. It exists for transparency and lists deletions and edits." },
        ],
    },
};

const SECTION_IPBAN: ManualSection = {
    id: "ip-bans",
    icon: "ban",
    title: { tr: "IP Yasakları", en: "IP Bans" },
    summary: {
        tr: "IP bazlı engelleme, süre ve kaldırma.",
        en: "IP-level blocking, duration and removal.",
    },
    blocks: {
        tr: [
            {
                type: "ul", items: [
                    "IP yasağı, hesap yasağından bağımsızdır — kullanıcı adı ne olursa olsun o IP’den erişim engellenir.",
                    "Engelleme bir filtre üzerinden istek girişinde uygulanır.",
                    "Süre gün cinsindendir; <b>0</b> girilirse yasak <b>kalıcıdır</b>.",
                    "Yasak <b>kaldırılabilir</b> (pasife alınır) veya kayıt tamamen <b>silinebilir</b>.",
                    "Üç işlem de denetim kaydına yazılır: BAN_IP, UNBAN_IP, DELETE_IP_BAN.",
                    "Kullanıcı son giriş IP’si kullanıcı listesinde görünür — şüpheli hesapları eşleştirmek için kullanılır.",
                ]
            },
            { type: "settings", keys: ["moderation.default_ip_ban_days"] },
            { type: "note", tone: "warn", text: "Paylaşımlı IP’lerde (okul, iş yeri, mobil operatör NAT) tüm masum kullanıcılar da engellenir. Önce hesap bazlı askı/yasak tercih edilmelidir." },
        ],
        en: [
            {
                type: "ul", items: [
                    "IP bans are independent of account bans — access from that IP is blocked regardless of the username.",
                    "The block is applied by a filter at request entry.",
                    "Duration is in days; entering <b>0</b> makes the ban <b>permanent</b>.",
                    "A ban can be <b>lifted</b> (deactivated) or the record can be <b>deleted</b> outright.",
                    "All three actions are audited: BAN_IP, UNBAN_IP, DELETE_IP_BAN.",
                    "The user’s last login IP is shown in the user list, which helps correlate suspicious accounts.",
                ]
            },
            { type: "settings", keys: ["moderation.default_ip_ban_days"] },
            { type: "note", tone: "warn", text: "On shared IPs (schools, offices, carrier NAT) innocent users are blocked too. Prefer account-level suspension or bans first." },
        ],
    },
};

const SECTION_LOCK: ManualSection = {
    id: "locks",
    icon: "lock",
    title: { tr: "Başlık Kilitleme", en: "Topic Locking" },
    summary: {
        tr: "Beş domainde kilit, sebep alanı ve kimin muaf olduğu.",
        en: "Locking across all five domains, reason field and who bypasses it.",
    },
    blocks: {
        tr: [
            {
                type: "ul", items: [
                    "Beş domainin tamamı (maç, kulüp, takım, kişi, gündem) kilitlenebilir.",
                    "Kilitli bir başlığa tanım girmeye çalışan kullanıcı “bu başlık kilitli” uyarısı alır.",
                    "<b>ADMIN ve MODERATOR kilidi aşar</b> — kilitli başlığa tanım girebilirler.",
                    "Kilitle birlikte bir <b>sebep</b> yazılabilir; kullanıcıya bilgi modalinde gösterilir.",
                    "Kilitleme ve açma denetim kaydına yazılır: LOCK_TOPIC, UNLOCK_TOPIC.",
                    "Kilit, mevcut tanımları gizlemez — sadece yeni girişi durdurur.",
                ]
            },
            { type: "note", tone: "info", text: "Gündem başlıkları ayrıca <b>aktif/pasif</b> yapılabilir. Pasif bir gündem başlığı tanım girişinde <b>bulunamadı</b> gibi davranır — kilitten farklıdır." },
        ],
        en: [
            {
                type: "ul", items: [
                    "All five domains (match, club, team, person, gündem) can be locked.",
                    "A user trying to post into a locked topic is told the topic is locked.",
                    "<b>ADMIN and MODERATOR bypass the lock</b> and can still post.",
                    "A <b>reason</b> can be recorded with the lock and is shown to users in an info modal.",
                    "Locking and unlocking are audited: LOCK_TOPIC, UNLOCK_TOPIC.",
                    "A lock never hides existing entries — it only stops new ones.",
                ]
            },
            { type: "note", tone: "info", text: "Gündem topics can additionally be set <b>inactive</b>. An inactive topic behaves as <b>not found</b> for entry submission — which is different from being locked." },
        ],
    },
};

const SECTION_PINS: ManualSection = {
    id: "pins",
    icon: "thumbtack",
    title: { tr: "Sabitlenenler (Pin)", en: "Pins" },
    summary: {
        tr: "Başlık sabitleme ve başlık içinde tanım sabitleme.",
        en: "Pinning topics and pinning one entry inside a topic.",
    },
    blocks: {
        tr: [
            {
                type: "ul", items: [
                    "İki bağımsız sabitleme türü vardır ve ikisi de başlık başına tek bir kayıtta tutulur:",
                    "<b>Başlık sabitleme:</b> başlık, kendi sol panel listesinin en üstünde görünür.",
                    "<b>Tanım sabitleme:</b> seçilen tek bir tanım, başlık sayfasının en üstünde görünür.",
                    "İki tür de kaldırıldığında kayıt tamamen silinir.",
                    "Aktif sabitlemeler tek küçük bir liste olarak tüm ziyaretçilere servis edilir.",
                    "Dört işlem de denetim kaydına yazılır: PIN_TOPIC, UNPIN_TOPIC, PIN_ENTRY, UNPIN_ENTRY.",
                ]
            },
            { type: "note", tone: "warn", text: "Sabitlediğiniz bir tanımın yazarı sonradan kalıcı olarak silinirse, sabitleme de otomatik olarak kalkar." },
        ],
        en: [
            {
                type: "ul", items: [
                    "Two independent pin kinds share one row per topic:",
                    "<b>Topic pin:</b> the topic appears at the top of its left-pane list.",
                    "<b>Entry pin:</b> one selected entry appears at the top of the topic page.",
                    "When both kinds are cleared, the row is removed.",
                    "Active pins are served to every visitor as a single small list.",
                    "All four actions are audited: PIN_TOPIC, UNPIN_TOPIC, PIN_ENTRY, UNPIN_ENTRY.",
                ]
            },
            { type: "note", tone: "warn", text: "If the author of a pinned entry is later hard-deleted, the pin is removed automatically." },
        ],
    },
};

const SECTION_MOVE_MERGE: ManualSection = {
    id: "move-merge",
    icon: "random",
    title: { tr: "Taşıma ve Birleştirme", en: "Move and Merge" },
    summary: {
        tr: "Tanım taşıma ve başlık birleştirme — ikisi de MOD + ADMIN.",
        en: "Moving entries and merging topics — both MOD + ADMIN.",
    },
    blocks: {
        tr: [
            { type: "sub", text: "Taşıma — MOD + ADMIN" },
            {
                type: "ul", items: [
                    "<b>Tipler arası taşıma serbesttir</b>: bir maç tanımı gündem başlığına, bir kişi tanımı kulübe taşınabilir.",
                    "Tanım eski başlığından koparılıp yeni başlığa bağlanır; içeriği hiç değişmez.",
                    "<b>Tanımın yazılma ve düzenlenme tarihleri değişmez</b> — taşıma bir düzenleme sayılmaz, kullanıcının üzerine “düzenlendi” damgası vurulmaz.",
                    "Etkilenen her kullanıcıya, işlem başına <b>tek</b> ENTRY_MOVED bildirimi gider.",
                    "İşlem denetim kaydına MOVE_ENTRY olarak yazılır.",
                ]
            },
            { type: "sub", text: "Birleştirme — MOD + ADMIN" },
            {
                type: "ul", items: [
                    "<b>Sadece aynı tip</b> birleştirilebilir (maç↔maç, gündem↔gündem …).",
                    "Kaynağın tüm tanımları — <b>silinmiş olanlar dahil</b>, çünkü moderasyon geçmişi korunmalıdır — hedefe aktarılır.",
                    "Tarihler korunduğu için tanımlar hedef sayfada <b>kendi tarihlerine göre araya girer</b> — hepsi topluca sona eklenmez.",
                    "Taslaklar da hedefe taşınır. Oylar, favoriler ve şikayetler tanıma bağlı olduğu için onlarla birlikte gelir.",
                    "Kaynak başlık yayından kalkar; eski linkine tıklayan ziyaretçiler <b>otomatik olarak hedef başlığa yönlendirilir</b>, hata sayfası görmezler.",
                    "Zincirleme birleştirmeler de doğru çalışır: A başlığı B’ye, B de sonradan C’ye birleştirilmişse, A’nın linki doğrudan C’ye götürür.",
                    "<b>Nickaltı başlıkları hiçbir zaman birleştirilemez</b> — ne kaynak ne hedef olabilir.",
                    "İşlem denetim kaydına MERGE_TOPIC olarak yazılır.",
                ]
            },
            { type: "note", tone: "danger", text: "<b>Birleştirme geri alınamaz</b> ve moderatörlere de açıktır. Arayüz bu yüzden hedef başlığın adının harfi harfine yazılmasını ister. Yön kritiktir: <b>kaynak</b> kaybolur, <b>hedef</b> yaşar." },
        ],
        en: [
            { type: "sub", text: "Move — MOD + ADMIN" },
            {
                type: "ul", items: [
                    "<b>Cross-type moves are allowed</b>: a match entry can go to a Gündem topic, a person entry to a club, and so on.",
                    "The entry is detached from its old topic and attached to the new one; its content is untouched.",
                    "<b>The created and edited timestamps stay as they were</b> — a move is not an edit and does not stamp the entry as edited.",
                    "Each affected user receives <b>one</b> ENTRY_MOVED notification per operation.",
                    "Audited as MOVE_ENTRY.",
                ]
            },
            { type: "sub", text: "Merge — MOD + ADMIN" },
            {
                type: "ul", items: [
                    "<b>Same type only</b> (match↔match, gündem↔gündem …).",
                    "All entries of the source — <b>including soft-deleted ones</b>, since moderation history must survive — are repointed to the target.",
                    "Because the dates are preserved, entries <b>slot in chronologically</b> on the target page instead of all being appended at the end.",
                    "Drafts are moved too. Votes, favorites and reports are attached to the entry, so they come along automatically.",
                    "The source topic goes offline; visitors following its old link are <b>redirected to the surviving topic</b> instead of hitting an error page.",
                    "Chained merges work correctly too: if A was merged into B and B later into C, A’s link takes you straight to C.",
                    "<b>Nickaltı topics can never be merged</b> — on either side.",
                    "Audited as MERGE_TOPIC.",
                ]
            },
            { type: "note", tone: "danger", text: "<b>Merging is irreversible</b> and open to moderators as well. That is why the UI requires the target title to be typed verbatim. The direction matters: the <b>source</b> disappears, the <b>target</b> survives." },
        ],
    },
};

const SECTION_AUDIT: ManualSection = {
    id: "audit",
    icon: "history",
    title: { tr: "Denetim Kayıtları", en: "Audit Logs" },
    summary: {
        tr: "Kaydedilen tüm admin/moderatör işlemleri.",
        en: "Every admin/moderator action that is recorded.",
    },
    blocks: {
        tr: [
            { type: "p", text: "Denetim kaydı <b>sadece ADMIN</b> tarafından görülebilir. Her satırda işlemi yapan kişi, işlem tipi, hedef tipi, hedef kimliği, hedefin kullanıcı adı/başlığı, serbest metin detay ve zaman damgası bulunur." },
            {
                type: "table",
                headers: ["Grup", "İşlemler"],
                rows: [
                    ["Tanım", "DELETE_ENTRY · RESTORE_ENTRY · EDIT_ENTRY · HARD_DELETE_ENTRY"],
                    ["Kullanıcı", "CREATE_USER · DELETE_USER · HARD_DELETE_USER · BAN_USER · UNBAN_USER · SUSPEND_USER · UNSUSPEND_USER · CHANGE_USER_ROLE · CHANGE_USER_STATUS · CHANGE_USER_PASSWORD · CHANGE_USERNAME · CHANGE_USER_EMAIL · SET_VERIFIED"],
                    ["Biyografi", "EDIT_BIO · DELETE_BIO · BLOCK_BIO · UNBLOCK_BIO"],
                    ["IP", "BAN_IP · UNBAN_IP · DELETE_IP_BAN"],
                    ["Kilit", "LOCK_TOPIC · UNLOCK_TOPIC"],
                    ["Taşı / Birleştir", "MERGE_TOPIC · MOVE_ENTRY"],
                    ["Gündem", "HARD_DELETE_TOPIC"],
                    ["Sabitleme", "PIN_TOPIC · UNPIN_TOPIC · PIN_ENTRY · UNPIN_ENTRY"],
                ],
            },
            { type: "note", tone: "info", text: "Toplu işlemlerde <b>her hedef için ayrı bir kayıt</b> yazılır; toplu silmede girilen not da kayda eklenir. Kullanıcı adı anlık görüntü olarak saklandığı için, kullanıcı sonradan silinse bile kayıt okunabilir kalır." },
        ],
        en: [
            { type: "p", text: "Audit logs are visible to <b>ADMIN only</b>. Each row stores the actor, the action, the target type, the target id, the target’s username/title, a free-text detail and a timestamp." },
            {
                type: "table",
                headers: ["Group", "Actions"],
                rows: [
                    ["Entry", "DELETE_ENTRY · RESTORE_ENTRY · EDIT_ENTRY · HARD_DELETE_ENTRY"],
                    ["User", "CREATE_USER · DELETE_USER · HARD_DELETE_USER · BAN_USER · UNBAN_USER · SUSPEND_USER · UNSUSPEND_USER · CHANGE_USER_ROLE · CHANGE_USER_STATUS · CHANGE_USER_PASSWORD · CHANGE_USERNAME · CHANGE_USER_EMAIL · SET_VERIFIED"],
                    ["Bio", "EDIT_BIO · DELETE_BIO · BLOCK_BIO · UNBLOCK_BIO"],
                    ["IP", "BAN_IP · UNBAN_IP · DELETE_IP_BAN"],
                    ["Lock", "LOCK_TOPIC · UNLOCK_TOPIC"],
                    ["Move / Merge", "MERGE_TOPIC · MOVE_ENTRY"],
                    ["Gündem", "HARD_DELETE_TOPIC"],
                    ["Pins", "PIN_TOPIC · UNPIN_TOPIC · PIN_ENTRY · UNPIN_ENTRY"],
                ],
            },
            { type: "note", tone: "info", text: "Bulk operations write <b>one record per target</b>, and the note typed during a bulk delete is stored with it. Usernames are stored as snapshots, so records stay readable even after the user is gone." },
        ],
    },
};

// ─────────────────────────────────────────────────────────────────────────────
// GROUP 5 — SPORTS DATA
// ─────────────────────────────────────────────────────────────────────────────

const SECTION_CLUBS_TEAMS: ManualSection = {
    id: "clubs-teams",
    icon: "futbol",
    title: { tr: "Kulüp ve Takım: Kritik Kural", en: "Clubs and Teams: The Critical Rule" },
    summary: {
        tr: "Yeni kulüp eklenince mutlaka takım da eklenmeli — yoksa bulunamaz.",
        en: "A new club must always get a team — otherwise it cannot be found.",
    },
    blocks: {
        tr: [
            { type: "note", tone: "danger", text: "<b>Yeni bir kulüp eklediğinizde mutlaka en az bir takım da ekleyin.</b> Kulüp, organizasyonu temsil eder; maçlar ve listeler ise <b>takım</b> üzerinden çalışır. Takımı olmayan kulüp maça atanamaz ve pratikte “bulunamaz”." },
            { type: "sub", text: "Model" },
            {
                type: "ul", items: [
                    "<b>Kulüp</b> = spor organizasyonu (Beşiktaş JK, Real Madrid CF). Kısa ad, TR adı, EN adı, uzun ad, kısaltma ve slug tutulur.",
                    "<b>Takım</b> = kulübün spor/cinsiyet bazlı şubesi (Beşiktaş Erkek Futbol, Beşiktaş Erkek Basketbol).",
                    "Bir kulübün birden çok takımı olabilir; ilişki 1-N’dir.",
                    "Desteklenen sporlar: FOOTBALL, BASKETBALL, VOLLEYBALL, AMERICAN_FOOTBALL, BASEBALL, ICE_HOCKEY, HANDBALL.",
                ]
            },
            { type: "sub", text: "Takım kodu ve benzersizlik" },
            {
                type: "ul", items: [
                    "Bir kulübün altında aynı spor, aynı cinsiyet ve aynı takım koduyla ikinci bir takım açılamaz.",
                    "<b>Takım kodu boş bırakılırsa</b> o takım ana takımdır (A takımı). Aynı kulüp + spor + cinsiyet için <b>yalnızca bir</b> ana takım olabilir.",
                    "Ek takımlar farklı bir kod almalıdır: <code>1b</code>, <code>u19</code>, <code>paf</code> gibi. Kodlar küçük harfe çevrilir.",
                    "URL yapısı: <code>/takim/{kulüpSlug}/{sporSlug[-cinsiyet][-takımKodu]}</code> — örn. <code>/takim/besiktas/futbol</code>, <code>/takim/besiktas/futbol-kadin-u19</code>.",
                ]
            },
            { type: "sub", text: "Silme davranışı" },
            {
                type: "ul", items: [
                    "Kulüp ve takımlar <b>fiziksel olarak silinmez</b>, pasife alınır — mevcut maç kayıtları bozulmasın diye.",
                    "Kulüp silme (pasife alma) <b>sadece ADMIN</b> yetkisindedir; oluşturma ve düzenleme moderatöre de açıktır.",
                ]
            },
            { type: "note", tone: "warn", text: "Kontrol listesi — yeni kulüp eklerken: <b>1)</b> kulübü oluştur, <b>2)</b> en az bir takım oluştur (genelde erkek futbol, kod boş), <b>3)</b> gerekiyorsa diğer branşları/altyapıyı ekle, <b>4)</b> maçı bu takımlarla ilişkilendir." },
        ],
        en: [
            { type: "note", tone: "danger", text: "<b>Whenever you add a new club, always add at least one team.</b> The club represents the organisation; matches and lists work through <b>teams</b>. A club with no team cannot be assigned to a match and is effectively “not found”." },
            { type: "sub", text: "The model" },
            {
                type: "ul", items: [
                    "<b>Club</b> = the sports organisation (Beşiktaş JK, Real Madrid CF), storing short name, TR name, EN name, long name, abbreviation and slug.",
                    "<b>Team</b> = a sport/gender-specific branch of the club (Beşiktaş men’s football, Beşiktaş men’s basketball).",
                    "One club can have many teams; the relation is 1-N.",
                    "Supported sports: FOOTBALL, BASKETBALL, VOLLEYBALL, AMERICAN_FOOTBALL, BASEBALL, ICE_HOCKEY, HANDBALL.",
                ]
            },
            { type: "sub", text: "Team code and uniqueness" },
            {
                type: "ul", items: [
                    "You cannot create two teams under the same club with the same sport, gender and team code.",
                    "<b>An empty team code</b> means the main team. Only <b>one</b> main team is allowed per club + sport + gender.",
                    "Additional teams need a distinct code such as <code>1b</code>, <code>u19</code>, <code>paf</code>. Codes are lowercased.",
                    "URL shape: <code>/takim/{clubSlug}/{sportSlug[-gender][-teamCode]}</code> — e.g. <code>/takim/besiktas/futbol</code>, <code>/takim/besiktas/futbol-kadin-u19</code>.",
                ]
            },
            { type: "sub", text: "Deletion behaviour" },
            {
                type: "ul", items: [
                    "Clubs and teams are <b>never physically deleted</b>, only deactivated, so existing match records stay intact.",
                    "Deleting (deactivating) a club is <b>ADMIN only</b>; creating and editing is open to moderators too.",
                ]
            },
            { type: "note", tone: "warn", text: "Checklist when adding a club: <b>1)</b> create the club, <b>2)</b> create at least one team (usually men’s football, empty code), <b>3)</b> add other branches/youth teams if needed, <b>4)</b> link matches to those teams." },
        ],
    },
};

const SECTION_MATCHES: ManualSection = {
    id: "matches",
    icon: "futbol",
    title: { tr: "Maçlar, Kişiler ve Stadyumlar", en: "Matches, People and Stadiums" },
    summary: {
        tr: "Maç alanları, durumlar, uzatma/penaltı skorları, hakem atama.",
        en: "Match fields, statuses, ET/penalty scores, referee assignment.",
    },
    blocks: {
        tr: [
            { type: "sub", text: "Maç alanları" },
            {
                type: "ul", items: [
                    "Ev sahibi ve deplasman <b>takım</b> referansı (kulüp değil), ayrıca serbest metin takım adı alanları",
                    "Stadyum adı, maç tarihi ve saati",
                    "Normal süre skoru, <b>uzatma skoru</b>, <b>penaltı skoru</b> ve uzatma periyodu sayısı",
                    "Spor tipi (varsayılan FOOTBALL) ve cinsiyet (varsayılan MALE)",
                    "Lig, sezon, hafta/tur bilgisi",
                    "Aktif/pasif ve kilit durumu + kilit sebebi",
                ]
            },
            { type: "sub", text: "Maç durumları" },
            {
                type: "table",
                headers: ["Durum", "Anlamı"],
                rows: [
                    ["<b>SCHEDULED</b>", "Programlandı, henüz başlamadı (varsayılan)"],
                    ["<b>LIVE</b>", "Devam ediyor"],
                    ["<b>COMPLETED</b>", "Tamamlandı"],
                    ["<b>CANCELLED</b>", "Başlamadan iptal edildi"],
                    ["<b>POSTPONED</b>", "Başka tarihe ertelendi"],
                    ["<b>ABANDONED</b>", "Oyun sırasında tatil edildi (hava, saha olayları vb.)"],
                ],
            },
            { type: "sub", text: "Kişiler" },
            {
                type: "ul", items: [
                    "Futbolcu, teknik direktör, hakem, yönetici gibi kişiler tek bir <b>Kişi</b> tablosunda tutulur.",
                    "Kişinin rolü master data’daki <b>Kişi Rolleri</b> listesinden gelir.",
                    "Maçlara hakem ataması ayrı bir ilişki üzerinden yapılır ve hakem rolü (orta hakem, yardımcı, VAR vb.) belirtilir.",
                    "Her kişi aynı zamanda bir başlıktır: kilitlenebilir, sabitlenebilir, tanım alır.",
                ]
            },
            { type: "sub", text: "Stadyumlar" },
            { type: "p", text: "Stadyum kayıtlarında kapasite, zemin tipi ve konum bilgileri tutulur. Maç kaydında stadyum adı metin olarak da saklandığı için, stadyum kaydı sonradan değişse bile geçmiş maçların bilgisi bozulmaz." },
        ],
        en: [
            { type: "sub", text: "Match fields" },
            {
                type: "ul", items: [
                    "Home and away <b>team</b> references (not clubs), plus free-text team name fields",
                    "Stadium name, match date and time",
                    "Regular-time score, <b>extra-time score</b>, <b>penalty score</b> and overtime period count",
                    "Sport type (defaults to FOOTBALL) and gender (defaults to MALE)",
                    "League, season, round",
                    "Active/inactive and locked state + lock reason",
                ]
            },
            { type: "sub", text: "Match statuses" },
            {
                type: "table",
                headers: ["Status", "Meaning"],
                rows: [
                    ["<b>SCHEDULED</b>", "Scheduled, not started (default)"],
                    ["<b>LIVE</b>", "In progress"],
                    ["<b>COMPLETED</b>", "Finished"],
                    ["<b>CANCELLED</b>", "Cancelled before kick-off"],
                    ["<b>POSTPONED</b>", "Moved to another date"],
                    ["<b>ABANDONED</b>", "Abandoned during play (weather, crowd trouble, etc.)"],
                ],
            },
            { type: "sub", text: "People" },
            {
                type: "ul", items: [
                    "Players, coaches, referees and officials all live in a single <b>Person</b> table.",
                    "A person’s role comes from the <b>Person Roles</b> master data list.",
                    "Referee assignment to matches is a separate relation that also records the referee role (main, assistant, VAR, etc.).",
                    "Every person is also a topic: lockable, pinnable, accepts entries.",
                ]
            },
            { type: "sub", text: "Stadiums" },
            { type: "p", text: "Stadium records store capacity, surface type and location. The match record also keeps the stadium name as text, so historical matches stay correct even if the stadium record changes later." },
        ],
    },
};

const SECTION_MASTER_DATA: ManualSection = {
    id: "master-data",
    icon: "database",
    title: { tr: "Master Data", en: "Master Data" },
    summary: {
        tr: "Ülke, lig, hafta, kişi rolü, başlık kategorisi — silme yok, pasife alma var.",
        en: "Countries, leagues, rounds, person roles, topic categories — deactivate, never delete.",
    },
    blocks: {
        tr: [
            { type: "p", text: "Master data, arayüzdeki açılır listeleri besleyen referans verilerdir. <b>Okuma uçları herkese açıktır</b> (giriş gerekmez); yazma işlemleri <b>sadece ADMIN</b>." },
            {
                type: "table",
                headers: ["Tablo", "Kullanım yeri"],
                rows: [
                    ["<b>Ülkeler</b>", "Kulüp, kişi ve profil ülke seçimleri"],
                    ["<b>Ligler</b>", "Maç lig alanı"],
                    ["<b>Haftalar / Turlar</b>", "Maç hafta/tur alanı"],
                    ["<b>Kişi Rolleri</b>", "Kişi kaydının rolü (futbolcu, teknik direktör, hakem …)"],
                    ["<b>Başlık Kategorileri</b>", "Gündem başlıklarının <b>kanalları</b> — nickaltı kanalı da buradadır"],
                ],
            },
            { type: "note", tone: "warn", text: "<b>Silme bilinçli olarak yoktur.</b> Satırlar <code>active = false</code> yapılarak pasife alınır; aksi halde bu değerlere referans veren maç/kulüp kayıtları bozulurdu." },
            { type: "sub", text: "Sıralama mantığı" },
            { type: "p", text: "Görünüm sırası “araya ekleme” mantığıyla çalışır: bir satır <b>N</b> sırasıyla kaydedildiğinde, N ve üzerindeki mevcut satırlar önce bir kaydırılır. Böylece yeni satır temiz şekilde araya girer, çift sıra numarası oluşmaz." },
        ],
        en: [
            { type: "p", text: "Master data feeds the dropdowns across the UI. <b>Read endpoints are public</b> (no auth required); writes are <b>ADMIN only</b>." },
            {
                type: "table",
                headers: ["Table", "Used by"],
                rows: [
                    ["<b>Countries</b>", "Club, person and profile country pickers"],
                    ["<b>Leagues</b>", "Match league field"],
                    ["<b>Rounds</b>", "Match round field"],
                    ["<b>Person roles</b>", "The role of a person record (player, coach, referee …)"],
                    ["<b>Topic categories</b>", "The <b>channels</b> of Gündem topics — the nickaltı channel lives here too"],
                ],
            },
            { type: "note", tone: "warn", text: "<b>Deletion is deliberately not implemented.</b> Rows are deactivated with <code>active = false</code>; deleting them would break match/club records that reference those values." },
            { type: "sub", text: "Ordering semantics" },
            { type: "p", text: "Display order uses “insert-at-position” semantics: when a row is saved with order <b>N</b>, existing rows at N and above are shifted up by one first. The new row slots in cleanly with no duplicate positions." },
        ],
    },
};

// ─────────────────────────────────────────────────────────────────────────────
// GROUP 6 — SYSTEM
// ─────────────────────────────────────────────────────────────────────────────

const SECTION_REGISTRATION: ManualSection = {
    id: "registration",
    icon: "userShield",
    title: { tr: "Kayıt, Giriş ve Site Modu", en: "Registration, Login and Site Mode" },
    summary: {
        tr: "Kayıt açık/kapalı, varsayılan rol, e-posta doğrulama, bakım modu, duyuru.",
        en: "Registration toggle, default role, email verification, maintenance mode, announcement.",
    },
    blocks: {
        tr: [
            { type: "settings", keys: ["registration.enabled", "registration.default_role", "registration.email_verification", "auth.email_verification_token_hours", "auth.password_reset_token_hours", "auth.verification_resend_cooldown_minutes", "site.maintenance.enabled", "site.announcement.enabled", "site.announcement.text", "site.name", "site.slogan"] },
            { type: "sub", text: "Kayıt akışı" },
            {
                type: "ol", items: [
                    "Kullanıcı adı ve şifre doğrulanır (desen, uzunluk, benzersizlik).",
                    "Rol, <code>registration.default_role</code> ayarından alınır; tanınmayan değer gelirse <b>ROOKIE</b>’ye düşer.",
                    "E-posta doğrulama <b>açıksa</b> hesap <b>INACTIVE</b> açılır ve {{setting:auth.email_verification_token_hours}} saat geçerli bir doğrulama linki gönderilir. Kapalıysa hesap doğrudan <b>ACTIVE</b> olur.",
                    "Varsayılan kullanıcı tercihleri ve istatistik satırı oluşturulur.",
                    "Kullanıcının <b>nickaltı</b> başlığı otomatik açılır.",
                ]
            },
            { type: "sub", text: "Google ile giriş" },
            {
                type: "ul", items: [
                    "Google kimlik token’ı her zaman Google sunucularında doğrulanır — ham veriye güvenilmez.",
                    "Önce googleId, sonra e-posta ile hesap aranır. E-posta ile bulunursa Google kimliği o hesaba <b>bağlanır</b>.",
                    "Hesap yoksa yeni hesap açılır ve <b>doğrudan ACTIVE</b> olur (e-postayı Google zaten doğrulamıştır).",
                    "Kullanıcı adı e-postanın @ öncesinden türetilir, desene uydurulur, kısa kalırsa doldurulur, çakışırsa sayı eklenir.",
                    "Askı/yasak/dondurma kontrolleri <b>aynen uygulanır</b> — Google girişi hiçbir kısıtlamayı atlatmaz.",
                ]
            },
            { type: "sub", text: "Şifre" },
            {
                type: "ul", items: [
                    "Minimum {{const:password_min}}, maksimum {{const:password_max}} karakter.",
                    "Şifre sıfırlama linki tek kullanımlıktır ve {{setting:auth.password_reset_token_hours}} saat sonra geçersizleşir.",
                    "Doğrulama e-postası tekrar istendiğinde aynı hesap için {{setting:auth.verification_resend_cooldown_minutes}} dakika beklenir — bu süre gelen kutusunun arka arkaya doldurulmasını engeller. 0 girilirse bekleme tamamen kapanır.",
                    "Hesap e-posta doğrulaması beklerken (INACTIVE) başarılı bir şifre sıfırlama, doğrulama sayılır ve hesabı <b>otomatik aktifleştirir</b>.",
                ]
            },
            { type: "note", tone: "warn", text: "<b>Bakım modu</b> açıldığında site ziyaretçilere bakım sayfası gösterir; admin dışındaki herkes için <b>tüm adresler</b> (ana sayfa, /admin dahil) bakım sayfasına düşer ve o sırada giriş yapmış kullanıcıların <b>oturumu kapatılır</b>, sebebi ekranda yazar. <b>Kilitli kalırsanız kapatmak için:</b> <code>/login</code> adresine gidin — bakım modunda giriş formu <b>yalnızca bu adreste</b> açılır, bakım sayfasında hiçbir düğme yoktur — admin hesabıyla girin, site normal açılır, sonra <b>Ayarlar → Genel → Bakım Modu</b> kapatın. Admin olmayan bir hesapla girilirse oturum anında kapatılır. <b>Duyuru</b> açıldığında duyuru metni tüm kullanıcılara banner olarak görünür — metin boş bırakılırsa banner anlamsız görünür, mutlaka doldurun." },
        ],
        en: [
            { type: "settings", keys: ["registration.enabled", "registration.default_role", "registration.email_verification", "auth.email_verification_token_hours", "auth.password_reset_token_hours", "auth.verification_resend_cooldown_minutes", "site.maintenance.enabled", "site.announcement.enabled", "site.announcement.text", "site.name", "site.slogan"] },
            { type: "sub", text: "Sign-up flow" },
            {
                type: "ol", items: [
                    "Username and password are validated (pattern, length, uniqueness).",
                    "The role comes from <code>registration.default_role</code>; an unrecognised value falls back to <b>ROOKIE</b>.",
                    "If email verification is <b>on</b>, the account is created <b>INACTIVE</b> with a verification link valid for {{setting:auth.email_verification_token_hours}} hours. If off, it is <b>ACTIVE</b> immediately.",
                    "Default preferences and a stats row are created.",
                    "The user’s <b>nickaltı</b> topic is created automatically.",
                ]
            },
            { type: "sub", text: "Google sign-in" },
            {
                type: "ul", items: [
                    "The Google ID token is always verified against Google’s servers — the raw payload is never trusted.",
                    "Lookup is by googleId first, then by email. If found by email, the Google identity is <b>linked</b> to that account.",
                    "If no account exists, a new one is created and is <b>ACTIVE immediately</b> (Google already verified the email).",
                    "The username is derived from the email local part, sanitised to the pattern, padded if too short and suffixed on collision.",
                    "Suspension/ban/freeze checks run <b>identically</b> — Google sign-in can never bypass a restriction.",
                ]
            },
            { type: "sub", text: "Passwords" },
            {
                type: "ul", items: [
                    "Minimum {{const:password_min}}, maximum {{const:password_max}} characters.",
                    "Reset links are single-use and expire after {{setting:auth.password_reset_token_hours}} hours.",
                    "Asking for another verification e-mail is refused for {{setting:auth.verification_resend_cooldown_minutes}} minutes per account, which stops the button being used to flood an inbox. Set it to 0 to disable the wait entirely.",
                    "If the account is INACTIVE awaiting email verification, a successful password reset counts as confirmation and <b>auto-activates</b> it.",
                ]
            },
            { type: "note", tone: "warn", text: "<b>Maintenance mode</b> shows visitors a maintenance page; for everyone except admins <b>every route</b> (home page and /admin included) falls back to that page, and any signed-in session is ended with an on-screen explanation. <b>If you lock yourself out:</b> go to <code>/login</code> — during maintenance the login form opens <b>only</b> on that path, the notice page itself has no buttons — sign in as admin, the normal site returns, then set <b>Settings → General → Maintenance Mode</b> to off. Signing in with a non-admin account ends that session immediately. <b>Announcement</b> shows the announcement text as a banner to all users — leaving the text empty makes the banner meaningless, so always fill it in." },
        ],
    },
};

const SECTION_MEDIA_SEARCH: ManualSection = {
    id: "media-search",
    icon: "image",
    title: { tr: "Medya, Arama ve İstatistikler", en: "Media, Search and Statistics" },
    summary: {
        tr: "Yükleme limitleri, arama eşikleri, istatistik sayfası.",
        en: "Upload limits, search thresholds, statistics page.",
    },
    blocks: {
        tr: [
            { type: "sub", text: "Medya yükleme limitleri" },
            { type: "settings", keys: ["media.avatar_max_mb", "media.banner_max_mb", "media.entry_image_max_mb"] },
            { type: "p", text: "Medya dosyaları sunucuda ortak ve ortam bazlı dizinlerde tutulur, ters vekil sunucu üzerinden <code>/media/</code> yolundan servis edilir. Ön yüzde tüm medya yolları, kök göreli yolları API adresiyle önekleyen yardımcı fonksiyondan geçirilmelidir." },
            { type: "sub", text: "Arama" },
            { type: "settings", keys: ["search.typeahead_min_chars", "search.typeahead_delay_ms", "search.result_limit"] },
            {
                type: "ul", items: [
                    "Arama kutusu {{setting:search.typeahead_min_chars}} karakterden sonra tetiklenir ve {{setting:search.typeahead_delay_ms}} ms geciktirilir.",
                    "Açılır menüde kategori başına {{setting:search.result_limit}} sonuç gösterilir.",
                    "Tam sonuç sayfasında kategori başına {{const:search_full_max}} sonuca kadar listelenir.",
                    "Tanım sonuçlarında içerik {{const:search_snippet}} karakterde kısaltılır.",
                    "Aranan kategoriler: kullanıcı, tanım, maç, kulüp, takım, kişi, stadyum, lig ve gündem başlığı.",
                    "<b>Filtreleme her zaman sunucu tarafında yapılır</b> — sayfalama sayaçları doğru kalsın diye.",
                ]
            },
            { type: "sub", text: "İstatistik sayfası" },
            { type: "p", text: "Herkese açık istatistik sayfası genel toplamları, en çok tanım giren yazarları, karma sıralamasını, en çok konuşulan kulüp/takım/kişileri, ülke dağılımını ve taraftar kulübü dağılımını gösterir. Admin panelindeki Panel sekmesi ise yönetimsel KPI’ları ve dönemsel grafikleri gösterir." },
        ],
        en: [
            { type: "sub", text: "Media upload limits" },
            { type: "settings", keys: ["media.avatar_max_mb", "media.banner_max_mb", "media.entry_image_max_mb"] },
            { type: "p", text: "Media files live in shared and per-environment directories on the server and are served from the <code>/media/</code> path through the reverse proxy. On the frontend every media path must go through the resolver helper that prefixes root-relative paths with the API base URL." },
            { type: "sub", text: "Search" },
            { type: "settings", keys: ["search.typeahead_min_chars", "search.typeahead_delay_ms", "search.result_limit"] },
            {
                type: "ul", items: [
                    "Typeahead fires after {{setting:search.typeahead_min_chars}} characters, debounced by {{setting:search.typeahead_delay_ms}} ms.",
                    "The dropdown shows {{setting:search.result_limit}} results per category.",
                    "The full results page lists up to {{const:search_full_max}} results per category.",
                    "Entry results are truncated at {{const:search_snippet}} characters.",
                    "Searched categories: users, entries, matches, clubs, teams, people, stadiums, leagues and Gündem topics.",
                    "<b>Filtering is always done server-side</b> so pagination counts stay correct.",
                ]
            },
            { type: "sub", text: "Statistics page" },
            { type: "p", text: "The public statistics page shows overall totals, top authors by entry count, karma rankings, most-discussed clubs/teams/people, country distribution and fan-club distribution. The admin Dashboard tab covers operational KPIs and period charts instead." },
        ],
    },
};

const SECTION_LEGAL: ManualSection = {
    id: "legal",
    icon: "gavel",
    title: { tr: "Yasal Metinler ve Çerez Onayı", en: "Legal Texts and Cookie Consent" },
    summary: {
        tr: "Kullanım koşulları, gizlilik ve çerez politikası; kayıt onayı ve çerez bandı.",
        en: "Terms, privacy and cookie policy; registration consent and the cookie bar.",
    },
    blocks: {
        tr: [
            { type: "p", text: "Platform dört yasal metin yayımlar. Metinler veritabanında tutulur ve <b>Yasal Metinler</b> sekmesinden düzenlenir; deploy gerektirmez." },
            {
                type: "table",
                headers: ["Metin", "Adres", "Ne işe yarar"],
                rows: [
                    ["Kullanım Koşulları", "<code>/kullanim-kosullari</code>", "Kullanıcı sözleşmesi. Kayıt sırasında onaylanır."],
                    ["Hamur Abi Kuralları", "<code>/hamur-abi-kurallari</code>", "Topluluk kuralları: format, üslup, çaylaklık, moderasyon."],
                    ["Gizlilik Politikası", "<code>/gizlilik-politikasi</code>", "Hangi kişisel verinin ne kadar saklandığı."],
                    ["Çerez Politikası", "<code>/cerez-politikasi</code>", "Çerez ve yerel depolama kullanımı."],
                    ["Çerez Tercihleri", "<code>/cerez-tercihleri</code>", "Kullanıcının onayını değiştirdiği sayfa."],
                ],
            },
            { type: "p", text: "Beşi de footer’dan her sayfada erişilebilir. Her metnin <b>Türkçe</b> ve <b>İngilizce</b> sürümü ayrı satırdır; kullanıcının arayüz diline göre servis edilir, o dilde yoksa Türkçe’ye düşer." },

            { type: "sub", text: "Metinlerdeki değişkenler" },
            { type: "p", text: "Metinlerde sayı ve adres <b>asla</b> elle yazılmaz. Bunun yerine <code>{{setting:legal.company.name}}</code> gibi değişkenler kullanılır; metin yayınlanırken canlı ayar değeriyle değiştirilir. Böylece ayarı değiştirdiğinizde yasal metin de kendiliğinden güncellenir." },
            {
                type: "ul", items: [
                    "<code>{{setting:...}}</code> — herhangi bir sistem ayarının güncel değeri.",
                    "<code>{{const:ACCOUNT_DELETION_GRACE_DAYS}}</code> — koddaki sabitler (yöneticinin değiştiremediği değerler).",
                    "Karşılığı boş olan bir değişken <b>kısa çizgi (—)</b> olarak görünür. Editördeki değişken listesinden kopyalayın, elle yazmayın.",
                    "<code>{{if:anahtar=deger}}...{{else}}...{{endif}}</code> — koşullu blok. <code>{{else}}</code> isteğe bağlıdır, <code>!=</code> da desteklenir, iç içe kullanılamaz.",
                ]
            },
            { type: "sub", text: "0 = sınırsız olan ayarlar" },
            { type: "p", text: "Üç ayarda <b>0 değeri “sınır yok”</b> demektir. Bu alanlarda sayıyı düz yazmak metni yanlış yapar — “günlük tanım hakkınız 0 adettir” cümlesi kodun yaptığının tam tersini söyler. Bu yüzden Kullanım Koşulları’nda ilgili cümleler koşullu bloklar hâlinde yazılıdır ve doğru cümle yayın anında kendiliğinden seçilir." },
            {
                type: "table",
                headers: ["Ayar", "0 iken ne olur", "Metinde"],
                rows: [
                    ["<code>entry.daily_limit</code>", "Günlük tanım sınırı yoktur", "“Günlük tanım sayınızda sınır yoktur” cümlesine döner"],
                    ["<code>entry.edit_window_minutes</code>", "Tanım her zaman düzenlenebilir", "“Süre sınırı olmaksızın düzenleyebilirsiniz” cümlesine döner"],
                    ["<code>moderation.report_cooldown_days</code>", "Aynı tanım bir daha hiç şikâyet edilemez", "“Yalnızca bir kez şikâyet edebilirsiniz” cümlesine döner"],
                ],
            },
            { type: "note", tone: "ok", text: "Bu ayarları değiştirdiğinizde yasal metne <b>dokunmanız gerekmez</b>. Metni kendiniz yazarken 0’ın sınırsız anlamına geldiği bir ayardan söz ediyorsanız, sayıyı düz yazmak yerine koşullu blok kullanın." },
            { type: "settings", keys: ["legal.company.name", "legal.site.domain", "legal.kvkk.email", "legal.kvkk.kep", "legal.jurisdiction", "legal.min_age", "legal.content_retention_years", "legal.log_retention_years", "legal.cookie_banner.enabled"] },
            { type: "note", tone: "warn", text: "Boş bırakılan <b>legal.*</b> ayarları yayındaki metinde kısa çizgi olarak görünür. Yayına açmadan önce şirket unvanı, alan adı, KVKK e-posta ve KEP adresini doldurun." },

            { type: "sub", text: "Sürüm numarası neden önemli?" },
            {
                type: "ul", items: [
                    "Bir kullanıcı kayıt olurken kendisine gösterilen sürüm <code>users.terms_version</code> alanına yazılır.",
                    "Metni <b>esaslı</b> şekilde değiştirdiğinizde sürümü yükseltin — böylece eski sözleşmeyi kabul etmiş kullanıcılar sorgulanabilir hale gelir.",
                    "Çerez Politikası sürümünü yükselttiğinizde tüm ziyaretçilerin çerez tercihi <b>sıfırlanır</b> ve onay yeniden istenir.",
                    "Yazım hatası düzeltmesi gibi küçük değişikliklerde sürümü artırmayın; gereksiz yere herkese yeniden onay sordurursunuz.",
                    "Metni değiştirip sürümü yükseltmezseniz editör sizi uyarır.",
                ]
            },
            { type: "note", tone: "info", text: "<b>Yayında</b> kutusunu kapatırsanız metin düzenlenebilir kalır ama ziyaretçiye gösterilmez; sayfa “görüntülenemiyor” der. Taslak hazırlarken kullanışlıdır." },

            { type: "sub", text: "Kayıt onayı" },
            {
                type: "ul", items: [
                    "Kayıt formunda <b>iki ayrı</b> onay kutusu vardır: yaş beyanı ve sözleşme onayı.",
                    "İkisi de zorunludur. Eksikse sunucu kaydı reddeder — kontrol sadece tarayıcıda değildir.",
                    "Onay tarihi, kabul edilen metin sürümü ve yaş beyanı kullanıcı kaydına yazılır.",
                    "<b>Google ile giriş</b> yeni hesap açacaksa aynı onay istenir; mevcut hesaba giriş etkilenmez.",
                    "Bu özellik gelmeden önce açılmış hesaplarda onay alanları <b>boştur</b> — bilerek geriye dönük doldurulmadı.",
                ]
            },

            { type: "sub", text: "Çerez bandı" },
            {
                type: "ul", items: [
                    "Ziyaretçi karar verene kadar sayfanın altında durur. Seçenekler: <b>Kabul et</b>, <b>Reddet</b>, <b>Tercihleri yönet</b>.",
                    "<b>Zorunlu kayıtlar</b> kapatılamaz (oturum, dil, tema, tercihler).",
                    "<b>Ölçümleme</b> onayı verilmeden Google Analytics betiği tarayıcıya hiç yüklenmez. Onay geri çekilirse ölçüm durur.",
                    "<b>Reklam</b> onayı verilmezse reklamlar gösterilmeye devam edebilir ama kişiselleştirilmez.",
                    "Seçim tarayıcının yerel deposunda saklanır — platform kendi alan adından çerez yazmaz.",
                    "<code>legal.cookie_banner.enabled</code> kapatılırsa bant hiç görünmez ve isteğe bağlı hiçbir betik çalışmaz.",
                ]
            },
            { type: "note", tone: "danger", text: "Çerez metninde “onay vermeden çalışmaz” yazıyorsa bu kodda da böyledir. Ölçümleme veya reklam betiğini banttan bağımsız olarak <b>index.html</b>’e elle eklerseniz metni yalan çıkarırsınız." },
        ],
        en: [
            { type: "p", text: "The platform publishes four legal documents. They live in the database and are edited from the <b>Legal Texts</b> tab; no deploy is needed." },
            {
                type: "table",
                headers: ["Document", "Address", "Purpose"],
                rows: [
                    ["Terms of Use", "<code>/kullanim-kosullari</code>", "The user agreement, accepted at registration."],
                    ["Hamur Abi Rules", "<code>/hamur-abi-kurallari</code>", "Community rules: format, tone, rookie status, moderation."],
                    ["Privacy Policy", "<code>/gizlilik-politikasi</code>", "What personal data is kept and for how long."],
                    ["Cookie Policy", "<code>/cerez-politikasi</code>", "Use of cookies and local storage."],
                    ["Cookie Preferences", "<code>/cerez-tercihleri</code>", "Where the user changes their consent."],
                ],
            },
            { type: "p", text: "All five are reachable from the footer on every page. Each document has a separate <b>Turkish</b> and <b>English</b> row, served by interface language and falling back to Turkish when a translation is missing." },

            { type: "sub", text: "Variables inside the texts" },
            { type: "p", text: "Numbers and addresses are <b>never</b> hardcoded in the prose. Use a variable such as <code>{{setting:legal.company.name}}</code> instead; it is replaced with the live setting value when the text is served, so changing a setting also updates the published document." },
            {
                type: "ul", items: [
                    "<code>{{setting:...}}</code> — the current value of any system setting.",
                    "<code>{{const:ACCOUNT_DELETION_GRACE_DAYS}}</code> — backend constants the admin cannot configure.",
                    "A variable with no value renders as a <b>dash (—)</b>. Copy from the variable list in the editor rather than typing by hand.",
                    "<code>{{if:key=value}}...{{else}}...{{endif}}</code> — conditional block. <code>{{else}}</code> is optional, <code>!=</code> is supported, nesting is not.",
                ]
            },
            { type: "sub", text: "Settings where 0 means unlimited" },
            { type: "p", text: "In three settings <b>0 means “no limit”</b>. Printing the number in prose makes the text false — “your daily allowance is 0 entries” says the opposite of what the code does. The affected sentences in the Terms of Use are therefore written as conditional blocks, and the correct wording is chosen automatically when the text is served." },
            {
                type: "table",
                headers: ["Setting", "Behaviour at 0", "In the text"],
                rows: [
                    ["<code>entry.daily_limit</code>", "No daily entry limit", "Switches to “there is no limit on entries per day”"],
                    ["<code>entry.edit_window_minutes</code>", "Entries stay editable forever", "Switches to “you can edit at any time”"],
                    ["<code>moderation.report_cooldown_days</code>", "An entry can never be re-reported", "Switches to “can only be reported once”"],
                ],
            },
            { type: "note", tone: "ok", text: "You do <b>not</b> need to touch the legal text when changing these settings. When writing your own copy about a setting where 0 means unlimited, use a conditional block instead of printing the number." },
            { type: "settings", keys: ["legal.company.name", "legal.site.domain", "legal.kvkk.email", "legal.kvkk.kep", "legal.jurisdiction", "legal.min_age", "legal.content_retention_years", "legal.log_retention_years", "legal.cookie_banner.enabled"] },
            { type: "note", tone: "warn", text: "Empty <b>legal.*</b> settings appear as a dash in the published text. Fill in the company name, domain, data protection email and registered mail address before going live." },

            { type: "sub", text: "Why the version number matters" },
            {
                type: "ul", items: [
                    "The version shown to a user at registration is written to <code>users.terms_version</code>.",
                    "Raise the version after a <b>material</b> change, so users still on the old agreement can be identified.",
                    "Raising the Cookie Policy version <b>resets</b> every visitor's cookie choice and asks again.",
                    "Do not raise it for a typo fix; you would re-prompt everyone for nothing.",
                    "The editor warns you if the body changed but the version did not.",
                ]
            },
            { type: "note", tone: "info", text: "Unchecking <b>Published</b> keeps the document editable but hides it from visitors, who then see a “cannot be displayed” page. Useful while drafting." },

            { type: "sub", text: "Registration consent" },
            {
                type: "ul", items: [
                    "The sign-up form has <b>two separate</b> checkboxes: age declaration and terms acceptance.",
                    "Both are required. The server rejects the registration when either is missing — the check is not browser-only.",
                    "The consent timestamp, the accepted text version and the age declaration are stored on the user record.",
                    "<b>Google sign-in</b> asks for the same consent when it would create a new account; signing into an existing account is unaffected.",
                    "Accounts created before this feature have <b>empty</b> consent fields — deliberately not backfilled.",
                ]
            },

            { type: "sub", text: "The cookie bar" },
            {
                type: "ul", items: [
                    "Stays at the bottom of the page until the visitor decides. Options: <b>Accept</b>, <b>Reject</b>, <b>Manage preferences</b>.",
                    "<b>Strictly necessary</b> records cannot be switched off (session, language, theme, preferences).",
                    "Without <b>measurement</b> consent the Google Analytics script is never loaded. Withdrawing consent stops measurement.",
                    "Without <b>advertising</b> consent ads may still be shown, but are not personalised.",
                    "The choice is kept in browser local storage — the platform writes no cookies from its own domain.",
                    "Turning off <code>legal.cookie_banner.enabled</code> hides the bar entirely and leaves every optional script disabled.",
                ]
            },
            { type: "note", tone: "danger", text: "If the cookie text says something does not run without consent, that must remain true in code. Adding a measurement or advertising script directly to <b>index.html</b>, bypassing the bar, makes the published text false." },
        ],
    },
};

const SECTION_SETTINGS_REF: ManualSection = {
    id: "settings-ref",
    icon: "cogs",
    title: { tr: "Tüm Sistem Ayarları (Canlı Değerler)", en: "All System Settings (Live Values)" },
    summary: {
        tr: "Ayarlar sekmesindeki her anahtarın anlık değeri ve son değiştiren.",
        en: "Current value and last editor for every key in the Settings tab.",
    },
    blocks: {
        tr: [
            { type: "p", text: "Aşağıdaki tablo, veritabanındaki <b>canlı</b> ayar değerlerini gösterir. Ayarlar sekmesinden bir değeri değiştirip bu sayfayı yenilediğinizde yeni değer burada da görünür — kılavuz ile gerçek yapılandırma <b>asla ayrışmaz</b>." },
            { type: "note", tone: "info", text: "Ayar değişiklikleri önbelleğe yazılır ve <b>anında</b> geçerli olur; uygulamayı yeniden başlatmaya gerek yoktur." },
            { type: "sub", text: "Genel" },
            { type: "settings", keys: ["site.name", "site.slogan", "site.maintenance.enabled", "site.announcement.enabled", "site.announcement.text"] },
            { type: "sub", text: "Kayıt ve kullanıcı" },
            { type: "settings", keys: ["registration.enabled", "registration.default_role", "registration.email_verification", "user.bio.max_length", "user.username.max_length"] },
            { type: "sub", text: "Kimlik doğrulama ve e-posta linkleri" },
            { type: "settings", keys: ["auth.email_verification_token_hours", "auth.password_reset_token_hours", "auth.verification_resend_cooldown_minutes"] },
            { type: "sub", text: "Tanım ve başlık kuralları" },
            { type: "settings", keys: ["entry.min_length", "entry.max_length", "entry.daily_limit", "entry.edit_window_minutes", "entry.daily_edit_limit", "entry.draft_limit", "entry.preview_line_limit", "topic.title.max_length", "topic.suggestion.cooldown_seconds", "topic.suggestion.daily_limit"] },
            { type: "sub", text: "Moderasyon" },
            { type: "settings", keys: ["moderation.auto_ban_report_threshold", "moderation.report_cooldown_days", "moderation.spam_cooldown_seconds", "moderation.default_ip_ban_days"] },
            { type: "sub", text: "Mesajlaşma" },
            { type: "settings", keys: ["messaging.max_length", "messaging.inbox_limit", "messaging.rate_limit_max_messages", "messaging.rate_limit_window_seconds"] },
            { type: "sub", text: "Arama" },
            { type: "settings", keys: ["search.typeahead_min_chars", "search.typeahead_delay_ms", "search.result_limit"] },
            { type: "sub", text: "Medya" },
            { type: "settings", keys: ["media.avatar_max_mb", "media.banner_max_mb", "media.entry_image_max_mb"] },
            { type: "sub", text: "Ayarlanamayan sabitler" },
            { type: "p", text: "Aşağıdakiler kodda tanımlıdır ve panelden değiştirilemez:" },
            {
                type: "table",
                headers: ["Sabit", "Değer"],
                rows: [
                    ["Kullanıcı adı minimum uzunluk", "{{const:username_min}}"],
                    ["Kullanıcı adı mutlak tavan", "{{const:username_max}}"],
                    ["Şifre minimum uzunluk", "{{const:password_min}}"],
                    ["Şifre maksimum uzunluk", "{{const:password_max}}"],
                    ["Pasif sayılma eşiği", "{{const:inactive_days}} gün"],
                    ["Çaylaklıktan çıkış referans eşiği", "{{const:rookie_min_entries}} tanım / {{const:rookie_min_days}} gün"],
                    ["Hesap silme bekleme süresi", "{{const:deletion_grace_days}} gün"],
                    ["Silme talebi iptal kilidi", "{{const:deletion_cancel_cooldown_hours}} saat"],
                    ["Dondurma iptal kilidi", "{{const:freeze_cooldown_hours}} saat"],
                    ["Silme işi programı", "{{setting:scheduler.account_deletion.cron.human}}"],
                    ["Oy hız limiti", "{{const:vote_window}} sn içinde {{const:vote_max}} oy, {{const:vote_cooldown}} sn bekleme"],
                    ["Mesaj hız limiti", "{{setting:messaging.rate_limit_window_seconds}} sn içinde {{setting:messaging.rate_limit_max_messages}} mesaj (0 = sınırsız)"],
                    ["Başlık başına azami kanal", "{{const:max_channels}}"],
                    ["Gündemde gösterilen kendi tanım listesi", "en yeni {{const:own_topic_entries_max}} tanım"],
                    ["Birleştirme zincir derinliği", "{{const:merge_chain_depth}}"],
                    ["Şikayet metni azami uzunluk", "{{const:report_text_max}} karakter"],
                ],
            },
        ],
        en: [
            { type: "p", text: "The table below shows the <b>live</b> values stored in the database. Change something in the Settings tab, refresh this page, and the new value appears here too — the manual and the real configuration <b>never drift apart</b>." },
            { type: "note", tone: "info", text: "Setting changes are written to the cache and take effect <b>immediately</b>; no restart is required." },
            { type: "sub", text: "General" },
            { type: "settings", keys: ["site.name", "site.slogan", "site.maintenance.enabled", "site.announcement.enabled", "site.announcement.text"] },
            { type: "sub", text: "Registration and users" },
            { type: "settings", keys: ["registration.enabled", "registration.default_role", "registration.email_verification", "user.bio.max_length", "user.username.max_length"] },
            { type: "sub", text: "Authentication and e-mail links" },
            { type: "settings", keys: ["auth.email_verification_token_hours", "auth.password_reset_token_hours", "auth.verification_resend_cooldown_minutes"] },
            { type: "sub", text: "Entry and topic rules" },
            { type: "settings", keys: ["entry.min_length", "entry.max_length", "entry.daily_limit", "entry.edit_window_minutes", "entry.daily_edit_limit", "entry.draft_limit", "entry.preview_line_limit", "topic.title.max_length", "topic.suggestion.cooldown_seconds", "topic.suggestion.daily_limit"] },
            { type: "sub", text: "Moderation" },
            { type: "settings", keys: ["moderation.auto_ban_report_threshold", "moderation.report_cooldown_days", "moderation.spam_cooldown_seconds", "moderation.default_ip_ban_days"] },
            { type: "sub", text: "Messaging" },
            { type: "settings", keys: ["messaging.max_length", "messaging.inbox_limit", "messaging.rate_limit_max_messages", "messaging.rate_limit_window_seconds"] },
            { type: "sub", text: "Search" },
            { type: "settings", keys: ["search.typeahead_min_chars", "search.typeahead_delay_ms", "search.result_limit"] },
            { type: "sub", text: "Media" },
            { type: "settings", keys: ["media.avatar_max_mb", "media.banner_max_mb", "media.entry_image_max_mb"] },
            { type: "sub", text: "Non-configurable constants" },
            { type: "p", text: "These are defined in code and cannot be changed from the panel:" },
            {
                type: "table",
                headers: ["Constant", "Value"],
                rows: [
                    ["Username minimum length", "{{const:username_min}}"],
                    ["Username hard ceiling", "{{const:username_max}}"],
                    ["Password minimum length", "{{const:password_min}}"],
                    ["Password maximum length", "{{const:password_max}}"],
                    ["Inactivity threshold", "{{const:inactive_days}} days"],
                    ["Rookie promotion reference thresholds", "{{const:rookie_min_entries}} entries / {{const:rookie_min_days}} days"],
                    ["Account deletion grace period", "{{const:deletion_grace_days}} days"],
                    ["Deletion cancel lockout", "{{const:deletion_cancel_cooldown_hours}} hours"],
                    ["Freeze cancel lockout", "{{const:freeze_cooldown_hours}} hours"],
                    ["Deletion job schedule", "{{setting:scheduler.account_deletion.cron.human}}"],
                    ["Vote rate limit", "{{const:vote_max}} votes per {{const:vote_window}}s, {{const:vote_cooldown}}s cooldown"],
                    ["Message rate limit", "{{setting:messaging.rate_limit_max_messages}} messages per {{setting:messaging.rate_limit_window_seconds}}s (0 = unlimited)"],
                    ["Max channels per topic", "{{const:max_channels}}"],
                    ["Own entry list on Gündem pages", "newest {{const:own_topic_entries_max}} entries"],
                    ["Merge chain depth cap", "{{const:merge_chain_depth}}"],
                    ["Report text max length", "{{const:report_text_max}} chars"],
                ],
            },
        ],
    },
};


// ─────────────────────────────────────────────────────────────────────────────
// TURKISH LABELS FOR SYSTEM SETTINGS
//
// The `description` column in system_settings is seeded in English. The manual
// is read in Turkish most of the time, so AdminManual.tsx prefers the Turkish
// text below and only falls back to the DB description when a key is missing
// here. Add an entry whenever a new setting key is introduced.
// ─────────────────────────────────────────────────────────────────────────────

export const SETTING_LABELS_TR: Record<string, string> = {
    // General
    "site.name": "Sitenin görünen adı",
    "site.slogan": "Logonun altında görünen slogan",
    "site.maintenance.enabled": "Bakım modu — açıkken ziyaretçiler bakım sayfası görür, açık oturumlar kapatılır (kilitli kalırsanız /login adresinden admin girişi yapın)",
    "site.announcement.enabled": "Duyuru bandını tüm kullanıcılara göster",
    "site.announcement.text": "Duyuru bandında yazan metin",

    // Legal
    "legal.company.name": "Yasal metinlerde görünen işletmeci/şirket unvanı",
    "legal.site.domain": "Yasal metinlerde görünen alan adı",
    "legal.kvkk.email": "KVKK başvurularının gönderileceği e-posta adresi",
    "legal.kvkk.kep": "Yasal tebligat için kayıtlı elektronik posta (KEP) adresi",
    "legal.jurisdiction": "Uyuşmazlıklarda yetkili mahkeme ve icra dairesinin bulunduğu il",
    "legal.min_age": "Kayıt olmak için gereken asgari yaş",
    "legal.content_retention_years": "Silinen içeriğin arşivde tutulacağı yıl sayısı",
    "legal.log_retention_years": "Erişim kayıtlarının 5651 kapsamında saklanacağı yıl sayısı",
    "legal.cookie_banner.enabled": "Ziyaretçilere çerez onay bandını göster",

    // Registration & user
    "registration.enabled": "Yeni kullanıcı kaydına izin ver",
    "registration.default_role": "Yeni kayıt olan kullanıcıya verilen rol",
    "registration.email_verification": "Giriş öncesi e-posta doğrulamasını zorunlu tut",
    "user.bio.max_length": "Biyografi için azami karakter sayısı",
    "user.username.max_length": "Kullanıcı adı için azami karakter sayısı (mutlak tavan 50)",

    // Authentication — validity of the mailed one-time links
    "auth.email_verification_token_hours": "Doğrulama linkinin kaç saat geçerli kalacağı (1–168)",
    "auth.password_reset_token_hours": "Şifre sıfırlama linkinin kaç saat geçerli kalacağı (1–72)",
    "auth.verification_resend_cooldown_minutes": "İki doğrulama e-postası arasındaki en az süre (dakika, 0 = kapalı)",

    // Entry rules
    "entry.min_length": "Bir tanımın en az kaç karakter olması gerektiği",
    "entry.max_length": "Bir tanımın en fazla kaç karakter olabileceği",
    "entry.daily_limit": "Bir kullanıcının günde girebileceği azami tanım sayısı (0 = sınırsız)",
    "entry.edit_window_minutes": "Yazarın tanımını girdikten sonra kaç dakika düzenleyebileceği (0 = süresiz)",
    "entry.daily_edit_limit": "Bir kullanıcının günde yapabileceği azami tanım düzenleme sayısı (0 = sınırsız)",
    "entry.draft_limit": "Bir kullanıcının tutabileceği azami taslak sayısı",
    "entry.preview_line_limit": "“Devamını göster” çıkmadan önce gösterilen azami satır sayısı (0 = kısaltma yok)",

    // Topics
    "topic.title.max_length": "Gündem başlığı için azami karakter sayısı (50–80 arası)",
    "topic.suggestion.cooldown_seconds": "İki başlık önerisi arasında beklenmesi gereken en az süre (saniye)",
    "topic.suggestion.daily_limit": "Bir kullanıcının günde yapabileceği azami başlık önerisi",

    // Moderation
    "moderation.auto_ban_report_threshold": "Bir tanımda inceleme bayrağını tetikleyen şikayet sayısı",
    "moderation.report_cooldown_days": "Aynı tanımın tekrar şikayet edilebilmesi için geçmesi gereken gün",
    "moderation.spam_cooldown_seconds": "Aynı kullanıcının iki tanımı arasındaki en az süre (saniye, 0 = kapalı)",
    "moderation.default_ip_ban_days": "Varsayılan IP yasağı süresi (gün, 0 = kalıcı)",

    // Messaging
    "messaging.max_length": "Bir mesajın azami karakter sayısı",
    "messaging.inbox_limit": "Bir konuşmada saklanan azami mesaj sayısı (0 = sınırsız)",
    "messaging.rate_limit_max_messages": "Bir kullanıcının hız limiti penceresi içinde gönderebileceği azami mesaj sayısı (0 = sınırsız)",
    "messaging.rate_limit_window_seconds": "Hız limitinin ölçüldüğü zaman aralığı (saniye)",

    // Search
    "search.typeahead_min_chars": "Aramanın çalışması için gereken en az karakter sayısı",
    "search.typeahead_delay_ms": "Arama önerileri için gecikme süresi (milisaniye)",
    "search.result_limit": "Açılır aramada kategori başına gösterilen sonuç sayısı",

    // Media
    "media.avatar_max_mb": "Azami avatar yükleme boyutu (MB)",
    "media.banner_max_mb": "Azami profil kapak görseli boyutu (MB)",
    "media.entry_image_max_mb": "Tanım içi görseller için azami boyut (MB)",

    // Scheduler — the cron expressions behind the background jobs
    "scheduler.timezone": "Zamanlanmış işlerin saat dilimi",
    "scheduler.account_deletion.cron": "Hesap silme işinin çalışma programı (cron)",
    "scheduler.account_deletion.enabled": "Hesap silme işi açık mı",
    "scheduler.suspension_expiry.cron": "Askı bitişi işinin çalışma programı (cron)",
    "scheduler.suspension_expiry.enabled": "Askı bitişi işi açık mı",

    // Contact
    "contact.email.general": "Genel iletişim e-posta adresi",
    "contact.email.advertising": "Reklam ve iş birliği e-posta adresi",
    "contact.email.complaint": "Şikayet ve kötüye kullanım e-posta adresi",
    "contact.email.press": "Basın ve medya e-posta adresi",
    "contact.email.developer": "Geliştirici / API e-posta adresi",
    "contact.phone": "Herkese açık telefon numarası",
    "contact.address": "Fiziksel veya posta adresi",
    "contact.working_hours": "Çalışma saatleri metni",
    "contact.response_time": "Beklenen yanıt süresi metni",
    "contact.social.x": "X profil adresi",
    "contact.social.instagram": "Instagram profil adresi",
    "contact.social.youtube": "YouTube kanal adresi",
    "contact.social.tiktok": "TikTok profil adresi",
    "contact.url.privacy": "Gizlilik politikası sayfa adresi",
    "contact.url.terms": "Kullanım şartları sayfa adresi",
};

/**
 * Human-readable description of a setting key in the requested language.
 * Turkish prefers the curated label above; English falls back to the
 * DB-provided description, which is already written in English.
 */
export const describeSetting = (
    key: string,
    lang: ManualLang,
    dbDescription?: string | null
): string => {
    if (lang === "tr") {
        return SETTING_LABELS_TR[key] ?? dbDescription ?? "—";
    }
    return dbDescription ?? "—";
};

// ─────────────────────────────────────────────────────────────────────────────
// NON-CONFIGURABLE CONSTANTS
// Mirror of backend SettingConstants.java + a few service-level constants.
// Referenced from the prose with {{const:key}}.
// Keep in sync when the corresponding Java constant changes.
// ─────────────────────────────────────────────────────────────────────────────

export const MANUAL_CONSTANTS: Record<string, string> = {
    // SettingConstants.java — username / password
    username_min: "3",
    username_max: "50",
    password_min: "8",
    password_max: "64",

    // SettingConstants.java — account lifecycle
    inactive_days: "180",
    rookie_min_entries: "10",
    rookie_min_days: "30",
    deletion_grace_days: "14",
    deletion_cancel_cooldown_hours: "24",
    freeze_cooldown_hours: "24",

    // SettingConstants.java — vote rate limiting
    vote_max: "5",
    vote_window: "10",
    vote_cooldown: "30",

    // SettingConstants.java — messaging
    // msg_max / msg_window used to live here. They are admin settings now
    // (messaging.rate_limit_*), so the manual reads them through {{setting:...}}
    // and shows the value actually in force rather than a compiled-in 60.
    msg_preview: "200",
    messages_per_page: "30",
    conversations_per_page: "20",

    // SettingConstants.java — pagination defaults
    entries_per_page: "20",
    matches_per_page: "20",
    min_entries_per_page: "10",
    max_entries_per_page: "50",
    min_matches_per_page: "10",
    max_matches_per_page: "50",

    // SettingConstants.java — reports & search
    report_text_max: "500",
    search_full_max: "50",
    search_snippet: "200",

    // Service-level constants
    max_channels: "10",       // CustomTopicService.MAX_CHANNELS_PER_TOPIC
    own_topic_entries_max: "50", // EntryService.OWN_TOPIC_ENTRIES_LIMIT
    merge_chain_depth: "10",  // TopicMergeGuardService.MAX_CHAIN_DEPTH
};

// ─────────────────────────────────────────────────────────────────────────────
// GROUP ASSEMBLY — the order here is the order rendered in the navigation rail
// ─────────────────────────────────────────────────────────────────────────────

export const MANUAL_GROUPS: ManualGroup[] = [
    {
        id: "basics",
        title: { tr: "Temeller", en: "Basics" },
        sections: [SECTION_OVERVIEW, SECTION_DOMAINS, SECTION_GUNDEM, SECTION_ENTRY_RULES],
    },
    {
        id: "users",
        title: { tr: "Kullanıcılar ve Yetkiler", en: "Users and Permissions" },
        sections: [SECTION_ROLES, SECTION_STATUSES, SECTION_DELETION, SECTION_LIFECYCLE_TIMELINE, SECTION_PROMOTION, SECTION_VOTES],
    },
    {
        id: "experience",
        title: { tr: "Kullanıcı Deneyimi", en: "User Experience" },
        sections: [SECTION_NOTIFICATIONS, SECTION_PROFILE, SECTION_PREFERENCES, SECTION_SOCIAL, SECTION_ONLINE],
    },
    {
        id: "moderation",
        title: { tr: "Moderasyon ve Panel", en: "Moderation and Panel" },
        sections: [
            SECTION_PANEL_TOUR,
            SECTION_TOGGLES,
            SECTION_REPORTS,
            SECTION_IPBAN,
            SECTION_LOCK,
            SECTION_PINS,
            SECTION_MOVE_MERGE,
            SECTION_AUDIT,
        ],
    },
    {
        id: "data",
        title: { tr: "Spor Verisi", en: "Sports Data" },
        sections: [SECTION_CLUBS_TEAMS, SECTION_MATCHES, SECTION_MASTER_DATA],
    },
    {
        id: "system",
        title: { tr: "Sistem", en: "System" },
        sections: [SECTION_REGISTRATION, SECTION_MEDIA_SEARCH, SECTION_LEGAL, SECTION_SETTINGS_REF],
    },
];

/** Flat list of every section, in navigation order. Used by the search filter. */
export const ALL_MANUAL_SECTIONS: ManualSection[] =
    MANUAL_GROUPS.flatMap((g) => g.sections);